import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    Link,
    InputAdornment,
    IconButton,
    AppBar,
    Toolbar,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
    Grow,
    Fade,
    Slide,
    Zoom,
    Grid,
    Chip,
    Stepper,
    Step,
    StepLabel,
    Alert,
    Snackbar,
    CircularProgress,
    Radio,
    FormControlLabel,
    RadioGroup,
    darken,
    Autocomplete,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
} from '@mui/material'
import {
    Visibility,
    VisibilityOff,
    Person,
    CameraAlt,
    Delete,
    CloudUpload,
    CheckCircle,
    Add,
    ArrowForward,
    AutoAwesome,
    PictureAsPdf,
    Security,
    Verified,
    Lock,
    Cancel,
    KeyboardArrowDown,
    InfoOutlined,
    AssignmentOutlined,
} from '@mui/icons-material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format as formatDate } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import api from '@/services/api'
import SubscriptionPlans from '@/components/SubscriptionPlans'
import PaymentModal from '@/components/contracts/PaymentModal'
import { SubscriptionPlan, MembershipPlan } from '@/types/subscription'
import { membershipApi } from '@/services/api'
import { initiateStripeCheckout } from '@/services/stripeService'
import { useDebounce } from '@/hooks/useDebounce'
import { AddressAutocomplete } from '@/components/AddressAutocomplete'
import {
    isValidEmail,
    isValidPhone,
    isValidSSN,
    isValidEIN,
    ValidationRules,
    calculatePasswordStrength,
    PasswordStrength,
} from '@/utils/validation'
import PasswordStrengthMeter from '@/components/ui/PasswordStrengthMeter'
import PhoneInput from '@/components/ui/PhoneInput'
import LocationSelector from '@/components/ui/LocationSelector'
import { getUSPhoneErrorMessage } from '@/utils/phoneUtils'
import ProgressStepper from './components/ProgressStepper'
import AboutYouStep from './steps/AboutYouStep'
import ChooseSubscriptionStep from './steps/ChooseSubscriptionStep'
import ConnectBankStep from './steps/ConnectBankStep'
import AgencyDocumentsSection from './steps/AgencyDocumentsSection'
import OnboardingNavigation from './components/OnboardingNavigation'
import { bi } from 'node_modules/@fullcalendar/core/internal-common'
import { log } from 'console'
import AgencyPaymentModal from './components/AgencyPaymentModal'

const getBillingPhoneValidationMessage = (value: string): string => {
    const trimmed = value.trim()
    if (!trimmed) {
        return 'Billing phone number is required'
    }
    return getUSPhoneErrorMessage(value)
}

const THERAPIST_CREATE_ACCOUNT_DRAFT_KEY =
    'therapist_create_account_draft_v1' as const

const THERAPIST_CREATE_ACCOUNT_DRAFT_SECRET =
    'therapist_create_account_password_v1' as const

const encodeBase64 = (bytes: Uint8Array): string => {
    let binary = ''
    bytes.forEach(b => {
        binary += String.fromCharCode(b)
    })
    return btoa(binary)
}

const decodeBase64ToArrayBuffer = (value: string): ArrayBuffer => {
    const binary = atob(value)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i)
    }
    return bytes.buffer.slice(0)
}

const getPasswordEncryptionKey = async (): Promise<CryptoKey> => {
    const cryptoApi = window.crypto
    const encoder = new TextEncoder()
    const baseKey = await cryptoApi.subtle.importKey(
        'raw',
        encoder.encode(THERAPIST_CREATE_ACCOUNT_DRAFT_SECRET),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
    )

    return cryptoApi.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: encoder.encode('therapist-create-account'),
            iterations: 100000,
            hash: 'SHA-256',
        },
        baseKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    )
}

const encryptDraftText = async (value: string): Promise<string> => {
    if (!value) return ''
    const cryptoApi = window.crypto
    const encoder = new TextEncoder()
    const iv = cryptoApi.getRandomValues(new Uint8Array(12))
    const key = await getPasswordEncryptionKey()
    const encrypted = await cryptoApi.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encoder.encode(value)
    )
    return `${encodeBase64(iv)}:${encodeBase64(new Uint8Array(encrypted))}`
}

const decryptDraftText = async (encryptedValue: string): Promise<string> => {
    if (!encryptedValue) return ''
    const [ivB64, payloadB64] = encryptedValue.split(':')
    if (!ivB64 || !payloadB64) return ''
    const cryptoApi = window.crypto
    const key = await getPasswordEncryptionKey()
    const decrypted = await cryptoApi.subtle.decrypt(
        { name: 'AES-GCM', iv: decodeBase64ToArrayBuffer(ivB64) },
        key,
        decodeBase64ToArrayBuffer(payloadB64)
    )
    return new TextDecoder().decode(decrypted)
}

interface Specialty {
    _id: string
    name: string
    description: string
}

interface Degree {
    _id: string
    name: string
    discription: string
    isActive: boolean
}

interface LicenseType {
    _id: string
    name: string
    description: string
    isDeleted: boolean
    required?: 'mandatory' | 'non_mandatory'
    task: string
}

interface PayerAltName {
    _id: string
    alt_payer_name: string
}

interface Payer {
    _id: string
    payer_name: string
    payer_state: string
    payer_type: string
    workers_comp: string
    ub_claims: string
    eligibility: string
    auto: string
    attachment: string
    dent_claims: string
    era: string
    payer_alt_names: PayerAltName[]
    avg_era_enroll_days: number
    secondary_support: string
    payerid: string
    '1500_claims': string
    status: string
    __v: number
    createdAt: string
    updatedAt: string
}

interface BoardOption {
    _id: string
    name: string
}

interface OnboardingData {
    // Step 1: Service Information
    serviceType: string[]
    services: string[]
    degrees: string[]
    genderIdentity: string
    sessionFee: string
    sessionLength: string
    insuranceAccepted: string[]
    yearsOfExperience: string
    pronouns: string
    // Board certification (all optional - not all providers have board certificates)
    noOfProvider: string
    boardCertificationNo: string
    boardSpeciality: string
    boardName: string
    boardSubSpeciality: string
    // Step 2: About you
    profilePicture: string | null
    professionalBio: string
    // Step 3: NPI & License verification
    npiNumber: string
    ssn: string
    licenseNpi: string
    licenseStatus: string
    socialSecurityNumber: string
    licenseType: string[]
    licenseState: string | string[]
    licenseNumber: string
    licenseExpirationYear: string
    licenseExpirationDate: string
    issueDate: string
    licenseFileUrl: string
    licenseFileName?: string
    licenseFilePreview?: string
    licenseStateDocs?: Record<string, {
        fileUrl: string
        fileName?: string
        filePreview?: string
    }>
}

interface SubscriptionDetails {
    orderId: string
    client_secret: string
    customerId: string
    ephemeralKey?: string
    discountedPrice?: number
    noOfUser?: number
}

type TherapistCreateAccountDraftV1 = {
    v: 1
    savedAt: number
    currentStep: number
    showOnboarding: boolean
    encryptedPassword?: string
    encryptedConfirmPassword?: string
    encryptedProviderSsn?: string
    // Subscription progress (so refresh/return keeps correct state)
    therapistId?: string | null
    selectedPlan?: MembershipPlan | null
    billingCycle?: 'monthly' | 'yearly'
    discountedPrice?: number | null
    subscriptionDetails?: SubscriptionDetails | null
    subscriptionPaymentSuccess?: boolean
    formData: Record<string, any>
    onboardingData: Record<string, any>
    additionalLicenses: Array<Record<string, any>>
}

const capitalizeWords = (str: string): string => {
    if (!str) return ''
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

const AgencySignUp: React.FC = () => {
    const navigate = useNavigate()
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(
        calculatePasswordStrength('')
    )
    const [formData, setFormData] = useState({
        agencyName: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        password: '',
        confirmPassword: '',
        gender: '',
        dateOfBirth: null as Date | null,
        address: '',
        state: '',
        city: '',
        country: '',
        zipCode: '',
        latitude: undefined as number | undefined,
        longitude: undefined as number | undefined,
        noOfProvider: '',
        // New billing fields
        billingType: 'individual',
        billingNpi: '',
        taxId: '',
        companyName: '',
        ssn: '',
        billingPhone: '',
        billingAddressLine: '',
        billingState: '',
        billingCity: '',
        billingZipCode: '',
        billingCountry: '',
        addressLine2: '',
        licenseDocument: null as File | null,
        // New fields for redesigned "Create your account" form
        dbaName: '',
        ein: '',
        npiOrganization: '',
        nursingNpi: '',
        nursingNpiDocUrl: '',
        nursingNpiDocName: '',
        therapyNpi: '',
        therapyNpiDocUrl: '',
        therapyNpiDocName: '',
        caregiverNpi: '',
        caregiverNpiDocUrl: '',
        caregiverNpiDocName: '',
        // agencyLicenseNumber: '',
        // agencyLicenseExpiration: null as Date | null,
        stateOfOperations: [] as string[],
        businessEmail: '',
        businessPhoneNumber: '',
        website: '',
        professionalLiabilityInsurance: '',
        generalLiabilityInsurance: '',
        coverageOfInsurance: '',
        workersCompensation: '',
        // Corporate documents / clinical accreditation uploads
        agencyNameDocUrl: '',
        agencyNameDocName: '',
        dbaNameDocUrl: '',
        dbaNameDocName: '',
        einDocUrl: '',
        einDocName: '',
        w9FormUrl: '',
        w9FormName: '',
        npiOrganizationDocUrl: '',
        npiOrganizationDocName: '',
        certificateOfInsuranceUrl: '',
        certificateOfInsuranceName: '',
        clinicalAccreditationUrls: [] as string[],
        clinicalAccreditationNames: [] as string[],
        professionalLiabilityInsuranceDocUrls: [] as string[],
        professionalLiabilityInsuranceDocNames: [] as string[],
        generalLiabilityInsuranceDocUrls: [] as string[],
        generalLiabilityInsuranceDocNames: [] as string[],
        coverageOfInsuranceDocUrls: [] as string[],
        coverageOfInsuranceDocNames: [] as string[],
        workersCompensationDocUrls: [] as string[],
        workersCompensationDocNames: [] as string[],
    })
    const primaryColor = "#7A6FA1"
    // Shared styles for the redesigned "Create your account" form fields
    const accountFieldSx = {
        '& .MuiOutlinedInput-root': {
            bgcolor: 'white',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '& fieldset': { borderColor: 'grey.300' },
            '&:hover fieldset': { borderColor: 'grey.400' },
            '&.Mui-focused fieldset': { borderColor: '#574F75', borderWidth: 2 },
        },
        '& .MuiInputBase-input': { py: 1.5, fontSize: '0.875rem' },
    }
    const accountFieldLabelSx = {
        color: 'grey.700',
        mb: 1,
        fontWeight: 500,
        fontSize: '0.875rem',
    } as const
    const accountDatePickerSx = {
        '& .MuiPickersInputBase-root': {
            height: '48px',
            bgcolor: 'white',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '& fieldset': { borderColor: 'grey.300' },
            '&:hover fieldset': { borderColor: 'grey.400' },
            '&.Mui-focused fieldset': { borderColor: '#574F75 !important', borderWidth: 2 },
        },
        '& .MuiInputBase-input': { fontSize: '0.875rem' },
    }
    const uploadBtnSx = {
        color: '#7A6FA1',
        bgcolor: '#F3F1F8',
        borderRadius: '8px',
        padding: '6px',
        transition: 'all 0.2s ease',
        '&:hover': {
            color: '#574F75',
            bgcolor: '#E7E2F3',
            transform: 'translateY(-1px)',
        },
        '&:active': {
            transform: 'translateY(0)',
        }
    }


    const [currentStep, setCurrentStep] = useState(0)
    const formContainerRef = useRef<HTMLDivElement>(null)
    const formPaperRef = useRef<HTMLDivElement>(null)
    const [showOnboarding, setShowOnboarding] = useState(false)
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showErrorAlert, setShowErrorAlert] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false)
    const [hasAutoOpenedSubscriptionDialog, setHasAutoOpenedSubscriptionDialog] =
        useState(false)
    const [noOfProviderError, setNoOfProviderError] = useState('')
    const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(null)
    const [isNpiVerifying, setIsNpiVerifying] = useState(false)
    const [isNpiVerified, setIsNpiVerified] = useState(false)
    const [npiVerificationError, setNpiVerificationError] = useState('')
    // Billing NPI verification state
    const [isBillingNpiVerifying, setIsBillingNpiVerifying] = useState(false)
    const [isBillingNpiVerified, setIsBillingNpiVerified] = useState(false)
    const [billingNpiVerificationError, setBillingNpiVerificationError] =
        useState('')
    // Email verification state
    const [isVerifyingEmail, setIsVerifyingEmail] = useState(false)
    const [emailVerificationError, setEmailVerificationError] = useState('')
    // Phone number validation state
    const [phoneNumberError, setPhoneNumberError] = useState('')
    const [isPhoneNumberTouched, setIsPhoneNumberTouched] = useState(false)
    const [billingPhoneError, setBillingPhoneError] = useState('')
    const [isBillingPhoneTouched, setIsBillingPhoneTouched] = useState(false)
    const [taxIdError, setTaxIdError] = useState('')
    const [companyNameError, setCompanyNameError] = useState('')
    // Tax ID (SSN/EIN) validation state
    const [ssnIdError, setSsnError] = useState('')
    // Password confirmation validation state
    const [confirmPasswordError, setConfirmPasswordError] = useState('')
    const [genderError, setGenderError] = useState('')
    const [dateOfBirthError, setDateOfBirthError] = useState('')
    const [npiStepErrors, setNpiStepErrors] = useState<Record<string, string>>({})
    // Board certification errors
    const [boardCertificationNoError, setBoardCertificationNoError] = useState('')
    const [boardSpecialityError, setBoardSpecialityError] = useState('')
    const [boardNameError, setBoardNameError] = useState('')
    const [servicesError, setServicesError] = useState('')
    const [degreesError, setDegreesError] = useState('')
    const [insuranceError, setInsuranceError] = useState('')
    const [yearsOfExperienceError, setYearsOfExperienceError] = useState('')
    // New "Create your account" redesign - validation state
    const [agencyNameError, setAgencyNameError] = useState('')
    const [dbaNameError, setDbaNameError] = useState('')
    const [einError, setEinError] = useState('')
    const [npiOrganizationError, setNpiOrganizationError] = useState('')
    const [agencyNameDocError, setAgencyNameDocError] = useState('')
    const [dbaNameDocError, setDbaNameDocError] = useState('')
    const [einDocError, setEinDocError] = useState('')
    const [npiOrganizationDocError, setNpiOrganizationDocError] = useState('')
    const [businessEmailError, setBusinessEmailError] = useState('')
    const [businessPhoneError, setBusinessPhoneError] = useState('')
    const [isBusinessPhoneTouched, setIsBusinessPhoneTouched] = useState(false)
    const [stateOfOperationsError, setStateOfOperationsError] = useState('')
    const [stateOfOperationsSelectOpen, setStateOfOperationsSelectOpen] =
        useState(false)
    // Secondary administrators (optional)
    const [secondaryAdministrators, setSecondaryAdministrators] = useState<
        Array<{
            id: string
            firstName: string
            lastName: string
            role: string
            email: string
        }>
    >([])
    const [showAddAdminDialog, setShowAddAdminDialog] = useState(false)
    const [newAdminDraft, setNewAdminDraft] = useState({
        firstName: '',
        lastName: '',
        role: '',
        email: '',
    })
    const [newAdminErrors, setNewAdminErrors] = useState<Record<string, string>>(
        {}
    )
    const [uploadingDocumentField, setUploadingDocumentField] = useState<
        string | null
    >(null)
    const activeUploadsCountRef = useRef<Record<string, number>>({})
    // Track if we're deleting a chip to prevent Select from opening
    const [isDeletingChip, setIsDeletingChip] = useState(false)
    const [servicesSelectOpen, setServicesSelectOpen] = useState(false)
    const [providedServicesSelectOpen, setProvidedServicesSelectOpen] = useState(false)
    const [degreesSelectOpen, setDegreesSelectOpen] = useState(false)
    const [serviceTypesSelectOpen, setServiceTypesSelectOpen] = useState(false)
    const [serviceTypeError, setServiceTypeError] = useState('')
    // License file upload loading states
    const [isUploadingLicense, setIsUploadingLicense] = useState(false)
    const [stateLicenseUploadLoading, setStateLicenseUploadLoading] = useState<Record<string, boolean>>({})
    const [isUploadingAdditionalLicenses, setIsUploadingAdditionalLicenses] =
        useState<Record<number, boolean>>({})
    // Additional license cards state
    const [additionalLicenses, setAdditionalLicenses] = useState<
        Array<{
            licenseType: string
            licenseNpi: string
            licenseState: string | string[]
            licenseNumber: string
            licenseExpirationDate: string
            issueDate: string
            licenseStatus: string
            licenseFileUrl: string
            licenseFileName?: string
            licenseFilePreview?: string
            licenseStateDocs?: Record<string, { fileUrl?: string; fileName?: string; filePreview?: string }>
        }>
    >([])

    const prefildlicenseNpi = localStorage.getItem('providerNpi') || ''

    const handleAdditionalLicenseChange = (
        index: number,
        field:
            | 'licenseType'
            | 'licenseNpi'
            | 'licenseState'
            | 'licenseNumber'
            | 'ssn'
            | 'issueDate'
            | 'licenseExpirationDate'
            | 'licenseStatus',
        value: any
    ) => {
        setAdditionalLicenses(prev => {
            const next = [...prev]
            next[index] = { ...next[index], [field]: value }
            return next
        })
        // Clear NPI error when license type or NPI changes
        if (field === 'licenseType' || field === 'licenseNpi') {
            clearNpiFieldError(`additionalLicenseNpi_${index}`)
            clearNpiFieldError(`additionalLicenseType_${index}`)
        }
        if (field === 'licenseState') {
            clearNpiFieldError(`additionalLicenseState_${index}`)
        }
        if (field === 'licenseNumber') {
            clearNpiFieldError(`additionalLicenseNumber_${index}`)
        }
        if (field === 'issueDate') {
            clearNpiFieldError(`additionalIssueDate_${index}`)
        }
        if (field === 'licenseExpirationDate') {
            clearNpiFieldError(`additionalLicenseExpirationDate_${index}`)
        }
    }

    const handleAdditionalStateLicenseDocumentChange = (
        index: number,
        stateName: string
    ) => async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        const loadingKey = `add_${index}_${stateName}`
        try {
            setStateLicenseUploadLoading(prev => ({ ...prev, [loadingKey]: true }))

            const existingDoc = additionalLicenses[index]?.licenseStateDocs?.[stateName]
            if (existingDoc?.filePreview) {
                URL.revokeObjectURL(existingDoc.filePreview)
            }

            const uploadRes = await membershipApi.uploadLicenseFile(file)
            const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.fileUrl || ''
            if (url) {
                let previewUrl = ''
                if (file.type.startsWith('image/')) {
                    previewUrl = URL.createObjectURL(file)
                }

                setAdditionalLicenses(prev => {
                    const next = [...prev]
                    const cur = next[index]
                    const newDocs = { ...(cur?.licenseStateDocs || {}) }
                    newDocs[stateName] = {
                        fileUrl: url,
                        fileName: file.name,
                        filePreview: previewUrl
                    }
                    next[index] = {
                        ...cur,
                        licenseStateDocs: newDocs,
                        licenseFileUrl: url,
                        licenseFileName: file.name,
                        licenseFilePreview: previewUrl
                    }
                    return next
                })
                clearNpiFieldError(`additionalLicenseFileUrl_${index}`)
            }
        } catch (e) {
            console.error(`Error uploading license for ${stateName}:`, e)
        } finally {
            setStateLicenseUploadLoading(prev => ({ ...prev, [loadingKey]: false }))
        }
    }

    const handleRemoveAdditionalStateLicense = (index: number, stateName: string) => (e?: React.MouseEvent) => {
        e?.stopPropagation()
        setAdditionalLicenses(prev => {
            const next = [...prev]
            const cur = next[index]
            if (!cur) return next
            const doc = cur.licenseStateDocs?.[stateName]
            if (doc?.filePreview) {
                URL.revokeObjectURL(doc.filePreview)
            }
            const newDocs = { ...(cur.licenseStateDocs || {}) }
            delete newDocs[stateName]
            const firstDoc = Object.values(newDocs)[0]
            next[index] = {
                ...cur,
                licenseStateDocs: newDocs,
                licenseFileUrl: firstDoc?.fileUrl || '',
                licenseFileName: firstDoc?.fileName || '',
                licenseFilePreview: firstDoc?.filePreview || '',
            }
            return next
        })
    }

    const handleAdditionalLicenseFile = async (
        index: number,
        file: File | null
    ) => {
        if (!file) return
        try {
            // Set loading state
            setIsUploadingAdditionalLicenses(prev => ({ ...prev, [index]: true }))

            // Clean up previous preview URL if exists
            setAdditionalLicenses(prev => {
                const next = [...prev]
                if (next[index]?.licenseFilePreview) {
                    URL.revokeObjectURL(next[index].licenseFilePreview!)
                }
                return next
            })

            const uploadRes = await membershipApi.uploadLicenseFile(file)
            const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.fileUrl || ''
            if (!url) {
                setIsUploadingAdditionalLicenses(prev => ({ ...prev, [index]: false }))
                return
            }

            // Create preview URL for images
            let previewUrl = ''
            if (file.type.startsWith('image/')) {
                previewUrl = URL.createObjectURL(file)
            }

            setAdditionalLicenses(prev => {
                const next = [...prev]
                next[index] = {
                    ...next[index],
                    licenseFileUrl: url,
                    licenseFileName: file.name,
                    licenseFilePreview: previewUrl,
                }
                return next
            })
        } catch (e) {
            console.error('Error uploading additional license:', e)
        } finally {
            setIsUploadingAdditionalLicenses(prev => ({ ...prev, [index]: false }))
        }
    }

    // Payment flow state
    const [showPaymentModal, setShowPaymentModal] = useState(false)
    const [subscriptionDetails, setSubscriptionDetails] =
        useState<SubscriptionDetails | null>(null)
    const [therapistId, setTherapistId] = useState<string | null>(null)
    const [discountedPrice, setDiscountedPrice] = useState<number | null>(null)
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(
        'monthly'
    )
    const [isProcessingPayment, setIsProcessingPayment] = useState(false)
    const [paymentError, setPaymentError] = useState<string | null>(null)
    const [isConnectingBank, setIsConnectingBank] = useState(false)
    const [subscriptionPaymentSuccess, setSubscriptionPaymentSuccess] =
        useState(false)
    // Plan picked from the pricing dialog, but not yet paid for — the
    // Subscription Agreement panel shows in this window, before any charge.
    const [planSelected, setPlanSelected] = useState(false)
    const [pendingPlanSelection, setPendingPlanSelection] = useState<{
        plan: MembershipPlan
        cycle: 'monthly' | 'yearly'
        amountPaid: string
        totalPrice: string
        couponApplied: string
        customNoOfUser?: number
    } | null>(null)

    // Specialties state
    const [specialties, setSpecialties] = useState<Specialty[]>([])
    const [isLoadingSpecialties, setIsLoadingSpecialties] = useState(false)

    // ServiceType state
    interface ServiceType {
        _id: string
        name: string
        description: string
        isActive: boolean
        isDeleted: boolean
    }
    const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([])
    const [isLoadingServiceTypes, setIsLoadingServiceTypes] = useState(false)

    // Provided Services state
    interface ProvidedService {
        id: number
        name: string
    }
    const [providedServices, setProvidedServices] = useState<ProvidedService[]>([])
    const [isLoadingProvidedServices, setIsLoadingProvidedServices] = useState(false)

    // Degrees state
    const [degrees, setDegrees] = useState<Degree[]>([])
    const [isLoadingDegrees, setIsLoadingDegrees] = useState(false)

    // Payers state
    const [payers, setPayers] = useState<Payer[]>([])
    const [isLoadingPayers, setIsLoadingPayers] = useState(false)
    const [payerSearchInput, setPayerSearchInput] = useState('')
    const [payerSearchOptions, setPayerSearchOptions] = useState<Payer[]>([])
    const [isSearchingPayers, setIsSearchingPayers] = useState(false)
    const [selectedPayers, setSelectedPayers] = useState<Payer[]>([])

    // Board certification search state
    const [boardNameSearch, setBoardNameSearch] = useState('')
    const [boardSpecialitySearch, setBoardSpecialitySearch] = useState('')
    const [boardSubSpecialitySearch, setBoardSubSpecialitySearch] = useState('')
    const [boardNameOptions, setBoardNameOptions] = useState<BoardOption[]>([])
    const [boardSpecialityOptions, setBoardSpecialityOptions] = useState<
        BoardOption[]
    >([])
    const [boardSubSpecialityOptions, setBoardSubSpecialityOptions] = useState<
        BoardOption[]
    >([])
    const [isSearchingBoardName, setIsSearchingBoardName] = useState(false)
    const [isSearchingBoardSpeciality, setIsSearchingBoardSpeciality] =
        useState(false)
    const [isSearchingBoardSubSpeciality, setIsSearchingBoardSubSpeciality] =
        useState(false)

    // States state
    const [states, setStates] = useState<Array<{ _id: string; name: string }>>([])
    const [isLoadingStates, setIsLoadingStates] = useState(false)

    // License types state
    const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([])
    const [isLoadingLicenseTypes, setIsLoadingLicenseTypes] = useState(false)

    // Profile photo upload state
    const [isUploadingProfilePhoto, setIsUploadingProfilePhoto] = useState(false)
    const [isRestored, setIsRestored] = useState(false)

    const [onboardingData, setOnboardingData] = useState<OnboardingData>({
        serviceType: [],
        services: [],
        degrees: [],
        genderIdentity: 'Male',
        sessionFee: '',
        sessionLength: '',
        insuranceAccepted: [],
        yearsOfExperience: '',
        pronouns: '',
        boardCertificationNo: '',
        boardSpeciality: '',
        boardName: '',
        boardSubSpeciality: '',
        profilePicture: null,
        professionalBio: '',
        npiNumber: '',
        noOfProvider: '',
        ssn: '',
        socialSecurityNumber: '',
        licenseNpi: localStorage.getItem('providerNpi') || '',
        licenseStatus: 'Active',
        licenseType: [],
        licenseState: [],
        licenseNumber: '',
        licenseExpirationYear: '',
        licenseExpirationDate: '',
        issueDate: '',
        licenseFileUrl: '',
        licenseFileName: '',
        licenseFilePreview: '',
        licenseStateDocs: {},
    })

    const restoreDraftOnceRef = useRef(false)
    const draftPersistenceEnabledRef = useRef(true)

    const licenseTypeArray = useMemo(() => {
        const lt = onboardingData.licenseType;
        return Array.isArray(lt) ? lt : (lt ? [lt] : []);
    }, [onboardingData.licenseType]);

    const shouldShowSpecialtyLicense = useMemo(() => {
        const selectedServiceTypeIds = Array.isArray(onboardingData.serviceType) ? onboardingData.serviceType : [];
        if (selectedServiceTypeIds.length === 0) return false;

        const firstSevenNames = [
            'Home Health Agency',
            'Home Care Agency',
            'Rehabilitation Therapy Agency',
            'Skilled Nursing Agency',
            'Hospice Agency',
            'Skilled Nursing Facility (SNF)',
            'Senior Living Facility'
        ];

        return selectedServiceTypeIds.some(id => {
            const found = serviceTypes.find(st => st._id === id);
            return found && firstSevenNames.includes(found.name);
        });
    }, [onboardingData.serviceType, serviceTypes]);

    const draftSnapshot = useMemo<TherapistCreateAccountDraftV1>(() => {
        const {
            password: _password,
            confirmPassword: _confirmPassword,
            ssn: _ssn,
            taxId: _taxId,
            licenseDocument: _licenseDocument,
            dateOfBirth,
            ...safeFormData
        } = formData as any

        const {
            ssn: _onboardingSsn,
            socialSecurityNumber: _socialSecurityNumber,
            licenseFilePreview: _licenseFilePreview,
            licenseStateDocs,
            ...safeOnboardingData
        } = onboardingData as any

        const safeAdditionalLicenses = (additionalLicenses || []).map(l => {
            const { licenseFilePreview: _preview, licenseStateDocs: addDocs, ...rest } = l as any
            const sanitizedAddDocs = Object.fromEntries(
                Object.entries(addDocs || {}).map(([st, doc]: [string, any]) => [
                    st,
                    { fileUrl: doc.fileUrl, fileName: doc.fileName }
                ])
            )
            return {
                ...rest,
                licenseStateDocs: sanitizedAddDocs,
            }
        })

        const sanitizedStateDocs = Object.fromEntries(
            Object.entries(licenseStateDocs || {}).map(([st, doc]: [string, any]) => [
                st,
                { fileUrl: doc.fileUrl, fileName: doc.fileName }
            ])
        )

        return {
            v: 1,
            savedAt: Date.now(),
            currentStep,
            showOnboarding,
            therapistId,
            selectedPlan,
            billingCycle,
            discountedPrice,
            subscriptionDetails,
            subscriptionPaymentSuccess,
            formData: {
                ...safeFormData,
                dateOfBirth: dateOfBirth ? (dateOfBirth as Date).toISOString() : null,
            },
            onboardingData: {
                ...safeOnboardingData,
                licenseStateDocs: sanitizedStateDocs,
            },
            additionalLicenses: safeAdditionalLicenses,
        }
    }, [
        formData,
        onboardingData,
        additionalLicenses,
        currentStep,
        showOnboarding,
        therapistId,
        selectedPlan,
        billingCycle,
        discountedPrice,
        subscriptionDetails,
        subscriptionPaymentSuccess,
    ])

    const debouncedDraftSnapshot = useDebounce(draftSnapshot, 500)

    useEffect(() => {
        // Restore draft once on mount (if present).
        if (restoreDraftOnceRef.current) return
        restoreDraftOnceRef.current = true;

        (async () => {
            try {
                const raw = localStorage.getItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
                if (raw) {
                    const parsed = JSON.parse(raw) as Partial<TherapistCreateAccountDraftV1>
                    if (parsed && parsed.v === 1) {
                        let restoredPassword = ''
                        let restoredConfirmPassword = ''
                        let restoredProviderSsn = ''
                        if (parsed.encryptedPassword) {
                            restoredPassword = await decryptDraftText(parsed.encryptedPassword)
                        }
                        if (parsed.encryptedConfirmPassword) {
                            restoredConfirmPassword = await decryptDraftText(
                                parsed.encryptedConfirmPassword
                            )
                        }
                        if (parsed.encryptedProviderSsn) {
                            restoredProviderSsn = await decryptDraftText(parsed.encryptedProviderSsn)
                        }

                        if (typeof parsed.showOnboarding === 'boolean') {
                            setShowOnboarding(parsed.showOnboarding)
                        }

                        if (typeof parsed.currentStep === 'number' && parsed.currentStep >= 0) {
                            setCurrentStep(parsed.currentStep)
                        }

                        if (typeof parsed.therapistId === 'string' || parsed.therapistId === null) {
                            setTherapistId(parsed.therapistId)
                        }
                        if (parsed.selectedPlan) {
                            setSelectedPlan(parsed.selectedPlan as any)
                        }
                        if (parsed.billingCycle === 'monthly' || parsed.billingCycle === 'yearly') {
                            setBillingCycle(parsed.billingCycle)
                        }
                        if (typeof parsed.discountedPrice === 'number' || parsed.discountedPrice === null) {
                            setDiscountedPrice(parsed.discountedPrice ?? null)
                        }
                        if (parsed.subscriptionDetails) {
                            setSubscriptionDetails(parsed.subscriptionDetails as any)
                        }
                        if (typeof parsed.subscriptionPaymentSuccess === 'boolean') {
                            setSubscriptionPaymentSuccess(parsed.subscriptionPaymentSuccess)
                        }

                        if (parsed.formData && typeof parsed.formData === 'object') {
                            setFormData(prev => {
                                const restoredDob =
                                    parsed.formData?.dateOfBirth && typeof parsed.formData.dateOfBirth === 'string'
                                        ? new Date(parsed.formData.dateOfBirth)
                                        : null

                                const restoredLicenseExpiration =
                                    parsed.formData?.agencyLicenseExpiration && typeof parsed.formData.agencyLicenseExpiration === 'string'
                                        ? new Date(parsed.formData.agencyLicenseExpiration)
                                        : null

                                return {
                                    ...prev,
                                    ...parsed.formData,
                                    dateOfBirth: restoredDob,
                                    agencyLicenseExpiration: restoredLicenseExpiration,
                                    password: restoredPassword,
                                    confirmPassword: restoredConfirmPassword,
                                    ssn: restoredProviderSsn,
                                    taxId: '',
                                    licenseDocument: null,
                                }
                            })
                        }

                        if (parsed.onboardingData && typeof parsed.onboardingData === 'object') {
                            setOnboardingData(prev => {
                                const restoredLicenseType = parsed.onboardingData?.licenseType
                                const sanitizedLicenseType = Array.isArray(restoredLicenseType)
                                    ? restoredLicenseType
                                    : (typeof restoredLicenseType === 'string' && restoredLicenseType
                                        ? [restoredLicenseType]
                                        : [])
                                return {
                                    ...prev,
                                    ...parsed.onboardingData,
                                    licenseType: sanitizedLicenseType,
                                    // Never restore sensitive / non-serializable fields
                                    ssn: '',
                                    socialSecurityNumber: '',
                                    licenseFilePreview: '',
                                }
                            })
                        }

                        if (Array.isArray(parsed.additionalLicenses)) {
                            setAdditionalLicenses(
                                parsed.additionalLicenses.map(l => ({
                                    ...l,
                                    licenseFilePreview: '',
                                })) as any
                            )
                        }
                        // Keep password meter consistent with restored password.
                        setPasswordStrength(calculatePasswordStrength(restoredPassword))
                    }
                }
            } catch (e) {
                console.warn('Failed to restore therapist create account draft:', e)
            } finally {
                setIsRestored(true)
            }
        })()
    }, [])

    useEffect(() => {
        // Persist draft (sanitized) so users can resume after closing the browser.
        if (!isRestored) return
        if (!draftPersistenceEnabledRef.current) return
        if (subscriptionPaymentSuccess) return;

        (async () => {
            try {
                const encryptedPassword = await encryptDraftText(formData.password || '')
                const encryptedConfirmPassword = await encryptDraftText(
                    formData.confirmPassword || ''
                )
                const encryptedProviderSsn = await encryptDraftText(formData.ssn || '')

                localStorage.setItem(
                    THERAPIST_CREATE_ACCOUNT_DRAFT_KEY,
                    JSON.stringify({
                        ...debouncedDraftSnapshot,
                        encryptedPassword,
                        encryptedConfirmPassword,
                        encryptedProviderSsn,
                    } satisfies TherapistCreateAccountDraftV1)
                )
            } catch (e) {
                console.warn('Failed to persist therapist create account draft:', e)
            }
        })()
    }, [debouncedDraftSnapshot, isRestored])

    const steps = [
        'About Agency',
        'Service Information',
        'Agency Credentials & Verification',
        'Choose Subscription',
        'Connect Bank',
    ]

    const hasServiceInfoData = useMemo(() => {
        return (
            onboardingData.services.length > 0 ||
            onboardingData.degrees.length > 0 ||
            onboardingData.insuranceAccepted.length > 0 ||
            onboardingData.yearsOfExperience.trim() !== '' ||
            onboardingData.noOfProvider.trim() !== ''
        )
    }, [
        onboardingData.services,
        onboardingData.degrees,
        onboardingData.insuranceAccepted,
        onboardingData.yearsOfExperience,
        onboardingData.noOfProvider,
    ])

    const hasAboutYouData = useMemo(() => {
        return (
            !!onboardingData.profilePicture?.trim() ||
            onboardingData.professionalBio.trim().length > 0
        )
    }, [onboardingData.profilePicture, onboardingData.professionalBio])

    const handleInputChange =
        (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            setFormData(prev => ({
                ...prev,
                [field]: value,
            }))

            // Calculate password strength when password field changes
            if (field === 'password') {
                setPasswordStrength(calculatePasswordStrength(value))
                // Re-validate confirm password when password changes
                if (formData.confirmPassword) {
                    if (value !== formData.confirmPassword) {
                        setConfirmPasswordError('Passwords do not match')
                    } else {
                        setConfirmPasswordError('')
                    }
                }
            }

            // Real-time validation for confirm password
            if (field === 'confirmPassword') {
                if (value && formData.password) {
                    if (value !== formData.password) {
                        setConfirmPasswordError('Passwords do not match')
                    } else {
                        setConfirmPasswordError('')
                    }
                } else if (!value) {
                    setConfirmPasswordError('')
                }
            }
        }

    const handleSelectChange = (field: string) => (event: any) => {
        const value = event.target.value
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }))
        if (field === 'gender') {
            setGenderError('')
        }
    }

    const verifyBusinessEmail = async (email: string): Promise<boolean> => {
        if (!email || !email.trim()) {
            setBusinessEmailError('Please enter a business email address')
            return false
        }

        if (!isValidEmail(email)) {
            setBusinessEmailError(ValidationRules.email.message)
            return false
        }

        setBusinessEmailError('')
        const baseUrl = import.meta.env.VITE_API_BASE_URL

        try {
            await axios.post(`${baseUrl}/agency/verify-email-agency`, {
                email: email.trim(),
            })
            setBusinessEmailError('')
            return true
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    const { data } = error.response
                    setBusinessEmailError(
                        data.message || 'Email already exists. Please use a different email address.'
                    )
                    return false
                }
            }
            setBusinessEmailError('Failed to verify business email. Please try again.')
            return false
        }
    }

    const verifyAdminEmail = async (email: string): Promise<boolean> => {
        if (!email || !email.trim()) {
            setEmailVerificationError('Please enter an email address')
            return false
        }

        if (!isValidEmail(email)) {
            setEmailVerificationError(ValidationRules.email.message)
            return false
        }

        setEmailVerificationError('')
        const baseUrl = import.meta.env.VITE_API_BASE_URL

        try {
            const response = await axios.post(`${baseUrl}/agency/verify-email-agency-user`, {
                email: email.trim(),
            })

            if (response.data?.message === 'Email not found in database' || response.status === 201 || response.status === 200) {
                setEmailVerificationError('')
                return true
            }

            setEmailVerificationError(
                response.data?.message || 'Email already exists. Please use a different email address.'
            )
            return false
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    const { data } = error.response
                    setEmailVerificationError(
                        data.message || 'Email already exists. Please use a different email address.'
                    )
                    return false
                }
            }
            setEmailVerificationError('Failed to verify email. Please try again.')
            return false
        }
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()

        // Reset validation errors
        setAgencyNameError('')
        setAgencyNameDocError('')
        setDbaNameError('')
        setDbaNameDocError('')
        setEinError('')
        setEinDocError('')
        setNpiOrganizationError('')
        setNpiOrganizationDocError('')

        // Validate Legal Business Name
        if (!formData.agencyName.trim()) {
            setAgencyNameError('Legal Business Name is required')
            return
        }
        if (!formData.agencyNameDocUrl) {
            setAgencyNameDocError('Click the upload icon on the right to upload the Legal Business Name document')
            return
        }

        // Validate DBA Name
        // if (!formData.dbaName.trim()) {
        //     setDbaNameError('DBA Name is required')
        //     return
        // }
        // if (!formData.dbaNameDocUrl) {
        //     setDbaNameDocError('Fictitious Business Statement document is required')
        //     return
        // }

        // Validate EIN
        if (!formData.ein.trim()) {
            setEinError('EIN is required')
            return
        }
        if (!isValidEIN(formData.ein)) {
            setEinError(ValidationRules.ein.message)
            return
        }
        if (!formData.einDocUrl) {
            setEinDocError('Click the upload icon on the right to upload the EIN document')
            return
        }





        // Validate Business Email
        if (!formData.businessEmail.trim()) {
            setBusinessEmailError('Business email is required')
            return
        }
        if (!isValidEmail(formData.businessEmail)) {
            setBusinessEmailError(ValidationRules.email.message)
            return
        }
        setBusinessEmailError('')

        // Validate Business Phone Number (optional, but must be valid if provided)
        if (formData.businessPhoneNumber.trim()) {
            const businessPhoneErr = getUSPhoneErrorMessage(formData.businessPhoneNumber)
            if (businessPhoneErr) {
                setBusinessPhoneError(businessPhoneErr)
                setIsBusinessPhoneTouched(true)
                return
            }
        }
        setBusinessPhoneError('')

        // Validate phone number before proceeding
        const phoneError = getUSPhoneErrorMessage(formData.phoneNumber)
        if (phoneError) {
            setPhoneNumberError(phoneError)
            setIsPhoneNumberTouched(true)
            return
        }

        // Validate passwords match before proceeding
        if (formData.password !== formData.confirmPassword) {
            setConfirmPasswordError('Passwords do not match')
            return
        }

        // if (!formData.gender) {
        //     setGenderError('Please select your gender')
        //     scrollToErrorField('gender-field')
        //     return
        // }
        // setGenderError('')

        // if (!formData.dateOfBirth) {
        //     setDateOfBirthError('Date of birth is required')
        //     scrollToErrorField('gender-field')
        //     return
        // }
        // setDateOfBirthError('')

        // Verify emails before proceeding
        setIsVerifyingEmail(true)
        setBusinessEmailError('')
        setEmailVerificationError('')

        const isBusinessEmailAvailable = await verifyBusinessEmail(formData.businessEmail)
        if (!isBusinessEmailAvailable) {
            setIsVerifyingEmail(false)
            return
        }

        const isAdminEmailAvailable = await verifyAdminEmail(formData.email)
        if (!isAdminEmailAvailable) {
            setIsVerifyingEmail(false)
            return
        }

        setIsVerifyingEmail(false)

        // Handle account creation logic here
        console.log('Account creation attempt:', formData)
        // Show onboarding form instead of navigating
        setShowOnboarding(true)
    }

    const handleLoginClick = () => {
        navigate('/agency-login')
    }

    const handleOnboardingInputChange =
        (field: keyof OnboardingData) => (event: any) => {
            const value = event.target.value
            setOnboardingData(prev => ({
                ...prev,
                [field]: value,
            }))
            if (
                field === 'licenseType' ||
                field === 'licenseNpi' ||
                field === 'licenseState' ||
                field === 'licenseNumber' ||
                field === 'ssn'
            ) {
                clearNpiFieldError(field)
            }
        }

    const handleYearsOfExperienceChange = (event: any) => {
        const value = event.target.value

        // Allow empty value
        if (value === '') {
            setOnboardingData(prev => ({
                ...prev,
                yearsOfExperience: '',
            }))
            setYearsOfExperienceError('Years of Experience is required')
            return
        }

        setYearsOfExperienceError('')

        // Check if value contains more than one decimal point
        if ((value.match(/\./g) || []).length > 1) {
            return // Reject if more than one decimal point
        }

        // Check if value has more than 1 decimal place
        const decimalIndex = value.indexOf('.')
        if (decimalIndex !== -1 && value.length - decimalIndex - 1 > 1) {
            return // Reject if more than 1 decimal place
        }

        // Parse the number
        const numValue = parseFloat(value)

        // Don't allow negative numbers or NaN
        if (isNaN(numValue) || numValue < 0) {
            return
        }

        // Check if decimal part is valid (must be 0 or 5, i.e., .0 or .5)
        if (decimalIndex !== -1) {
            const decimalPart = value.substring(decimalIndex + 1)
            // Only allow .0 or .5 (or just . which means user is typing)
            if (
                decimalPart.length === 1 &&
                decimalPart !== '0' &&
                decimalPart !== '5'
            ) {
                return // Reject invalid decimal digit
            }
        }

        setOnboardingData(prev => ({
            ...prev,
            yearsOfExperience: value,
        }))
    }

    const formatSSN = (value: string) => {
        if (!value) return ''
        return value
            .replace(/^(\d{3})(\d)/, '$1-$2')
            .replace(/^(\d{3}-\d{2})(\d)/, '$1-$2')
            .substring(0, 11)
    }

    const formatEIN = (value: string) => {
        if (!value) return ''
        const digitsOnly = value.replace(/\D/g, '')
        if (digitsOnly.length <= 2) return digitsOnly
        // Format as XX-XXXXXXX (2 digits, hyphen, up to 7 digits)
        return digitsOnly.substring(0, 2) + '-' + digitsOnly.substring(2, 9)
    }

    const handleNpiChange = (event: any) => {
        const value = event.target.value
        setOnboardingData(prev => ({
            ...prev,
            npiNumber: value,
        }))
        // Reset verification when NPI changes
        setIsNpiVerified(false)
        setNpiVerificationError('')
    }

    const handleVerifyNpi = async () => {
        setIsNpiVerifying(true)
        setNpiVerificationError('')

        try {
            const npiNumber = (formData.npiOrganization || '').trim()
            localStorage.setItem('providerNpi', npiNumber)
            if (!npiNumber) {
                setNpiVerificationError('Please enter an NPI number')
                setIsNpiVerifying(false)
                return
            }

            const response = await api.get(`/npi/number`, {
                params: { npi: npiNumber },
            })

            const data = response.data

            // Check if the API response indicates a valid NPI
            if (data && (data.result_count > 0 || data.results?.length > 0)) {
                setIsNpiVerified(true)
                setIsBillingNpiVerified(true)
                setNpiVerificationError('')
                setNpiStepErrors(prev => ({ ...prev, npiOrganization: '', npiVerification: '' }))

                // Pre-populate other NPI fields
                setOnboardingData(prev => ({
                    ...prev,
                    npiNumber: npiNumber,
                    licenseNpi: npiNumber,
                }))
                setFormData(prev => ({
                    ...prev,
                    billingNpi: npiNumber,
                }))
                // If there are additional licenses, pre-populate them as well
                if (additionalLicenses.length > 0) {
                    setAdditionalLicenses(prev =>
                        prev.map(license => ({
                            ...license,
                            licenseNpi: npiNumber,
                        }))
                    )
                }
            } else {
                setNpiVerificationError('Invalid NPI number')
                setIsNpiVerified(false)
            }
        } catch (error) {
            console.error('NPI verification failed', error)
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    // Server responded with error status
                    setNpiVerificationError(
                        error.response.data?.message || 'Invalid NPI number'
                    )
                } else if (error.request) {
                    // Request made but no response received
                    setNpiVerificationError(
                        'Unable to verify NPI. Please check your connection.'
                    )
                } else {
                    // Something else happened
                    setNpiVerificationError('Failed to verify NPI. Please try again.')
                }
            } else {
                setNpiVerificationError('Failed to verify NPI. Please try again.')
            }
            setIsNpiVerified(false)
        } finally {
            setIsNpiVerifying(false)
        }
    }

    const handleVerifyBillingNpi = async () => {
        setIsBillingNpiVerifying(true)
        setBillingNpiVerificationError('')

        try {
            const billingNpi = (formData.billingNpi || '').trim()
            if (!billingNpi) {
                setBillingNpiVerificationError('Please enter a billing NPI number')
                setIsBillingNpiVerifying(false)
                return
            }

            const response = await api.get(`/npi/number`, {
                params: { npi: billingNpi },
            })

            const data = response.data

            // Check if the API response indicates a valid NPI
            if (data && (data.result_count > 0 || data.results?.length > 0)) {
                setIsBillingNpiVerified(true)
                setIsNpiVerified(true)
                setBillingNpiVerificationError('')
            } else {
                setBillingNpiVerificationError('Invalid billing NPI number')
                setIsBillingNpiVerified(false)
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    // Server responded with error status
                    setBillingNpiVerificationError(
                        error.response.data?.message || 'Invalid billing NPI number'
                    )
                } else if (error.request) {
                    // Request made but no response received
                    setBillingNpiVerificationError(
                        'Unable to verify billing NPI. Please check your connection.'
                    )
                } else {
                    // Something else happened
                    setBillingNpiVerificationError(
                        'Failed to verify billing NPI. Please try again.'
                    )
                }
            } else {
                setBillingNpiVerificationError(
                    'Failed to verify billing NPI. Please try again.'
                )
            }
            setIsBillingNpiVerified(false)
        } finally {
            setIsBillingNpiVerifying(false)
        }
    }

    const handleOnboardingMultiSelectChange =
        (field: keyof OnboardingData) => (event: any) => {
            setOnboardingData(prev => ({
                ...prev,
                [field]: event.target.value,
            }))
            if (field === 'services') setServicesError('')
            if (field === 'degrees') setDegreesError('')
            if (field === 'serviceType') setServiceTypeError('')
        }

    const handleRemoveChip = (
        field: keyof OnboardingData,
        valueToRemove: string
    ) => {
        setIsDeletingChip(true)
        if (field === 'services') {
            setServicesSelectOpen(false)
            setProvidedServicesSelectOpen(false)
        } else if (field === 'degrees') {
            setDegreesSelectOpen(false)
        } else if (field === 'serviceType') {
            setServiceTypesSelectOpen(false)
        }
        setOnboardingData(prev => ({
            ...prev,
            [field]: Array.isArray(prev[field])
                ? (prev[field] as string[]).filter(item => item !== valueToRemove)
                : prev[field],
        }))
        // Reset the flag after a short delay
        setTimeout(() => {
            setIsDeletingChip(false)
        }, 100)
    }

    // State of Operations multi-select handlers
    const handleStateOfOperationsChange = (event: any) => {
        setFormData(prev => ({
            ...prev,
            stateOfOperations: event.target.value as string[],
        }))
        setStateOfOperationsError('')
    }

    const handleRemoveStateOfOperations = (valueToRemove: string) => {
        setIsDeletingChip(true)
        setStateOfOperationsSelectOpen(false)
        setFormData(prev => ({
            ...prev,
            stateOfOperations: prev.stateOfOperations.filter(
                item => item !== valueToRemove
            ),
        }))
        setTimeout(() => {
            setIsDeletingChip(false)
        }, 100)
    }

    // Secondary administrators handlers
    const handleOpenAddAdminDialog = () => {
        setNewAdminDraft({ firstName: '', lastName: '', role: '', email: '' })
        setNewAdminErrors({})
        setShowAddAdminDialog(true)
    }

    const handleCloseAddAdminDialog = () => {
        setShowAddAdminDialog(false)
    }

    const handleAddSecondaryAdministrator = () => {
        const errors: Record<string, string> = {}
        if (!newAdminDraft.firstName.trim()) {
            errors.firstName = 'First name is required'
        }
        if (!newAdminDraft.lastName.trim()) {
            errors.lastName = 'Last name is required'
        }
        if (!newAdminDraft.role.trim()) {
            errors.role = 'Role is required'
        }
        if (!newAdminDraft.email.trim()) {
            errors.email = 'Email is required'
        } else if (!isValidEmail(newAdminDraft.email)) {
            errors.email = ValidationRules.email.message
        }
        if (Object.keys(errors).length > 0) {
            setNewAdminErrors(errors)
            return
        }
        setSecondaryAdministrators(prev => [
            ...prev,
            {
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                firstName: newAdminDraft.firstName.trim(),
                lastName: newAdminDraft.lastName.trim(),
                role: newAdminDraft.role.trim(),
                email: newAdminDraft.email.trim(),
            },
        ])
        setShowAddAdminDialog(false)
    }

    const handleRemoveSecondaryAdministrator = (id: string) => {
        setSecondaryAdministrators(prev => prev.filter(admin => admin.id !== id))
    }

    // Handle Autocomplete change for insurance accepted
    const handleInsuranceAcceptedChange = (
        event: React.SyntheticEvent,
        newValue: Payer[]
    ) => {
        setSelectedPayers(newValue)
        setOnboardingData(prev => ({
            ...prev,
            insuranceAccepted: newValue.map(payer => payer._id),
        }))
        setInsuranceError('')
        // Clear input after selection so user can search for next option
        setPayerSearchInput('')
    }

    // Update selectedPayers when search options include previously selected IDs
    useEffect(() => {
        if (
            payerSearchOptions.length > 0 &&
            onboardingData.insuranceAccepted.length > 0
        ) {
            setSelectedPayers(prevSelected => {
                const currentSelectedIds = prevSelected.map(p => p._id)
                const missingIds = onboardingData.insuranceAccepted.filter(
                    id => !currentSelectedIds.includes(id)
                )

                if (missingIds.length === 0) return prevSelected

                const newPayers = missingIds
                    .map(id => payerSearchOptions.find(p => p._id === id))
                    .filter((p): p is Payer => p !== undefined)

                return [...prevSelected, ...newPayers]
            })
        }
    }, [payerSearchOptions, onboardingData.insuranceAccepted])

    // Helper function to scroll to and focus an element
    const scrollToErrorField = (elementId: string) => {
        setTimeout(() => {
            const element = document.getElementById(elementId)
            if (element) {
                // Scroll to the element
                element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                // Find the input element within the TextField
                const input =
                    element instanceof HTMLInputElement
                        ? element
                        : (element.querySelector('input') as HTMLInputElement | null)
                // Focus the input after scrolling
                setTimeout(() => {
                    if (input) {
                        input.focus()
                        // Highlight the field by triggering focus state
                        input.blur()
                        input.focus()
                    }
                }, 300)
            }
        }, 100)
    }

    const clearNpiFieldError = (field: string) => {
        setNpiStepErrors(prev => {
            if (!prev[field]) return prev
            const { [field]: _removed, ...rest } = prev
            return rest
        })
    }

    // Helper function to check if NPI is required for a license type
    const isNpiRequired = (licenseTypeId: string): boolean => {
        if (licenseTypeId === '69aacb7782693bdedf50db43') return
        const licenseType = licenseTypes.find(lt => lt._id === licenseTypeId)
        return licenseType?.required === 'mandatory'
    }

    const validateBillingFields = (): boolean => {
        const errors: Record<string, string> = {}
        let isValid = true
        let firstErrorId: string | null = null

        const assignError = (key: string, message: string, elementId: string) => {
            errors[key] = message
            if (!firstErrorId) {
                firstErrorId = elementId
            }
            isValid = false
        }

        if (!formData.billingAddressLine.trim()) {
            assignError(
                'billingAddressLine',
                'Billing address is required',
                'billing-address-field'
            )
        }

        if (!formData.billingState.trim()) {
            assignError(
                'billingState',
                'Billing state is required',
                'billing-state-field'
            )
        }

        if (!formData.billingCity.trim()) {
            assignError(
                'billingCity',
                'Billing city is required',
                'billing-city-field'
            )
        }

        if (!formData.billingZipCode.trim()) {
            assignError(
                'billingZipCode',
                'Billing zip code is required',
                'billing-zip-field'
            )
        }

        if (!formData.billingCountry.trim()) {
            assignError(
                'billingCountry',
                'Billing country is required',
                'billing-country-field'
            )
        }

        setNpiStepErrors(prev => ({
            ...prev,
            ...errors
        }))

        if (!isValid && firstErrorId) {
            scrollToErrorField(firstErrorId)
        }

        return isValid
    }

    const validateNpiAdditionalFields = (): boolean => {
        const errors: Record<string, string> = {}
        let isValid = true
        let firstErrorId: string | null = null

        const assignError = (key: string, message: string, elementId: string) => {
            errors[key] = message
            if (!firstErrorId) {
                firstErrorId = elementId
            }
            isValid = false
        }

        if (!licenseTypeArray || licenseTypeArray.length === 0) {
            assignError(
                'licenseType',
                'Specialty License Type is required',
                'license-type-field'
            )
        }

        // Add Organization NPI validation checks:
        if (!formData.npiOrganization.trim()) {
            assignError(
                'npiOrganization',
                'Organization NPI is required',
                'npiOrganization'
            )
        } else if (formData.npiOrganization.trim().length !== 10 || !/^\d+$/.test(formData.npiOrganization)) {
            assignError(
                'npiOrganization',
                'NPI must be a 10-digit number',
                'npiOrganization'
            )
        }
        if (!formData.npiOrganizationDocUrl) {
            assignError(
                'npiOrganizationDocUrl',
                'Click the upload icon on the right to upload the NPI document',
                'npiOrganizationDoc'
            )
            assignError(
                'npiOrganization',
                'Please upload your NPI Certificate.',
                'npiOrganization'
            )
        }
        if (!isNpiVerified) {
            assignError(
                'npiVerification',
                'Please verify your Organization NPI before proceeding',
                'npiOrganization'
            )
        }


        // Validate NPI if required for the selected license type
        const requiresNpi = licenseTypeArray.some(id => isNpiRequired(id));
        if (
            licenseTypeArray &&
            licenseTypeArray.length > 0 &&
            requiresNpi &&
            !onboardingData.licenseNpi?.trim()
        ) {
            assignError(
                'licenseNpi',
                'NPI Number is required for this license type',
                'license-name-field'
            )
        }

        // Validate additional licenses
        additionalLicenses.forEach((license, index) => {
            if (!license.licenseType) {
                assignError(
                    `additionalLicenseType_${index}`,
                    'Specialty License Type is required',
                    `additional-license-type-${index}`
                )
            } else if (
                isNpiRequired(license.licenseType) &&
                !license.licenseNpi?.trim()
            ) {
                assignError(
                    `additionalLicenseNpi_${index}`,
                    'NPI Number is required for this license type',
                    `additional-license-npi-${index}`
                )
            }

            const addRequiresLicenseDetails = license.licenseType && license.licenseType !== '69aacb7782693bdedf50db43'
            if (addRequiresLicenseDetails) {
                const statesSelected = Array.isArray(license.licenseState)
                    ? license.licenseState
                    : (license.licenseState ? [license.licenseState] : []);

                if (statesSelected.length === 0) {
                    assignError(
                        `additionalLicenseState_${index}`,
                        'License state is required',
                        `additional-license-state-${index}`
                    )
                }

                if (!license.licenseNumber?.trim()) {
                    assignError(
                        `additionalLicenseNumber_${index}`,
                        'License number is required',
                        `additional-license-number-${index}`
                    )
                }

                if (!license.issueDate?.trim()) {
                    assignError(
                        `additionalIssueDate_${index}`,
                        'License issue date is required',
                        `additional-license-issue-${index}`
                    )
                }

                if (!license.licenseExpirationDate?.trim()) {
                    assignError(
                        `additionalLicenseExpirationDate_${index}`,
                        'License expiration date is required',
                        `additional-license-expiration-${index}`
                    )
                }

                if (shouldShowSpecialtyLicense) {
                    let missingDoc = false;
                    statesSelected.forEach(st => {
                        const doc = license.licenseStateDocs?.[st];
                        if (!doc?.fileUrl?.trim()) {
                            missingDoc = true;
                        }
                    });
                    if (missingDoc || (statesSelected.length === 0 && !license.licenseFileUrl?.trim())) {
                        assignError(
                            `additionalLicenseFileUrl_${index}`,
                            'Specialty License document is required for all selected states',
                            `additional-license-file-${index}`
                        )
                    }
                }
            }
        })

        const requiresLicenseDetails = licenseTypeArray.some(id => id !== '69aacb7782693bdedf50db43');
        if (requiresLicenseDetails) {
            const statesSelected = Array.isArray(onboardingData.licenseState)
                ? onboardingData.licenseState
                : (onboardingData.licenseState ? [onboardingData.licenseState] : []);

            if (statesSelected.length === 0) {
                assignError(
                    'licenseState',
                    'License state is required',
                    'license-state-field'
                )
            }

            if (!onboardingData.licenseNumber.trim()) {
                assignError(
                    'licenseNumber',
                    'License number is required',
                    'license-number-field'
                )
            }

            if (!onboardingData.issueDate.trim()) {
                assignError(
                    'issueDate',
                    'License issue date is required',
                    'license-issue-field'
                )
            }

            if (!onboardingData.licenseExpirationDate.trim()) {
                assignError(
                    'licenseExpirationDate',
                    'License expiration date is required',
                    'license-expiration-field'
                )
            }
        }

        // License file is optional for '69aacb7782693bdedf50db43'
        if (shouldShowSpecialtyLicense && requiresLicenseDetails) {
            const statesSelected = Array.isArray(onboardingData.licenseState)
                ? onboardingData.licenseState
                : (onboardingData.licenseState ? [onboardingData.licenseState] : []);

            let missingDoc = false;
            statesSelected.forEach(st => {
                const doc = onboardingData.licenseStateDocs?.[st];
                if (!doc?.fileUrl?.trim()) {
                    missingDoc = true;
                }
            });
            if (missingDoc || (statesSelected.length === 0 && !onboardingData.licenseFileUrl.trim())) {
                assignError(
                    'licenseFileUrl',
                    'Specialty License document is required for all selected states',
                    'license-file-field'
                )
            }
        }

        let phoneMessage = ''
        const billingPhoneMessage = getBillingPhoneValidationMessage(
            formData.billingPhone
        )
        if (billingPhoneMessage) {
            phoneMessage = billingPhoneMessage
            if (!firstErrorId) {
                firstErrorId = 'billing-phone-field'
            }
            isValid = false
        }
        setBillingPhoneError(phoneMessage)
        if (phoneMessage) {
            setIsBillingPhoneTouched(true)
        }

        setNpiStepErrors(errors)

        if (!isValid && firstErrorId) {
            scrollToErrorField(firstErrorId)
        }

        return isValid
    }

    const validateSpecialtyLicenseFields = (): boolean => {
        if (!shouldShowSpecialtyLicense) return true

        const errors: Record<string, string> = {}
        let isValid = true
        let firstErrorId: string | null = null

        const assignError = (key: string, message: string, elementId: string) => {
            errors[key] = message
            if (!firstErrorId) {
                firstErrorId = elementId
            }
            isValid = false
        }

        const requiresLicenseDetails = licenseTypeArray.some(id => id !== '69aacb7782693bdedf50db43');
        if (requiresLicenseDetails) {
            const statesSelected = Array.isArray(onboardingData.licenseState)
                ? onboardingData.licenseState
                : (onboardingData.licenseState ? [onboardingData.licenseState] : []);

            if (statesSelected.length === 0) {
                assignError(
                    'licenseState',
                    'License state is required',
                    'license-state-field'
                )
            }

            if (!onboardingData.licenseNumber.trim()) {
                assignError(
                    'licenseNumber',
                    'License number is required',
                    'license-number-field'
                )
            }

            if (!onboardingData.issueDate.trim()) {
                assignError(
                    'issueDate',
                    'License issue date is required',
                    'license-issue-field'
                )
            }

            if (!onboardingData.licenseExpirationDate.trim()) {
                assignError(
                    'licenseExpirationDate',
                    'License expiration date is required',
                    'license-expiration-field'
                )
            }

            let missingDoc = false;
            statesSelected.forEach(st => {
                const doc = onboardingData.licenseStateDocs?.[st];
                if (!doc?.fileUrl?.trim()) {
                    missingDoc = true;
                }
            });
            if (missingDoc || (statesSelected.length === 0 && !onboardingData.licenseFileUrl.trim())) {
                assignError(
                    'licenseFileUrl',
                    'Specialty License document is required for all selected states',
                    'license-number-field'
                )
            }
        }

        setNpiStepErrors(errors)

        if (!isValid && firstErrorId) {
            scrollToErrorField(firstErrorId)
        }

        return isValid
    }

    const moveToStep = (nextStep: number) => {
        setCurrentStep(nextStep)
        if (nextStep === 1 || nextStep === 2) {
            setFormData(prev => ({
                ...prev,
                billingNpi: prev.billingNpi || prev.npiOrganization || '',
                taxId: prev.taxId || prev.ein || '',
                billingPhone: prev.billingPhone || prev.businessPhoneNumber || prev.phoneNumber || '',
                billingAddressLine: prev.billingAddressLine || prev.address || '',
                billingCity: prev.billingCity || prev.city || '',
                billingState: prev.billingState || prev.state || '',
                billingZipCode: prev.billingZipCode || prev.zipCode || '',
                billingCountry: prev.billingCountry || prev.country || '',
            }))
            setOnboardingData(prev => ({
                ...prev,
                licenseNpi: prev.licenseNpi || formData.npiOrganization || '',
            }))
        }
        window.scrollTo(0, 0)
        if (document.documentElement) {
            document.documentElement.scrollTop = 0
        }
        if (document.body) {
            document.body.scrollTop = 0
        }
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
            if (document.documentElement) {
                document.documentElement.scrollTo({ top: 0, behavior: 'smooth' })
            }
            if (document.body) {
                document.body.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }, 100)
    }

    // console.log('formData', formData)

    const handleNext = async (action: 'skip' | 'save' = 'save') => {
        switch (currentStep) {

            case 0: {
                if (action === 'skip' || !hasAboutYouData) {
                    moveToStep(1)
                    return
                }
                moveToStep(1)
                return
            }
            case 1: {
                let hasError = false


                if (!onboardingData.serviceType || onboardingData.serviceType.length === 0) {
                    setServiceTypeError('Please select at least one service type')
                    hasError = true
                }
                if (!onboardingData.services || onboardingData.services.length === 0) {
                    setServicesError('Please select at least one specialty')
                    hasError = true
                }
                if (!onboardingData.degrees || onboardingData.degrees.length === 0) {
                    setDegreesError('Please select at least one degree')
                    hasError = true
                }
                if (!onboardingData.insuranceAccepted || onboardingData.insuranceAccepted.length === 0) {
                    setInsuranceError('Please select at least one insurance provider')
                    hasError = true
                }
                if (!onboardingData.noOfProvider || onboardingData.noOfProvider.trim() === '') {
                    setNoOfProviderError('Number of providers is required')
                    hasError = true
                }
                if (hasError) {
                    if (!onboardingData.serviceType || onboardingData.serviceType.length === 0) {
                        scrollToErrorField('service-type-field')
                    } else if (!onboardingData.services || onboardingData.services.length === 0) {
                        scrollToErrorField('services-field')
                    } else if (!onboardingData.degrees || onboardingData.degrees.length === 0) {
                        scrollToErrorField('degrees-field')
                    } else if (!onboardingData.insuranceAccepted || onboardingData.insuranceAccepted.length === 0) {
                        scrollToErrorField('insurance-field')
                    } else if (!onboardingData.noOfProvider || onboardingData.noOfProvider.trim() === '') {
                        scrollToErrorField('no-of-providers-field')
                    }
                    return
                }

                const hasBillingNpi =
                    formData.billingNpi && formData.billingNpi.trim().length > 0
                if (!formData.billingNpi || !formData.billingNpi.trim()) {
                    setBillingNpiVerificationError('Billing NPI is required')
                    scrollToErrorField('billing-npi-field')
                    return
                }
                if (hasBillingNpi && !isBillingNpiVerified) {
                    setBillingNpiVerificationError(
                        'Please verify your Billing NPI before proceeding.'
                    )
                    scrollToErrorField('billing-npi-field')
                    return
                }

                if (!formData.billingPhone || !formData.billingPhone.trim()) {
                    setBillingPhoneError('Billing phone number is required')
                    scrollToErrorField('billing-phone-field')
                    return
                }
                const billingPhoneMessage = getBillingPhoneValidationMessage(
                    formData.billingPhone
                )
                if (billingPhoneMessage) {
                    setBillingPhoneError(billingPhoneMessage)
                    setIsBillingPhoneTouched(true)
                    scrollToErrorField('billing-phone-field')
                    return
                }

                const billingFieldsValid = validateBillingFields()
                if (!billingFieldsValid) {
                    return
                }

                const specialtyLicenseValid = validateSpecialtyLicenseFields()
                if (!specialtyLicenseValid) {
                    return
                }

                moveToStep(2)
                return
            }

            case 2: {
                const extraFieldsValid = validateNpiAdditionalFields()
                if (!extraFieldsValid) {
                    return
                }

                // Create therapist account before moving to Step 4
                try {
                    setIsProcessingPayment(true)
                    setPaymentError(null)

                    if (!therapistId) {
                        // Normalize licenses: include primary license + additional licenses
                        const primaryLicensesMapped = licenseTypeArray.flatMap(lt => {
                            const statesSelected = Array.isArray(onboardingData.licenseState)
                                ? onboardingData.licenseState
                                : (onboardingData.licenseState ? [onboardingData.licenseState] : []);

                            return statesSelected.map(st => {
                                const docForState = onboardingData.licenseStateDocs?.[st];
                                return {
                                    licenseType: lt,
                                    licenseNpi: onboardingData.licenseNpi,
                                    licenseState: st,
                                    licenseNumber: onboardingData.licenseNumber,
                                    issueDate: onboardingData.issueDate,
                                    expiryDate: onboardingData.licenseExpirationDate,
                                    licenseStatus: onboardingData.licenseStatus,
                                    licenseFileUrl: docForState?.fileUrl || onboardingData.licenseFileUrl || '',
                                };
                            });
                        });
                        const additionalLicensesMapped = additionalLicenses.flatMap(l => {
                            const statesSelected = Array.isArray(l.licenseState)
                                ? l.licenseState
                                : (l.licenseState ? [l.licenseState] : []);

                            if (statesSelected.length === 0) {
                                return [{
                                    licenseType: l.licenseType,
                                    licenseNpi: l.licenseNpi,
                                    licenseState: '',
                                    licenseNumber: l.licenseNumber,
                                    issueDate: l.issueDate,
                                    expiryDate: l.licenseExpirationDate,
                                    licenseStatus: l.licenseStatus,
                                    licenseFileUrl: l.licenseFileUrl || '',
                                }];
                            }

                            return statesSelected.map(st => {
                                const docForState = l.licenseStateDocs?.[st];
                                return {
                                    licenseType: l.licenseType,
                                    licenseNpi: l.licenseNpi,
                                    licenseState: st,
                                    licenseNumber: l.licenseNumber,
                                    issueDate: l.issueDate,
                                    expiryDate: l.licenseExpirationDate,
                                    licenseStatus: l.licenseStatus,
                                    licenseFileUrl: docForState?.fileUrl || l.licenseFileUrl || '',
                                };
                            });
                        });
                        const normalizedLicenses = [
                            ...primaryLicensesMapped,
                            ...additionalLicensesMapped,
                        ].filter(l => !!l.licenseType || !!l.licenseNumber)

                        const therapistData = {
                            agencyName: formData.agencyName,
                            dbaName: formData.dbaName,
                            companyEin: formData.ein,
                            companyNpi: formData.npiOrganization || null,
                            nursingNpi: formData.nursingNpi || null,
                            nursingNpiDoc: formData.nursingNpiDocUrl || null,
                            therapyNpi: formData.therapyNpi || null,
                            therapyNpiDoc: formData.therapyNpiDocUrl || null,
                            caregiverNpi: formData.caregiverNpi || null,
                            caregiverNpiDoc: formData.caregiverNpiDocUrl || null,
                            // licenseNumber: formData.agencyLicenseNumber || null,
                            // licenseExpiration: formData.agencyLicenseExpiration
                            //     ? formatDate(formData.agencyLicenseExpiration, 'MM/dd/yyyy')
                            //     : null,
                            companyStates: formData.stateOfOperations,
                            companyEmail: formData.businessEmail,
                            companyPhone: formData.businessPhoneNumber || null,
                            website: formData.website || null,
                            professionalLiabilityInsurance:
                                formData.professionalLiabilityInsurance || null,
                            generalLiabilityInsurance: formData.generalLiabilityInsurance || null,
                            coverageOfInsurance: formData.coverageOfInsurance || null,
                            workersCompensation: formData.workersCompensation || null,
                            businessLicense: formData.agencyNameDocUrl || null,
                            agencyLicense: formData.dbaNameDocUrl || null,
                            npiCertificate: formData.npiOrganizationDocUrl || null,
                            wnineForm: formData.w9FormUrl || null,
                            agencyNameDoc: formData.agencyNameDocUrl || null,
                            dbaNameDoc: formData.dbaNameDocUrl || null,
                            companyEinDoc: formData.einDocUrl || null,
                            companyNpiDoc: formData.npiOrganizationDocUrl || null,
                            certificateInsurance: formData.certificateOfInsuranceUrl || null,
                            clinicalDoc: formData.clinicalAccreditationUrls || [],
                            professionalLiabilityInsuranceDoc: formData.professionalLiabilityInsuranceDocUrls || [],
                            generalLiabilityInsuranceDoc: formData.generalLiabilityInsuranceDocUrls || [],
                            coverageOfInsuranceDoc: formData.coverageOfInsuranceDocUrls || [],
                            workersCompensationDoc: formData.workersCompensationDocUrls || [],

                            // gender: formData.gender,
                            // dob: formData.dateOfBirth
                            //     ? formatDate(formData.dateOfBirth, 'yyyy-MM-dd')
                            //     : undefined,





                            addressLine1: formData.address,
                            addressLine2: formData.addressLine2,

                            city: formData.city,
                            country: formData.country,
                            state: formData.state,
                            zipCode: formData.zipCode,
                            latitude: formData.latitude,
                            longitude: formData.longitude,

                            serviceProvideType: onboardingData.services,
                            serviceType: onboardingData.serviceType,
                            insuranceAccepted: onboardingData.insuranceAccepted,
                            yearsOfExperience:
                                parseFloat(onboardingData.yearsOfExperience) || 0,
                            noOfProvider: onboardingData.noOfProvider,









                            bio: onboardingData.professionalBio,
                            specialization: onboardingData.degrees,


                            // boardCertificationNo:
                            //     onboardingData.boardCertificationNo?.trim() || null,
                            // boardSpeciality: onboardingData.boardSpeciality?.trim() || null,
                            // boardName: onboardingData.boardName?.trim() || null,
                            // boardSubSpeciality:
                            //     onboardingData.boardSubSpeciality?.trim() || null,


                            // billingType: formData.billingType,
                            // providerNpi: onboardingData.npiNumber?.trim() || null,
                            ssn: formData.ssn,
                            billingNpi: formData.billingNpi?.trim() || null,
                            taxId: formData.taxId,
                            companyName: formData.companyName || null,
                            billingPhone: formData.billingPhone,
                            billingAddressLine1: formData.billingAddressLine,
                            billingState: formData.billingState,
                            billingCountry: formData.billingCountry,
                            billingCity: formData.billingCity,
                            billingZipCode: formData.billingZipCode,
                            profilePhotoUrl: onboardingData.profilePicture || undefined,

                            licenses: normalizedLicenses,

                            users: [
                                {
                                    firstName: formData.firstName,
                                    lastName: formData.lastName,
                                    email: formData.email,
                                    phone: formData.phoneNumber,
                                    password: formData.password,
                                },
                                ...secondaryAdministrators.map(admin => ({
                                    firstName: admin.firstName,
                                    lastName: admin.lastName,
                                    email: admin.email,
                                    role: admin.role,
                                })),
                            ],
                        }

                        const accountResponse =
                            await membershipApi.agencySignup(therapistData)

                        console.log('Therapist signup response:', accountResponse)

                        if (!accountResponse.id) {
                            throw new Error(
                                accountResponse.message || 'Failed to create provider account'
                            )
                        }

                        const newTherapistId = accountResponse.id
                        setTherapistId(newTherapistId)

                        // Show success message from response
                        if (accountResponse.message) {
                            setSuccessMessage(accountResponse.message)
                            setShowSuccessAlert(true)
                        }

                        // Clean localStorage after successful account creation
                        try {
                            draftPersistenceEnabledRef.current = false
                            localStorage.removeItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
                        } catch (e) {
                            console.warn('Failed to clear draft after signup:', e)
                        }
                    }

                    moveToStep(3)
                } catch (error) {
                    console.error('Error in therapist signup:', error)
                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : 'An error occurred during account creation'
                    setPaymentError(errorMessage)
                } finally {
                    setIsProcessingPayment(false)
                }
                return
            }
            case 3: {
                setPaymentError(null)
                if (subscriptionPaymentSuccess) {
                    moveToStep(4)
                    return
                }
                navigate('/agency-login')

                return
            }
            default: {
                if (currentStep < steps.length - 1) {
                    moveToStep(currentStep + 1)
                } else {
                    handleCompleteProfile()
                }
            }
        }
    }

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        } else {
            setShowOnboarding(false)
        }
    }

    const handleProfilePictureChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        event.target.value = '' // Reset to allow re-uploading same file
        if (file) {
            try {
                setIsUploadingProfilePhoto(true)

                // Upload file to server
                const uploadResponse = await membershipApi.uploadProfilePhoto(file)

                // Store the fileUrl returned from the API
                const imageUrl = uploadResponse?.fileUrl || uploadResponse?.profilePhotoUrl || uploadResponse?.url || uploadResponse?.data?.profilePhotoUrl || uploadResponse?.data?.url
                if (imageUrl) {
                    setOnboardingData(prev => ({
                        ...prev,
                        profilePicture: imageUrl,
                    }))
                }
            } catch (error) {
                console.error('Error uploading profile photo:', error)
                // You could add an error notification here if needed
            } finally {
                setIsUploadingProfilePhoto(false)
            }
        }
    }

    const handleRemoveProfilePicture = () => {
        setOnboardingData(prev => ({
            ...prev,
            profilePicture: null,
        }))
    }

    const handleBioChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setOnboardingData(prev => ({
            ...prev,
            professionalBio: event.target.value,
        }))
    }

    const handleStateLicenseDocumentChange = (stateName: string) => async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        try {
            setStateLicenseUploadLoading(prev => ({ ...prev, [stateName]: true }))

            const existingDoc = onboardingData.licenseStateDocs?.[stateName]
            if (existingDoc?.filePreview) {
                URL.revokeObjectURL(existingDoc.filePreview)
            }

            const uploadRes = await membershipApi.uploadLicenseFile(file)
            const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.fileUrl || ''
            if (url) {
                let previewUrl = ''
                if (file.type.startsWith('image/')) {
                    previewUrl = URL.createObjectURL(file)
                }

                setOnboardingData(prev => {
                    const newDocs = { ...(prev.licenseStateDocs || {}) }
                    newDocs[stateName] = {
                        fileUrl: url,
                        fileName: file.name,
                        filePreview: previewUrl
                    }
                    return {
                        ...prev,
                        licenseStateDocs: newDocs,
                        licenseFileUrl: url,
                        licenseFileName: file.name,
                        licenseFilePreview: previewUrl
                    }
                })
                clearNpiFieldError('licenseFileUrl')
            }
        } catch (e) {
            console.error(`Error uploading license for ${stateName}:`, e)
        } finally {
            setStateLicenseUploadLoading(prev => ({ ...prev, [stateName]: false }))
        }
    }

    const handleRemoveStateLicense = (stateName: string) => (e?: React.MouseEvent) => {
        e?.stopPropagation()
        const doc = onboardingData.licenseStateDocs?.[stateName]
        if (doc?.filePreview) {
            URL.revokeObjectURL(doc.filePreview)
        }
        setOnboardingData(prev => {
            const newDocs = { ...(prev.licenseStateDocs || {}) }
            delete newDocs[stateName]
            return {
                ...prev,
                licenseStateDocs: newDocs,
                licenseFileUrl: '',
                licenseFileName: '',
                licenseFilePreview: '',
            }
        })
    }

    const handleLicenseDocumentChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        event.target.value = '' // Reset value to allow selecting the same file again
        if (!file) return
        try {
            setIsUploadingLicense(true)
            // Clean up previous preview URL if exists
            if (onboardingData.licenseFilePreview) {
                URL.revokeObjectURL(onboardingData.licenseFilePreview)
            }

            const uploadRes = await membershipApi.uploadLicenseFile(file)
            const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.fileUrl || ''
            if (url) {
                // Create preview URL for images
                let previewUrl = ''
                if (file.type.startsWith('image/')) {
                    previewUrl = URL.createObjectURL(file)
                }

                setOnboardingData(prev => ({
                    ...prev,
                    licenseFileUrl: url,
                    licenseFileName: file.name,
                    licenseFilePreview: previewUrl,
                }))
                clearNpiFieldError('licenseFileUrl')
            }
        } catch (e) {
            console.error('Error uploading license:', e)
        } finally {
            setIsUploadingLicense(false)
        }
    }

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        const file = event.dataTransfer.files[0]
        if (
            file &&
            (file.type === 'application/pdf' || file.type.startsWith('image/'))
        ) {
            try {
                // Clean up previous preview URL if exists
                if (onboardingData.licenseFilePreview) {
                    URL.revokeObjectURL(onboardingData.licenseFilePreview)
                }

                const uploadRes = await membershipApi.uploadLicenseFile(file)
                const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.fileUrl || ''
                if (url) {
                    // Create preview URL for images
                    let previewUrl = ''
                    if (file.type.startsWith('image/')) {
                        previewUrl = URL.createObjectURL(file)
                    }

                    setOnboardingData(prev => ({
                        ...prev,
                        licenseFileUrl: url,
                        licenseFileName: file.name,
                        licenseFilePreview: previewUrl,
                    }))
                    clearNpiFieldError('licenseFileUrl')
                }
            } catch (e) {
                console.error('Error uploading dropped license:', e)
            }
        }
    }

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
    }

    // Corporate documents / clinical accreditation upload (Agency Credentials & Verification step)
    const handleDocumentUpload = (fieldPrefix: string) => async (file: File) => {
        activeUploadsCountRef.current[fieldPrefix] = (activeUploadsCountRef.current[fieldPrefix] || 0) + 1
        setUploadingDocumentField(fieldPrefix)
        try {
            console.log('[Upload] Starting upload for file:', file.name, 'with prefix:', fieldPrefix);
            const uploadRes = await membershipApi.uploadLicenseFile(file)
            console.log('[Upload] Upload API response:', uploadRes);
            const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.files?.[0]?.location || uploadRes?.fileUrl || ''
            console.log('[Upload] Extracted URL:', url);
            if (url) {
                if (fieldPrefix === 'generalLiabilityInsuranceDoc') {
                    setFormData(prev => ({
                        ...prev,
                        generalLiabilityInsuranceDocUrls: [url],
                        generalLiabilityInsuranceDocNames: [file.name],
                    }))
                } else if (fieldPrefix === 'professionalLiabilityInsuranceDoc') {
                    setFormData(prev => ({
                        ...prev,
                        professionalLiabilityInsuranceDocUrls: [url],
                        professionalLiabilityInsuranceDocNames: [file.name],
                    }))
                } else if (fieldPrefix === 'coverageOfInsuranceDoc') {
                    setFormData(prev => ({
                        ...prev,
                        coverageOfInsuranceDocUrls: [...(prev.coverageOfInsuranceDocUrls || []), url],
                        coverageOfInsuranceDocNames: [...(prev.coverageOfInsuranceDocNames || []), file.name],
                    }))
                } else if (fieldPrefix === 'workersCompensationDoc') {
                    setFormData(prev => ({
                        ...prev,
                        workersCompensationDocUrls: [url],
                        workersCompensationDocNames: [file.name],
                    }))
                } else if (fieldPrefix === 'clinicalAccreditation') {
                    setFormData(prev => ({
                        ...prev,
                        clinicalAccreditationUrls: [...(prev.clinicalAccreditationUrls || []), url],
                        clinicalAccreditationNames: [...(prev.clinicalAccreditationNames || []), file.name],
                    }))
                } else {
                    setFormData(prev => ({
                        ...prev,
                        [`${fieldPrefix}Url`]: url,
                        [`${fieldPrefix}Name`]: file.name,
                    }))
                }
                if (fieldPrefix === 'agencyNameDoc') setAgencyNameDocError('')
                else if (fieldPrefix === 'dbaNameDoc') setDbaNameDocError('')
                else if (fieldPrefix === 'einDoc') setEinDocError('')
                else if (fieldPrefix === 'npiOrganizationDoc') {
                    setNpiOrganizationDocError('')
                    setNpiStepErrors(prev => ({ ...prev, npiOrganizationDocUrl: '', npiOrganization: '' }))
                }
            }
        } catch (e: any) {
            console.error('[Upload] Error uploading document:', e)
            const errText = e?.response?.data?.message || e?.message || 'Failed to upload document'
            setErrorMessage(errText)
            setShowErrorAlert(true)
        } finally {
            activeUploadsCountRef.current[fieldPrefix] = Math.max(0, (activeUploadsCountRef.current[fieldPrefix] || 0) - 1)
            if (activeUploadsCountRef.current[fieldPrefix] === 0) {
                setUploadingDocumentField(null)
            }
        }
    }

    const handleRemoveMainLicense = (e?: React.MouseEvent) => {
        e?.stopPropagation()
        if (onboardingData.licenseFilePreview) {
            URL.revokeObjectURL(onboardingData.licenseFilePreview)
        }
        setOnboardingData(prev => ({
            ...prev,
            licenseFileUrl: '',
            licenseFileName: '',
            licenseFilePreview: '',
        }))
    }

    const handleRemoveAdditionalLicenseFile = (
        index: number,
        e?: React.MouseEvent
    ) => {
        e?.stopPropagation()
        setAdditionalLicenses(prev => {
            const next = [...prev]
            if (next[index]?.licenseFilePreview) {
                URL.revokeObjectURL(next[index].licenseFilePreview!)
            }
            next[index] = {
                ...next[index],
                licenseFileUrl: '',
                licenseFileName: '',
                licenseFilePreview: '',
            }
            return next
        })
    }

    const handleStripeConnect = async () => {
        setIsConnectingBank(true)
        setPaymentError(null)

        try {
            console.log('Connecting bank account for:', formData.email)

            // Call the API to get Stripe hosted page URL
            const response = await membershipApi.connectAgencyBank(
                formData.email,
                '/agency-login'
            )

            console.log('Full API response:', response)

            // Check if response contains the URL directly or in a data property
            let stripeUrl = null
            if (typeof response === 'string') {
                stripeUrl = response
            } else if (response && response.data) {
                stripeUrl = response.data
            } else if (response && response.stripe_hosted_page_url) {
                stripeUrl = response.stripe_hosted_page_url
            }

            if (stripeUrl) {
                console.log('Redirecting to Stripe hosted page:', stripeUrl)

                // Redirect to Stripe hosted page
                window.location.href = stripeUrl
            } else {
                console.error('No Stripe hosted page URL received')
                setPaymentError(
                    'Failed to initialize bank connection. Please try again.'
                )
            }
        } catch (error) {
            console.error('Error connecting bank account:', error)
            console.error('Error details:', error.response?.data)
            console.error('Error status:', error.response?.status)

            // Check if it's a 401 error (unauthorized)
            if (error.response?.status === 401) {
                setPaymentError(
                    'Authentication required. Please ensure you are logged in.'
                )
            } else if (error.response?.status === 404) {
                setPaymentError(
                    'Bank connection service not found. Please contact support.'
                )
            } else if (error.code === 'NETWORK_ERROR' || !error.response) {
                setPaymentError(
                    'Network error. Please check your internet connection and try again.'
                )
            } else {
                setPaymentError(
                    `Failed to connect bank account: ${error.response?.data?.message || 'Please try again.'}`
                )
            }
        } finally {
            setIsConnectingBank(false)
        }
    }

    const handleCompleteProfile = async () => {
        setIsSubmitting(true)
        try {

            if (true) {
                setShowSuccessAlert(true)
                try {
                    localStorage.removeItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
                } catch { }
                // Redirect to login after 2 seconds
                setTimeout(() => {
                    navigate('/agency-login')
                }, 2000)
            } else {
                throw new Error('Failed to create account')
            }
        } catch (error) {
            console.error('Error creating account:', error)
            // TODO: Show error message to user
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleLoginLinkClick = () => {
        try {
            localStorage.removeItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
        } catch { }
        navigate('/agency-login')
    }

    // Auto-open subscription dialog when reaching step 3 (Choose Subscription) - only once
    useEffect(() => {
        if (
            currentStep === 3 &&
            showOnboarding &&
            !showSubscriptionDialog &&
            !hasAutoOpenedSubscriptionDialog
        ) {
            setShowSubscriptionDialog(true)
            setHasAutoOpenedSubscriptionDialog(true)
        }
    }, [
        currentStep,
        showOnboarding,
        showSubscriptionDialog,
        hasAutoOpenedSubscriptionDialog,
    ])

    // Fetch specialties on component mount
    useEffect(() => {
        const fetchSpecialties = async () => {
            try {
                setIsLoadingSpecialties(true)
                const response = await membershipApi.getSpecialties()
                setSpecialties(response.data || response)
            } catch (error) {
                console.error('Error fetching specialties:', error)
            } finally {
                setIsLoadingSpecialties(false)
            }
        }

        const fetchProvidedServices = async () => {
            try {
                setIsLoadingProvidedServices(true)
                const response = await api.get('/agency/provided-services')
                setProvidedServices(response.data?.data || response.data || response)
            } catch (error) {
                console.error('Error fetching provided services:', error)
            } finally {
                setIsLoadingProvidedServices(false)
            }
        }

        const fetchServiceTypes = async () => {
            try {
                setIsLoadingServiceTypes(true)
                const response = await api.get('/specilities/service-type')
                setServiceTypes(response.data || response)
            } catch (error) {
                console.error('Error fetching service types:', error)
            } finally {
                setIsLoadingServiceTypes(false)
            }
        }

        fetchSpecialties()
        fetchProvidedServices()
        fetchServiceTypes()
    }, [])

    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => {
            // Cleanup main license preview
            if (onboardingData.licenseFilePreview) {
                URL.revokeObjectURL(onboardingData.licenseFilePreview)
            }
            // Cleanup additional license previews
            additionalLicenses.forEach(license => {
                if (license.licenseFilePreview) {
                    URL.revokeObjectURL(license.licenseFilePreview)
                }
            })
        }
    }, [onboardingData.licenseFilePreview, additionalLicenses])

    // Debounce search input for payer search
    const debouncedPayerSearch = useDebounce(payerSearchInput, 300)
    const debouncedBoardNameSearch = useDebounce(boardNameSearch, 300)
    const debouncedBoardSpecialitySearch = useDebounce(boardSpecialitySearch, 300)
    const debouncedBoardSubSpecialitySearch = useDebounce(
        boardSubSpecialitySearch,
        300
    )

    // Search board options when debounced input changes
    useEffect(() => {
        const searchBoardNames = async () => {
            setIsSearchingBoardName(true)
            try {
                const response = await membershipApi.getBoardNames(
                    debouncedBoardNameSearch
                )
                const data = response?.data || response
                setBoardNameOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board names:', error)
                setBoardNameOptions([])
            } finally {
                setIsSearchingBoardName(false)
            }
        }
        searchBoardNames()
    }, [debouncedBoardNameSearch])

    useEffect(() => {
        const searchBoardSpecialities = async () => {
            // Only fetch if a board is selected
            if (!onboardingData.boardName) {
                setBoardSpecialityOptions([])
                return
            }
            setIsSearchingBoardSpeciality(true)
            try {
                const response = await membershipApi.getBoardSpecialities(
                    debouncedBoardSpecialitySearch,
                    onboardingData.boardName
                )
                const data = response?.data || response
                setBoardSpecialityOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board specialities:', error)
                setBoardSpecialityOptions([])
            } finally {
                setIsSearchingBoardSpeciality(false)
            }
        }
        searchBoardSpecialities()
    }, [debouncedBoardSpecialitySearch, onboardingData.boardName])

    useEffect(() => {
        const searchBoardSubSpecialities = async () => {
            // Only fetch if a speciality is selected
            if (!onboardingData.boardSpeciality) {
                setBoardSubSpecialityOptions([])
                return
            }
            setIsSearchingBoardSubSpeciality(true)
            try {
                const response = await membershipApi.getBoardSubspecialities(
                    debouncedBoardSubSpecialitySearch,
                    onboardingData.boardSpeciality
                )
                const data = response?.data || response
                setBoardSubSpecialityOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board subspecialities:', error)
                setBoardSubSpecialityOptions([])
            } finally {
                setIsSearchingBoardSubSpeciality(false)
            }
        }
        searchBoardSubSpecialities()
    }, [debouncedBoardSubSpecialitySearch, onboardingData.boardSpeciality])

    // Search payers when debounced input changes
    useEffect(() => {
        const searchPayers = async () => {
            if (debouncedPayerSearch.trim().length === 0) {
                setPayerSearchOptions([])
                setIsSearchingPayers(false)
                return
            }

            // Set loading state but keep previous results visible
            setIsSearchingPayers(true)

            try {
                const response = await membershipApi.searchPayers(debouncedPayerSearch)
                const payersData = response.data || response
                // Update options with new results (even if empty - user searched and got no results)
                if (Array.isArray(payersData)) {
                    setPayerSearchOptions(payersData)
                }
            } catch (error) {
                console.error('Error searching payers:', error)
                // Don't clear options on error, keep previous results visible
            } finally {
                setIsSearchingPayers(false)
            }
        }

        searchPayers()
    }, [debouncedPayerSearch])

    // Fetch states on component mount
    useEffect(() => {
        const fetchStates = async () => {
            try {
                setIsLoadingStates(true)
                const response = await api.get('/state/get-states')
                setStates(response.data || response)
            } catch (error) {
                console.error('Error fetching states:', error)
            } finally {
                setIsLoadingStates(false)
            }
        }

        fetchStates()
    }, [])

    // Fetch license types on component mount
    useEffect(() => {
        const fetchLicenseTypes = async () => {
            try {
                setIsLoadingLicenseTypes(true)
                const response = await membershipApi.getAgencySpecialtyRoles()
                const licenseTypesData = response.data || response
                if (Array.isArray(licenseTypesData)) {
                    setLicenseTypes(
                        licenseTypesData.filter((lt: LicenseType) => !lt.isDeleted)
                    )
                }
            } catch (error) {
                console.error('Error fetching specialty license types:', error)
            } finally {
                setIsLoadingLicenseTypes(false)
            }
        }

        fetchLicenseTypes()
    }, [])

    // Scroll to top when step changes
    useEffect(() => {
        // Skip scroll on initial mount
        if (currentStep === 0 && !showOnboarding) {
            return
        }

        // Delay to allow animations to start, then scroll
        const timer = setTimeout(() => {
            // Try multiple scroll methods for better compatibility
            const scrollToTop = () => {
                // Method 1: Scroll window to top
                window.scrollTo({ top: 0, behavior: 'smooth' })

                // Method 2: Also try scrolling the document element
                if (document.documentElement) {
                    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' })
                }

                // Method 3: Try scrolling body as well
                if (document.body) {
                    document.body.scrollTo({ top: 0, behavior: 'smooth' })
                }
            }

            scrollToTop()
        }, 300) // Increased delay to account for animations

        return () => clearTimeout(timer)
    }, [currentStep, showOnboarding])

    // Handle plan selection from subscription dialog
    // Plan chosen from the pricing dialog. No charge happens yet — this just
    // records the pick and hands off to the Subscription Agreement panel;
    // the actual subscription/payment intent is created in
    // handleActivateSubscription, once the agreement is signed and every
    // acknowledgement is initialed.
    const handlePlanSelect = (
        plan: MembershipPlan,
        cycle: 'monthly' | 'yearly',
        amountPaid: string,
        totalPrice: string,
        couponApplied: string,
        customNoOfUser?: number
    ) => {
        setPaymentError(null)
        setBillingCycle(cycle)
        setSelectedPlan(plan)
        setDiscountedPrice(parseFloat(amountPaid))
        setShowSubscriptionDialog(false)
        setPendingPlanSelection({ plan, cycle, amountPaid, totalPrice, couponApplied, customNoOfUser })
        setPlanSelected(true)

        if (therapistId) {
            membershipApi.agencySubscriptionAgreementSignup(therapistId, plan._id).catch(err => {
                console.error('Failed to record agency subscription agreement signup:', err)
            })
        }
    }

    // Runs once the Agency Admin has signed the Subscription Agreement and
    // completed every acknowledgement — creates the Stripe subscription /
    // payment intent for the plan picked earlier and opens the payment modal.
    const handleActivateSubscription = async () => {
        if (!pendingPlanSelection) return
        const { plan, cycle, amountPaid, totalPrice, couponApplied, customNoOfUser } = pendingPlanSelection

        try {
            setIsProcessingPayment(true)
            setPaymentError(null)

            // Open payment modal immediately with loading state
            setShowPaymentModal(true)

            // Disable draft persistence - will be removed from local storage when user clicks handlePayment
            draftPersistenceEnabledRef.current = false

            console.log('Step 1: Fetching price information for plan...')

            // Step 1: Fetch price IDs for the selected plan
            const priceResponse = await membershipApi.getMembershipSubscriptionPrice(
                plan._id
            )
            const prices = priceResponse.data

            // Find the appropriate price ID based on billing cycle
            const selectedPrice = prices.find(
                p => p.billing_frequency.toLowerCase() === cycle
            )

            if (!selectedPrice) {
                throw new Error(`No price found for ${cycle} billing`)
            }

            console.log('Price found:', selectedPrice)
            console.log('couponApplied:', couponApplied)
            console.log('amountPaid (discounted):', amountPaid)
            console.log('totalPrice (discounted):', totalPrice)

            // Step 2: Ensure therapist account already exists
            let currentTherapistId = therapistId

            if (!currentTherapistId) {
                throw new Error('Agency account not found. Please complete the previous steps.')
            }

            // Step 4: Prepare billing address
            const billingAddress = {
                address1: formData.billingAddressLine,
                address2: '',
                city: formData.billingCity,
                country: formData.country,
                state: formData.billingState,
                postal_code: formData.billingZipCode,
            }

            console.log('Step 3: Creating subscription with payment intent...')

            const originalProviderCount = parseInt(onboardingData.noOfProvider) || 1
            const newNoOfUser = customNoOfUser !== undefined ? customNoOfUser : originalProviderCount

            // Step 4: Create subscription
            const subscriptionResponse = await membershipApi.createAgencySubscription({
                agency_id: currentTherapistId,
                providerCount: originalProviderCount,
                noOfProvide: newNoOfUser,
                noOfUser: newNoOfUser,
                members_price: [{
                    price_id: selectedPrice.stripe_price_id,
                    quantity: newNoOfUser
                }],
                total_price: parseFloat(totalPrice) ?? parseFloat(selectedPrice.price),
                amount_paid: parseFloat(amountPaid) ?? parseFloat(selectedPrice.price),
                // total_price: parseFloat(selectedPrice.price),
                // amount_paid: parseFloat(selectedPrice.price),
                billing_address: billingAddress,
                // coupon: couponApplied,
                // Add coupon only if applied
                ...(couponApplied !== '' && {
                    coupon: couponApplied,
                }),

            })

            console.log('Subscription created:', subscriptionResponse)

            if (!subscriptionResponse.client_secret) {
                throw new Error('Failed to create subscription payment intent')
            }

            // Step 6: Store subscription details and show payment modal
            setSubscriptionDetails({
                orderId: subscriptionResponse.orderId,
                client_secret: subscriptionResponse.client_secret,
                customerId: subscriptionResponse.customerId,
                ephemeralKey: subscriptionResponse.ephemeralKey,
                noOfUser: newNoOfUser,
            })

            console.log('Ready for payment confirmation')

            // Payment modal is already open from the beginning, it will show loading until clientSecret is ready
        } catch (error) {
            console.error('Error activating subscription:', error)
            const errorMessage =
                error instanceof Error ? error.message : 'An error occurred'
            setPaymentError(errorMessage)
            // Keep payment modal open to show error
            // The modal will display the error state
        } finally {
            setIsProcessingPayment(false)
        }
    }

    // Handle payment success
    const handlePaymentSuccess = async (paymentIntentId: string) => {
        try {
            console.log('Payment successful! Intent ID:', paymentIntentId)

            // Mark subscription payment as successful
            setSubscriptionPaymentSuccess(true)

            // User purchased a plan — clear saved draft from localStorage
            try {
                draftPersistenceEnabledRef.current = false
                localStorage.removeItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
            } catch { }

            // Close payment modal and navigate to step 5 immediately
            setShowPaymentModal(false)
            setCurrentStep(4) // Step 5 is index 4 (0-based)
        } catch (error) {
            console.error('Error after payment:', error)
            setPaymentError('Error processing your payment. Please contact support.')
        }
    }















    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'white',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Header */}
            <Fade in timeout={800}>
                <AppBar
                    position="static"
                    elevation={0}
                    sx={{
                        bgcolor: 'white',
                        borderBottom: '1px solid',
                        borderColor: 'grey.200',
                        height: '80px',
                        flexShrink: 0,
                    }}
                >
                    <Toolbar
                        sx={{ justifyContent: 'space-between', py: 1, height: '100%' }}
                    >
                        <Slide direction="right" in timeout={1000}>
                            <Box
                                component="img"
                                src="/logo.svg"
                                alt="PillarThera Logo"
                                sx={{
                                    height: { xs: 32, sm: 60 },
                                    width: 'auto',
                                    cursor: 'pointer',
                                    transition: 'opacity 0.3s ease',
                                    '&:hover': {
                                        opacity: 0.8,
                                    },
                                }}
                            />
                        </Slide>

                        {/* <Slide
              direction="left"
              in
              timeout={1000}
              style={{ transitionDelay: '200ms' }}
            >
              <Button
                variant="outlined"
                startIcon={<Person />}
                onClick={handleLoginClick}
                sx={{
                  color: 'grey.600',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  px: { sm: 2, md: 3 },
                  py: 1,
                  fontSize: { sm: '0.75rem', md: '0.875rem' },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'grey.400',
                    bgcolor: 'grey.50',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                }}
              >
                Sign Up / Login
              </Button>
            </Slide> */}
                    </Toolbar>
                </AppBar>
            </Fade>

            {/* Main Content */}
            <Box
                ref={formContainerRef}
                sx={{
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                    justifyContent: 'center',
                    alignItems: 'center',
                    px: { xs: 2, sm: 3, md: 4 }, // Reduced horizontal padding
                    py: { xs: 3, sm: 4 }, // Reduced vertical padding
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        maxWidth: '1600px', // Increased max width
                        gap: { xs: 0, md: 3, lg: 4 }, // Reduced gap
                        alignItems: 'flex-start',
                    }}
                >
                    {/* Left Section - Image */}
                    <Zoom in timeout={1200} style={{ transitionDelay: '300ms' }}>
                        <Box
                            sx={{
                                flex: { xs: 0, md: 1 },
                                position: 'sticky',
                                top: { md: 24, lg: 32 }, // Sticky positioning with top offset
                                display: { xs: 'none', md: 'block' },
                                overflow: 'hidden',
                                bgcolor: 'white',
                                borderRadius: 3,
                                minHeight: { md: '600px', lg: '700px' },
                                maxHeight: {
                                    md: 'calc(100vh - 48px)',
                                    lg: 'calc(100vh - 64px)',
                                }, // Constrain to viewport height
                                alignSelf: 'flex-start', // Align to top
                            }}
                        >
                            <Box
                                component="img"
                                src="/agencyImage/createAccount.jpg"
                                alt="Agency create account illustration"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    transition: 'transform 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                    },
                                }}
                            />
                        </Box>
                    </Zoom>

                    {/* Right Section - Create Account Form or Onboarding */}
                    <Slide
                        direction="left"
                        in
                        timeout={1000}
                        style={{ transitionDelay: '500ms' }}
                    >
                        <Box
                            sx={{
                                flex: { xs: 1, md: 1.5 }, // Increased flex for more width
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            {/* Form Card */}
                            <Grow in timeout={1200} style={{ transitionDelay: '700ms' }}>
                                <Paper
                                    ref={formPaperRef}
                                    elevation={2}
                                    sx={{
                                        p: { xs: 3, sm: 4, md: 5 }, // Reduced padding for more space
                                        borderRadius: 4,
                                        width: '100%',
                                        maxWidth: {
                                            xs: '100%',
                                            sm: '700px', // Increased max width
                                            md: '800px',
                                            lg: '900px',
                                            xl: '1000px',
                                        },
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                                        '&:hover': {
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    {!showOnboarding ? (
                                        <>
                                            {/* Header */}
                                            <Fade
                                                in
                                                timeout={1000}
                                                style={{ transitionDelay: '900ms' }}
                                            >
                                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                                    <Typography
                                                        variant="h4"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#574F75',
                                                            mb: 1,
                                                            fontSize: {
                                                                xs: '1.75rem',
                                                                sm: '2rem',
                                                                md: '2.25rem',
                                                            },
                                                        }}
                                                    >
                                                        Create Your Agency Account
                                                    </Typography>
                                                </Box>
                                            </Fade>

                                            <Fade
                                                in
                                                timeout={1000}
                                                style={{ transitionDelay: '1000ms' }}
                                            >
                                                <Box
                                                    sx={{
                                                        background: 'linear-gradient(135deg, rgba(87, 79, 117, 0.05) 0%, rgba(115, 103, 240, 0.02) 100%)',
                                                        border: '1px solid rgba(87, 79, 117, 0.12)',
                                                        borderRadius: '16px',
                                                        p: 3,
                                                        mb: 4,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
                                                        <InfoOutlined sx={{ color: '#574F75', mt: 0.25 }} />
                                                        <Box>
                                                            <Typography
                                                                variant="subtitle1"
                                                                sx={{ fontWeight: 600, color: '#574F75', mb: 0.5 }}
                                                            >
                                                                Before You Begin
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: '#6E6B7B', lineHeight: 1.6 }}>
                                                                To complete your registration efficiently, please have your agency information and the required documents ready. Preparing these files in advance will help you complete your application in one session.
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Divider sx={{ my: 2, borderColor: 'rgba(87, 79, 117, 0.1)' }} />

                                                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                        <AssignmentOutlined sx={{ color: '#574F75', mt: 0.25 }} />
                                                        <Box sx={{ width: '100%' }}>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{ fontWeight: 600, color: '#574F75', mb: 1.5 }}
                                                            >
                                                                Required Document Checklist:
                                                            </Typography>
                                                            <Grid container spacing={1.5}>
                                                                {[
                                                                    'Secretary of State Statement of Information',
                                                                    'Doing Business As (DBA) Certificate (if applicable)',
                                                                    'EIN Certificate',
                                                                    'NPI Level II Certificate (Organization)',
                                                                    'Signed W9 Form',
                                                                    'Agency Specialty License(s)',
                                                                    'Professional Liability (Errors & Omissions, E&O) Insurance Certificate',
                                                                    'General Liability Insurance Certificate',
                                                                    'Workers\' Compensation Insurance Certificate',

                                                                ].map((doc, idx) => (
                                                                    <Grid item xs={12} sm={6} key={idx}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Box
                                                                                sx={{
                                                                                    width: 6,
                                                                                    height: 6,
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: '#7367F0',
                                                                                }}
                                                                            />
                                                                            <Typography variant="body2" sx={{ color: '#5E5873', fontWeight: 500 }}>
                                                                                {doc}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Fade>

                                            {/* Create Account Form */}
                                            <Box
                                                component="form"
                                                onSubmit={handleSubmit}
                                                autoComplete="off"
                                            >
                                                {/* Legal Business Name */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1100ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography variant="body2" sx={accountFieldLabelSx}>
                                                            Legal Business Name (Upload Statement of Information) *
                                                        </Typography>
                                                        <TextField
                                                            type="text"
                                                            placeholder="Enter your agency's legal business name"
                                                            name="agencyName"
                                                            value={formData.agencyName}
                                                            onChange={handleInputChange('agencyName')}
                                                            required
                                                            fullWidth
                                                            variant="outlined"
                                                            error={!!agencyNameError || !!agencyNameDocError}
                                                            helperText={agencyNameError || agencyNameDocError}
                                                            sx={accountFieldSx}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                        {uploadingDocumentField === 'agencyNameDoc' ? (
                                                                            <CircularProgress size={20} />
                                                                        ) : formData.agencyNameDocUrl ? (
                                                                            <Chip
                                                                                label={formData.agencyNameDocName || 'Statement of Info'}
                                                                                size="small"
                                                                                onClick={() => { window.open(formData.agencyNameDocUrl, "_blank") }}
                                                                                onDelete={() => {
                                                                                    setFormData(prev => ({
                                                                                        ...prev,
                                                                                        agencyNameDocUrl: '',
                                                                                        agencyNameDocName: '',
                                                                                    }))
                                                                                }}
                                                                                sx={{
                                                                                    cursor: 'pointer',
                                                                                    '&:hover': {
                                                                                        bgcolor: '#D5D1E1',
                                                                                    },
                                                                                    maxWidth: 150,
                                                                                    textOverflow: 'ellipsis',
                                                                                    overflow: 'hidden',
                                                                                    whiteSpace: 'nowrap',
                                                                                    bgcolor: '#EAE7F2',
                                                                                    color: '#574F75',
                                                                                    fontWeight: 500,
                                                                                    border: '1px solid #D1C9E7',
                                                                                    '& .MuiChip-deleteIcon': {
                                                                                        color: '#7A6FA1',
                                                                                        '&:hover': {
                                                                                            color: '#574F75'
                                                                                        }
                                                                                    }
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <>
                                                                                <input
                                                                                    type="file"
                                                                                    id="agencyNameDoc-file-input"
                                                                                    style={{ display: 'none' }}
                                                                                    accept=".pdf,image/*"
                                                                                    onChange={(e) => {
                                                                                        const file = e.target.files?.[0];
                                                                                        if (file) {
                                                                                            handleDocumentUpload('agencyNameDoc')(file);
                                                                                        }
                                                                                        e.target.value = '';
                                                                                    }}
                                                                                />
                                                                                <Tooltip
                                                                                    title="Click the upload icon on the right to upload the Legal Business document"
                                                                                    placement="top"
                                                                                >
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            e.preventDefault();
                                                                                            document.getElementById('agencyNameDoc-file-input')?.click();
                                                                                        }}

                                                                                        sx={uploadBtnSx}
                                                                                    >
                                                                                        <CloudUpload />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </>
                                                                        )}
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                    </Box>
                                                </Fade>

                                                {/* DBA name and EIN */}

                                                <Fade in timeout={800} style={{ transitionDelay: '1150ms' }}>
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>

                                                        {/* DBA Name Column */}
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                DBA Name (Upload DBA Certificate)
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter DBA (Doing Business As) name"
                                                                value={formData.dbaName}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    const value = e.target.value
                                                                    setFormData(prev => ({ ...prev, dbaName: value }))
                                                                    if (dbaNameError) setDbaNameError('')
                                                                }}
                                                                error={!!dbaNameError || !!dbaNameDocError}
                                                                helperText={dbaNameError || dbaNameDocError}
                                                                sx={accountFieldSx}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                            {uploadingDocumentField === 'dbaNameDoc' ? (
                                                                                <CircularProgress size={20} />
                                                                            ) : formData.dbaNameDocUrl ? (
                                                                                <Chip
                                                                                    label={formData.dbaNameDocName || 'Fictitious Business Stmt'}
                                                                                    size="small"
                                                                                    onClick={() => { window.open(formData.dbaNameDocUrl, "_blank") }}
                                                                                    onDelete={() => {
                                                                                        setFormData(prev => ({
                                                                                            ...prev,
                                                                                            dbaNameDocUrl: '',
                                                                                            dbaNameDocName: '',
                                                                                        }))
                                                                                    }}
                                                                                    sx={{
                                                                                        cursor: 'pointer',
                                                                                        '&:hover': {
                                                                                            bgcolor: '#D5D1E1',
                                                                                        },
                                                                                        maxWidth: 150,
                                                                                        textOverflow: 'ellipsis',
                                                                                        overflow: 'hidden',
                                                                                        whiteSpace: 'nowrap',
                                                                                        bgcolor: '#EAE7F2',
                                                                                        color: '#574F75',
                                                                                        fontWeight: 500,
                                                                                        border: '1px solid #D1C9E7',
                                                                                        '& .MuiChip-deleteIcon': {
                                                                                            color: '#7A6FA1',
                                                                                            '&:hover': {
                                                                                                color: '#574F75'
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <>
                                                                                    <input
                                                                                        type="file"
                                                                                        id="dbaNameDoc-file-input"
                                                                                        style={{ display: 'none' }}
                                                                                        accept=".pdf,image/*"
                                                                                        onChange={(e) => {
                                                                                            const file = e.target.files?.[0];
                                                                                            if (file) {
                                                                                                handleDocumentUpload('dbaNameDoc')(file);
                                                                                            }
                                                                                            e.target.value = '';
                                                                                        }}
                                                                                    />
                                                                                    <Tooltip title="Click the upload icon on the right to upload the DBA document" placement="top">
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                e.preventDefault();
                                                                                                document.getElementById('dbaNameDoc-file-input')?.click();
                                                                                            }}
                                                                                            sx={uploadBtnSx}
                                                                                        >
                                                                                            <CloudUpload />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </>
                                                                            )}
                                                                        </InputAdornment>
                                                                    )
                                                                }}
                                                            />
                                                        </Grid>

                                                        {/* EIN Column */}
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                EIN (Upload EIN Certificate) *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter EIN - Format: XX-XXXXXXX"
                                                                value={formatEIN(formData.ein || '')}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    const raw = e.target.value.replace(/\D/g, '')
                                                                    setFormData(prev => ({ ...prev, ein: raw }))
                                                                    if (einError) setEinError('')
                                                                    if (einDocError) setEinDocError('')
                                                                }}
                                                                onBlur={() => {
                                                                    if (formData.ein.trim() && !isValidEIN(formData.ein)) {
                                                                        setEinError(ValidationRules.ein.message)
                                                                    } else {
                                                                        setEinError('')
                                                                    }
                                                                }}
                                                                inputProps={{
                                                                    inputMode: 'numeric',
                                                                    maxLength: 10,
                                                                }}
                                                                required
                                                                error={!!einError || !!einDocError}
                                                                helperText={einError || einDocError}
                                                                sx={accountFieldSx}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                            {uploadingDocumentField === 'einDoc' ? (
                                                                                <CircularProgress size={20} />
                                                                            ) : formData.einDocUrl ? (
                                                                                <Chip
                                                                                    label={formData.einDocName || 'EIN Document'}
                                                                                    size="small"
                                                                                    onClick={() => { window.open(formData.einDocUrl, "_blank") }}
                                                                                    onDelete={() => {
                                                                                        setFormData(prev => ({
                                                                                            ...prev,
                                                                                            einDocUrl: '',
                                                                                            einDocName: '',
                                                                                        }))
                                                                                    }}
                                                                                    sx={{
                                                                                        cursor: 'pointer',
                                                                                        '&:hover': {
                                                                                            bgcolor: '#D5D1E1',
                                                                                        },
                                                                                        maxWidth: 120,
                                                                                        textOverflow: 'ellipsis',
                                                                                        overflow: 'hidden',
                                                                                        whiteSpace: 'nowrap',
                                                                                        bgcolor: '#EAE7F2',
                                                                                        color: '#574F75',
                                                                                        fontWeight: 500,
                                                                                        border: '1px solid #D1C9E7',
                                                                                        '& .MuiChip-deleteIcon': {
                                                                                            color: '#7A6FA1',
                                                                                            '&:hover': {
                                                                                                color: '#574F75'
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <>
                                                                                    <input
                                                                                        type="file"
                                                                                        id="einDoc-file-input"
                                                                                        style={{ display: 'none' }}
                                                                                        accept=".pdf,image/*"
                                                                                        onChange={(e) => {
                                                                                            const file = e.target.files?.[0];
                                                                                            if (file) {
                                                                                                handleDocumentUpload('einDoc')(file);
                                                                                            }
                                                                                            e.target.value = '';
                                                                                        }}
                                                                                    />
                                                                                    <Tooltip title="Click the upload icon on the right to upload the EIN document" placement="top">
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                e.preventDefault();
                                                                                                document.getElementById('einDoc-file-input')?.click();
                                                                                            }}
                                                                                            sx={uploadBtnSx}
                                                                                        >
                                                                                            <CloudUpload />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </>
                                                                            )}
                                                                        </InputAdornment>
                                                                    )
                                                                }}
                                                            />
                                                        </Grid>

                                                    </Grid>
                                                </Fade>




                                                {/* License Number & License Expiration Row */}
                                                {/* <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1250ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                License Number
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter License Number"
                                                                value={formData.agencyLicenseNumber}
                                                                onChange={handleInputChange('agencyLicenseNumber')}
                                                                sx={accountFieldSx}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                License Expiration
                                                            </Typography>
                                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                <DatePicker
                                                                    minDate={new Date()}
                                                                    value={formData.agencyLicenseExpiration}
                                                                    onChange={date => {
                                                                        const dateObj =
                                                                            date instanceof Date
                                                                                ? date
                                                                                : (date as any)?.toDate
                                                                                    ? (date as any).toDate()
                                                                                    : date
                                                                                        ? new Date(date as any)
                                                                                        : null
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            agencyLicenseExpiration: dateObj,
                                                                        }))
                                                                    }}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            placeholder: 'mm/dd/yyyy',
                                                                            sx: accountDatePickerSx,
                                                                        },
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        </Grid>
                                                    </Grid>
                                                </Fade> */}

                                                {/* State of Operations */}


                                                {/* Business Email */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1350ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography variant="body2" sx={accountFieldLabelSx}>
                                                            Business Email *
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            type="email"
                                                            placeholder="agency@example.com"
                                                            value={formData.businessEmail}
                                                            inputProps={{ autoComplete: 'off' }}
                                                            onChange={e => {
                                                                const value = e.target.value
                                                                setFormData(prev => ({ ...prev, businessEmail: value }))
                                                                if (value && value.trim()) {
                                                                    setBusinessEmailError(
                                                                        isValidEmail(value) ? '' : ValidationRules.email.message
                                                                    )
                                                                } else {
                                                                    setBusinessEmailError('')
                                                                }
                                                            }}
                                                            onBlur={e => {
                                                                const value = e.target.value
                                                                if (value && value.trim() && !isValidEmail(value)) {
                                                                    setBusinessEmailError(ValidationRules.email.message)
                                                                }
                                                            }}
                                                            required
                                                            error={!!businessEmailError}
                                                            helperText={businessEmailError}
                                                            sx={accountFieldSx}
                                                        />
                                                    </Box>
                                                </Fade>

                                                {/* Business Phone Number & Website Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1400ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                Business Phone Number
                                                            </Typography>
                                                            <PhoneInput
                                                                fullWidth
                                                                value={formData.businessPhoneNumber}
                                                                onChange={value => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        businessPhoneNumber: value,
                                                                    }))
                                                                    if (isBusinessPhoneTouched) {
                                                                        setBusinessPhoneError(
                                                                            value.trim() ? getUSPhoneErrorMessage(value) : ''
                                                                        )
                                                                    }
                                                                }}
                                                                onBlur={() => {
                                                                    setIsBusinessPhoneTouched(true)
                                                                    setBusinessPhoneError(
                                                                        formData.businessPhoneNumber.trim()
                                                                            ? getUSPhoneErrorMessage(formData.businessPhoneNumber)
                                                                            : ''
                                                                    )
                                                                }}
                                                                onValidationChange={isValid => {
                                                                    if (!isBusinessPhoneTouched) return
                                                                    if (!formData.businessPhoneNumber.trim()) {
                                                                        setBusinessPhoneError('')
                                                                        return
                                                                    }
                                                                    setBusinessPhoneError(
                                                                        isValid ? '' : getUSPhoneErrorMessage(formData.businessPhoneNumber)
                                                                    )
                                                                }}
                                                                error={!!businessPhoneError}
                                                                helperText={businessPhoneError}
                                                                sx={accountFieldSx}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                Website
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="https://www.youragency.com"
                                                                value={formData.website}
                                                                onChange={handleInputChange('website')}
                                                                sx={accountFieldSx}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Business Address Section Heading */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1450ms' }}
                                                >
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{ color: 'grey.800', fontWeight: 600, fontSize: '1.05rem' }}
                                                        >
                                                            Business Address
                                                        </Typography>
                                                    </Box>
                                                </Fade>

                                                {/* Address Field */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1500ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'grey.700',
                                                                mb: 1,
                                                                fontWeight: 500,
                                                                fontSize: '0.875rem',
                                                            }}
                                                        >
                                                            Street Address 1*
                                                        </Typography>
                                                        <AddressAutocomplete
                                                            value={formData.address}
                                                            onChange={(address, components) => {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    address,
                                                                    // Only update city, state, zip if components are provided and have values
                                                                    // Don't clear them when address is cleared (components will have empty strings)
                                                                    ...(components && {
                                                                        ...(components.city && {
                                                                            city: components.city,
                                                                        }),
                                                                        ...(components.country && {
                                                                            country: components.country,
                                                                        }),
                                                                        ...(components.state && {
                                                                            state: components.state,
                                                                        }),
                                                                        ...((components.zipCode ||
                                                                            components.postalCode) && {
                                                                            zipCode:
                                                                                components.zipCode ||
                                                                                components.postalCode,
                                                                        }),
                                                                        // Capture latitude and longitude
                                                                        ...(components.latitude !== undefined && {
                                                                            latitude: components.latitude,
                                                                        }),
                                                                        ...(components.longitude !== undefined && {
                                                                            longitude: components.longitude,
                                                                        }),
                                                                    }),
                                                                }))
                                                            }}
                                                            placeholder="Start typing an Street Address 1..."
                                                            addressType="address"
                                                            borderColor='#574F75'
                                                        />

                                                    </Box>

                                                </Fade>
                                                <Box sx={{ mb: 3 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'grey.700',
                                                            mb: 1,
                                                            fontWeight: 500,
                                                            fontSize: '0.875rem',
                                                        }}
                                                    >
                                                        Street Address 2
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        placeholder="Enter Street Address 2"
                                                        value={formData.addressLine2 || ''}
                                                        onChange={handleInputChange('addressLine2')}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                bgcolor: 'white',
                                                                borderRadius: 2,
                                                                transition: 'all 0.3s ease',
                                                                '& fieldset': { borderColor: 'grey.300' },
                                                                '&:hover fieldset': { borderColor: 'grey.400' },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: '#574F75',
                                                                    borderWidth: 2,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                py: 1.5,
                                                                fontSize: '0.875rem',
                                                            },
                                                        }}
                                                    />

                                                </Box>

                                                {/* Country → State cascading dropdowns */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1600ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <LocationSelector
                                                            country={formData.country}
                                                            state={formData.state}
                                                            onCountryChange={name =>
                                                                setFormData(prev => ({ ...prev, country: name, state: '' }))
                                                            }
                                                            onStateChange={name =>
                                                                setFormData(prev => ({ ...prev, state: name }))
                                                            }
                                                            accentColor="#574F75"
                                                            required
                                                        />
                                                    </Box>
                                                </Fade>

                                                {/* City & Zip Code Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1650ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                City *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter City"
                                                                value={formData.city}
                                                                onChange={handleInputChange('city')}
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': { borderColor: 'grey.300' },
                                                                        '&:hover fieldset': { borderColor: 'grey.400' },
                                                                        '&.Mui-focused fieldset': { borderColor: '#574F75', borderWidth: 2 },
                                                                    },
                                                                    '& .MuiInputBase-input': { py: 1.5, fontSize: '0.875rem' },
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Zip Code *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter Zip Code"
                                                                value={formData.zipCode}
                                                                onChange={handleInputChange('zipCode')}
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': { borderColor: 'grey.300' },
                                                                        '&:hover fieldset': { borderColor: 'grey.400' },
                                                                        '&.Mui-focused fieldset': { borderColor: '#574F75', borderWidth: 2 },
                                                                    },
                                                                    '& .MuiInputBase-input': { py: 1.5, fontSize: '0.875rem' },
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Primary Administrator Section Heading */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1700ms' }}
                                                >
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{ color: 'grey.800', fontWeight: 600, fontSize: '1.05rem' }}
                                                        >
                                                            Primary Administrator
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: 'grey.500', mt: 0.5 }}>
                                                            The person who will be the primary contact and administrator for this account.
                                                        </Typography>
                                                    </Box>
                                                </Fade>

                                                {/* First Name & Last Name Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1750ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                First Name *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                value={formData.firstName}
                                                                onChange={handleInputChange('firstName')}
                                                                placeholder="Enter First Name"
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Last Name *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter Last Name"
                                                                value={formData.lastName}
                                                                onChange={handleInputChange('lastName')}
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Email Address & Phone Number Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1800ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Email Address *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                type="email"
                                                                placeholder="Enter Email"
                                                                value={formData.email}
                                                                inputProps={{ autoComplete: 'off' }}
                                                                onChange={e => {
                                                                    const emailValue = e.target.value
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        email: emailValue,
                                                                    }))
                                                                    // Validate email format in real-time using validation utility
                                                                    if (emailValue && emailValue.trim()) {
                                                                        if (!isValidEmail(emailValue)) {
                                                                            setEmailVerificationError(
                                                                                ValidationRules.email.message
                                                                            )
                                                                        } else {
                                                                            // Clear error if format is valid
                                                                            setEmailVerificationError('')
                                                                        }
                                                                    } else {
                                                                        // Clear error when field is empty
                                                                        setEmailVerificationError('')
                                                                    }
                                                                }}
                                                                onBlur={e => {
                                                                    // Validate on blur as well
                                                                    const emailValue = e.target.value
                                                                    if (emailValue && emailValue.trim()) {
                                                                        if (!isValidEmail(emailValue)) {
                                                                            setEmailVerificationError(
                                                                                ValidationRules.email.message
                                                                            )
                                                                        }
                                                                    }
                                                                }}
                                                                required
                                                                error={!!emailVerificationError}
                                                                helperText={emailVerificationError}
                                                                disabled={isVerifyingEmail}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: emailVerificationError
                                                                                ? 'error.main'
                                                                                : 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: emailVerificationError
                                                                                ? 'error.main'
                                                                                : 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: emailVerificationError
                                                                                ? 'error.main'
                                                                                : '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Phone Number *
                                                            </Typography>
                                                            <PhoneInput
                                                                fullWidth
                                                                value={formData.phoneNumber}
                                                                onChange={value => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        phoneNumber: value,
                                                                    }))
                                                                    if (isPhoneNumberTouched) {
                                                                        setPhoneNumberError(
                                                                            getUSPhoneErrorMessage(value)
                                                                        )
                                                                    }
                                                                }}
                                                                onBlur={() => {
                                                                    setIsPhoneNumberTouched(true)
                                                                    setPhoneNumberError(
                                                                        getUSPhoneErrorMessage(formData.phoneNumber)
                                                                    )
                                                                }}
                                                                onValidationChange={isValid => {
                                                                    if (!isPhoneNumberTouched) {
                                                                        return
                                                                    }
                                                                    setPhoneNumberError(
                                                                        isValid
                                                                            ? ''
                                                                            : getUSPhoneErrorMessage(
                                                                                formData.phoneNumber
                                                                            )
                                                                    )
                                                                }}
                                                                required
                                                                error={!!phoneNumberError}
                                                                helperText={phoneNumberError}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: phoneNumberError
                                                                                ? 'error.main'
                                                                                : 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: phoneNumberError
                                                                                ? 'error.main'
                                                                                : 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: phoneNumberError
                                                                                ? 'error.main'
                                                                                : '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Password & Confirm Password Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1850ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Password *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                type={showPassword ? 'text' : 'password'}
                                                                placeholder="Enter Password"
                                                                value={formData.password}
                                                                onChange={handleInputChange('password')}
                                                                inputProps={{ autoComplete: 'new-password' }}
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor:
                                                                                formData.password.length > 0
                                                                                    ? passwordStrength.borderColor
                                                                                    : 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor:
                                                                                formData.password.length > 0
                                                                                    ? "#574F75"
                                                                                    : 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: "#574F75",
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    setShowPassword(!showPassword)
                                                                                }
                                                                                edge="end"
                                                                                sx={{
                                                                                    color: 'grey.400',
                                                                                    transition: 'transform 0.2s ease',
                                                                                    '&:hover': {
                                                                                        transform: 'scale(1.1)',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                {showPassword ? (
                                                                                    <VisibilityOff />
                                                                                ) : (
                                                                                    <Visibility />
                                                                                )}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                            {formData.password.length > 0 && (
                                                                <PasswordStrengthMeter
                                                                    strength={passwordStrength}
                                                                />
                                                            )}
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Confirm Password *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                type={showConfirmPassword ? 'text' : 'password'}
                                                                placeholder="Enter Confirm Password"
                                                                value={formData.confirmPassword}
                                                                onChange={handleInputChange('confirmPassword')}
                                                                inputProps={{ autoComplete: 'new-password' }}
                                                                required
                                                                error={!!confirmPasswordError}
                                                                helperText={confirmPasswordError}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: confirmPasswordError
                                                                                ? '#F44336'
                                                                                : 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: confirmPasswordError
                                                                                ? '#F44336'
                                                                                : 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: confirmPasswordError
                                                                                ? '#F44336'
                                                                                : '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                        '&.Mui-error fieldset': {
                                                                            borderColor: '#F44336',
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    setShowConfirmPassword(
                                                                                        !showConfirmPassword
                                                                                    )
                                                                                }
                                                                                edge="end"
                                                                                sx={{
                                                                                    color: 'grey.400',
                                                                                    transition: 'transform 0.2s ease',
                                                                                    '&:hover': {
                                                                                        transform: 'scale(1.1)',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                {showConfirmPassword ? (
                                                                                    <VisibilityOff />
                                                                                ) : (
                                                                                    <Visibility />
                                                                                )}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Secondary Administrators (Optional) */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1900ms' }}
                                                >
                                                    <Box sx={{ mb: 4 }}>
                                                        {/* <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{ color: 'grey.800', fontWeight: 600, fontSize: '1.05rem' }}
                                                                >
                                                                    Secondary Administrators
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: 'grey.500' }}>
                                                                    (Optional)
                                                                </Typography>
                                                            </Box>
                                                            <Button
                                                                startIcon={<Add />}
                                                                onClick={handleOpenAddAdminDialog}
                                                                sx={{
                                                                    color: '#574F75',
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                Add Administrator
                                                            </Button>
                                                        </Box> */}
                                                        {secondaryAdministrators.length > 0 && (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                {secondaryAdministrators.map(admin => (
                                                                    <Box
                                                                        key={admin.id}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                            p: 1.5,
                                                                            borderRadius: 2,
                                                                            bgcolor: '#f8f9fa',
                                                                            border: '1px solid #e9ecef',
                                                                        }}
                                                                    >
                                                                        <Box>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ fontWeight: 600, color: 'grey.800' }}
                                                                            >
                                                                                {admin.firstName} {admin.lastName}
                                                                                {' - '}
                                                                                <Typography
                                                                                    component="span"
                                                                                    variant="body2"
                                                                                    sx={{ color: 'grey.500', fontWeight: 400 }}
                                                                                >
                                                                                    {admin.role}
                                                                                </Typography>
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ color: 'grey.600', fontSize: '0.8rem' }}
                                                                            >
                                                                                {admin.email}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Button
                                                                            size="small"
                                                                            onClick={() =>
                                                                                handleRemoveSecondaryAdministrator(admin.id)
                                                                            }
                                                                            sx={{
                                                                                color: '#d32f2f',
                                                                                textTransform: 'none',
                                                                                fontWeight: 600,
                                                                                fontSize: '0.75rem',
                                                                            }}
                                                                        >
                                                                            Remove
                                                                        </Button>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Fade>

                                                {/* Add Secondary Administrator Dialog */}
                                                <Dialog
                                                    open={showAddAdminDialog}
                                                    onClose={handleCloseAddAdminDialog}
                                                    maxWidth="sm"
                                                    fullWidth
                                                >
                                                    <DialogTitle>Add Secondary Administrator</DialogTitle>
                                                    <DialogContent>
                                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                                            <Grid item xs={6}>
                                                                <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                    First Name *
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    value={newAdminDraft.firstName}
                                                                    onChange={e =>
                                                                        setNewAdminDraft(prev => ({
                                                                            ...prev,
                                                                            firstName: e.target.value,
                                                                        }))
                                                                    }
                                                                    error={!!newAdminErrors.firstName}
                                                                    helperText={newAdminErrors.firstName}
                                                                    sx={accountFieldSx}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                    Last Name *
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    value={newAdminDraft.lastName}
                                                                    onChange={e =>
                                                                        setNewAdminDraft(prev => ({
                                                                            ...prev,
                                                                            lastName: e.target.value,
                                                                        }))
                                                                    }
                                                                    error={!!newAdminErrors.lastName}
                                                                    helperText={newAdminErrors.lastName}
                                                                    sx={accountFieldSx}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                    Role / Title *
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    placeholder="e.g. Director of Operations"
                                                                    value={newAdminDraft.role}
                                                                    onChange={e =>
                                                                        setNewAdminDraft(prev => ({
                                                                            ...prev,
                                                                            role: e.target.value,
                                                                        }))
                                                                    }
                                                                    error={!!newAdminErrors.role}
                                                                    helperText={newAdminErrors.role}
                                                                    sx={accountFieldSx}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                    Email Address *
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    type="email"
                                                                    value={newAdminDraft.email}
                                                                    onChange={e =>
                                                                        setNewAdminDraft(prev => ({
                                                                            ...prev,
                                                                            email: e.target.value,
                                                                        }))
                                                                    }
                                                                    error={!!newAdminErrors.email}
                                                                    helperText={newAdminErrors.email}
                                                                    sx={accountFieldSx}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </DialogContent>
                                                    <DialogActions>
                                                        <Button
                                                            onClick={handleCloseAddAdminDialog}
                                                            sx={{ color: 'grey.600', textTransform: 'none' }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleAddSecondaryAdministrator}
                                                            sx={{
                                                                bgcolor: '#574F75',
                                                                textTransform: 'none',
                                                                '&:hover': { bgcolor: '#473f63' },
                                                            }}
                                                        >
                                                            Add
                                                        </Button>
                                                    </DialogActions>
                                                </Dialog>

                                                {/* Create Account Button */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '2300ms' }}
                                                >
                                                    <Button
                                                        type="submit"
                                                        fullWidth
                                                        variant="contained"
                                                        disabled={isVerifyingEmail}
                                                        sx={{
                                                            bgcolor: '#574F75',
                                                            py: 1.5,
                                                            fontSize: '1rem',
                                                            fontWeight: 600,
                                                            borderRadius: 2,
                                                            mb: 3,
                                                            textTransform: 'none',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                bgcolor: '#574F75',
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 4px 12px rgba(46, 125, 175, 0.3)',
                                                            },
                                                            '&:disabled': {
                                                                bgcolor: '#9e9e9e',
                                                                cursor: 'not-allowed',
                                                            },
                                                        }}
                                                    >
                                                        {isVerifyingEmail ? (
                                                            <>
                                                                <CircularProgress
                                                                    size={20}
                                                                    sx={{ mr: 1, color: 'white' }}
                                                                />
                                                                Verifying Email...
                                                            </>
                                                        ) : (
                                                            'Create Agency Account'
                                                        )}
                                                    </Button>
                                                </Fade>
                                            </Box>


                                            <Fade
                                                in
                                                timeout={800}
                                                style={{ transitionDelay: '2500ms' }}
                                            >
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'grey.600',
                                                            fontSize: '0.875rem',

                                                        }}
                                                    >
                                                        By creating an account, you agree to our
                                                        <Link
                                                            component="button"
                                                            onClick={() => { navigate('/privacy') }}
                                                            sx={{
                                                                color: '#574F75',
                                                                textDecoration: 'none',
                                                                fontWeight: 600,
                                                                margin: '0px 5px',
                                                                transition: 'all 0.3s ease',
                                                                '&:hover': {
                                                                    textDecoration: 'underline',
                                                                    transform: 'translateY(-1px)',
                                                                },
                                                            }}
                                                        >
                                                            Terms of Service
                                                        </Link>
                                                        and
                                                        <Link
                                                            component="button"
                                                            onClick={() => { navigate('/privacy') }}
                                                            sx={{
                                                                color: '#574F75',
                                                                textDecoration: 'none',
                                                                fontWeight: 600,
                                                                margin: '0px 5px',
                                                                transition: 'all 0.3s ease',
                                                                '&:hover': {
                                                                    textDecoration: 'underline',
                                                                    transform: 'translateY(-1px)',
                                                                },
                                                            }}
                                                        >
                                                            Privacy Policy
                                                        </Link>
                                                    </Typography>
                                                </Box>
                                            </Fade>









                                            {/* Login Link */}
                                            <Fade
                                                in
                                                timeout={800}
                                                style={{ transitionDelay: '2500ms' }}
                                            >
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'grey.600',
                                                            fontSize: '0.875rem',
                                                        }}
                                                    >
                                                        Already have an account?{' '}
                                                        <Link
                                                            component="button"
                                                            onClick={handleLoginLinkClick}
                                                            sx={{
                                                                color: '#574F75',
                                                                textDecoration: 'none',
                                                                fontWeight: 600,
                                                                transition: 'all 0.3s ease',
                                                                '&:hover': {
                                                                    textDecoration: 'underline',
                                                                    transform: 'translateY(-1px)',
                                                                },
                                                            }}
                                                        >
                                                            Login
                                                        </Link>
                                                    </Typography>
                                                </Box>
                                            </Fade>
                                        </>
                                    ) : (
                                        // Onboarding Form
                                        <>
                                            {/* Onboarding Header */}
                                            <Fade in timeout={800}>
                                                <Box sx={{ mb: 4 }}>
                                                    <Typography
                                                        variant="h3"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#574F75',
                                                            mb: 1,
                                                            fontSize: {
                                                                xs: '1.75rem',
                                                                sm: '2.25rem',
                                                                md: '2.5rem',
                                                            },
                                                        }}
                                                    >
                                                        Hello, {formData.firstName || ''}!
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            color: '#574F75',
                                                            fontWeight: 400,
                                                            fontSize: { xs: '1rem', sm: '1.125rem' },
                                                        }}
                                                    >
                                                        We're excited to build the <span style={{ color: '#574F75', fontWeight: 600 }}>{capitalizeWords(formData.agencyName || '')}</span> Agency profile with you.
                                                    </Typography>
                                                </Box>
                                            </Fade>

                                            {/* Progress Stepper */}
                                            <Fade
                                                in
                                                timeout={800}
                                                style={{ transitionDelay: '200ms' }}
                                            >
                                                <Box>
                                                    <ProgressStepper
                                                        steps={steps}
                                                        currentStep={currentStep}
                                                        onStepClick={stepIndex => moveToStep(stepIndex)}
                                                    />
                                                </Box>
                                            </Fade>

                                            {/* Step 2: About you */}
                                            {currentStep === 0 && (
                                                <AboutYouStep
                                                    profilePicture={onboardingData.profilePicture}
                                                    professionalBio={onboardingData.professionalBio}
                                                    isUploadingProfilePhoto={isUploadingProfilePhoto}
                                                    onProfilePictureChange={handleProfilePictureChange}
                                                    onRemoveProfilePicture={handleRemoveProfilePicture}
                                                    onBioChange={handleBioChange}
                                                />
                                            )}

                                            {/* Onboarding Form Fields */}
                                            {currentStep === 1 && (
                                                <>
                                                    <Grid container spacing={3}>

                                                        {/* Left Column */}
                                                        <Grid item xs={12} md={12}>
                                                            {/* Service Type(s) */}
                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '300ms' }}
                                                            >
                                                                <Box id="service-type-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Service Type(s) <span style={{ color: '#d32f2f' }}>*</span>
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!serviceTypeError}>
                                                                        <Select
                                                                            multiple
                                                                            value={Array.isArray(onboardingData.serviceType) ? onboardingData.serviceType : []}
                                                                            onChange={handleOnboardingMultiSelectChange(
                                                                                'serviceType'
                                                                            )}
                                                                            open={serviceTypesSelectOpen}
                                                                            onOpen={() => {
                                                                                if (!isDeletingChip) {
                                                                                    setServiceTypesSelectOpen(true)
                                                                                }
                                                                            }}
                                                                            onClose={() =>
                                                                                setServiceTypesSelectOpen(false)
                                                                            }
                                                                            displayEmpty
                                                                            disabled={isLoadingServiceTypes}
                                                                            sx={{
                                                                                borderRadius: 2,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                    borderWidth: 2,
                                                                                },
                                                                            }}
                                                                            renderValue={selected => {
                                                                                const selectedValues = Array.isArray(selected) ? (selected as string[]) : (selected ? [selected as string] : [])
                                                                                if (
                                                                                    selectedValues.length === 0
                                                                                ) {
                                                                                    return (
                                                                                        <em style={{ color: 'grey.400' }}>
                                                                                            Select service types
                                                                                        </em>
                                                                                    )
                                                                                }
                                                                                return (
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: 'flex',
                                                                                            flexWrap: 'wrap',
                                                                                            gap: 0.5,
                                                                                        }}
                                                                                    >
                                                                                        {selectedValues.map(id => {
                                                                                            const matched = serviceTypes.find(
                                                                                                s => s._id === id
                                                                                            )
                                                                                            return (
                                                                                                <Chip
                                                                                                    key={id}
                                                                                                    label={matched?.name || id}
                                                                                                    size="small"
                                                                                                    onDelete={e => {
                                                                                                        e.preventDefault()
                                                                                                        e.stopPropagation()
                                                                                                        handleRemoveChip(
                                                                                                            'serviceType',
                                                                                                            id
                                                                                                        )
                                                                                                    }}
                                                                                                    onMouseDown={e => {
                                                                                                        if (
                                                                                                            e.target ===
                                                                                                            e.currentTarget ||
                                                                                                            (
                                                                                                                e.target as HTMLElement
                                                                                                            ).closest(
                                                                                                                '.MuiChip-deleteIcon'
                                                                                                            )
                                                                                                        ) {
                                                                                                            e.preventDefault()
                                                                                                            e.stopPropagation()
                                                                                                        }
                                                                                                    }}
                                                                                                    onClick={e => {
                                                                                                        e.preventDefault()
                                                                                                        e.stopPropagation()
                                                                                                    }}
                                                                                                    sx={{
                                                                                                        bgcolor: '#efebfdff',
                                                                                                        color: '#7A6FA1',
                                                                                                        cursor: 'default',
                                                                                                        '& .MuiChip-deleteIcon': {
                                                                                                            color: '#7A6FA1',
                                                                                                            cursor: 'pointer',
                                                                                                            '&:hover': {
                                                                                                                color: '#7A6FA1',
                                                                                                            },
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            )
                                                                                        })}
                                                                                    </Box>
                                                                                )
                                                                            }}
                                                                        >
                                                                            {serviceTypes.map(st => (
                                                                                <MenuItem
                                                                                    key={st._id}
                                                                                    value={st._id}
                                                                                >
                                                                                    {st.name}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                        {serviceTypeError && (
                                                                            <FormHelperText>{serviceTypeError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>

                                                            {/* Services Offered */}
                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '400ms' }}
                                                            >
                                                                <Box id="services-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Services Offered <span style={{ color: '#d32f2f' }}>*</span>
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!degreesError}>
                                                                        <Select
                                                                            multiple
                                                                            value={Array.isArray(onboardingData.degrees) ? onboardingData.degrees : []}
                                                                            onChange={handleOnboardingMultiSelectChange(
                                                                                'degrees'
                                                                            )}
                                                                            open={degreesSelectOpen}
                                                                            onOpen={() => {
                                                                                if (!isDeletingChip) {
                                                                                    setDegreesSelectOpen(true)
                                                                                }
                                                                            }}
                                                                            onClose={() =>
                                                                                setDegreesSelectOpen(false)
                                                                            }
                                                                            displayEmpty
                                                                            disabled={isLoadingSpecialties}
                                                                            sx={{
                                                                                borderRadius: 2,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                    borderWidth: 2,
                                                                                },
                                                                            }}

                                                                            renderValue={selected => {
                                                                                const selectedValues = Array.isArray(selected) ? (selected as string[]) : (selected ? [selected as string] : [])
                                                                                if (
                                                                                    selectedValues.length === 0
                                                                                ) {
                                                                                    return (
                                                                                        <em style={{ color: 'grey.400' }}>
                                                                                            Select service
                                                                                        </em>
                                                                                    )
                                                                                }
                                                                                return (
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: 'flex',
                                                                                            flexWrap: 'wrap',
                                                                                            gap: 0.5,
                                                                                        }}
                                                                                    >
                                                                                        {selectedValues.map(id => {
                                                                                            const specialty =
                                                                                                specialties.find(
                                                                                                    s => s._id === id
                                                                                                )
                                                                                            return (
                                                                                                <Chip
                                                                                                    key={id}
                                                                                                    label={specialty?.name || id}
                                                                                                    size="small"
                                                                                                    onDelete={e => {
                                                                                                        e.preventDefault()
                                                                                                        e.stopPropagation()
                                                                                                        handleRemoveChip(
                                                                                                            'degrees',
                                                                                                            id
                                                                                                        )
                                                                                                    }}
                                                                                                    onMouseDown={e => {
                                                                                                        // Only prevent if clicking on the chip itself, not the delete icon
                                                                                                        if (
                                                                                                            e.target ===
                                                                                                            e.currentTarget ||
                                                                                                            (
                                                                                                                e.target as HTMLElement
                                                                                                            ).closest(
                                                                                                                '.MuiChip-deleteIcon'
                                                                                                            )
                                                                                                        ) {
                                                                                                            e.preventDefault()
                                                                                                            e.stopPropagation()
                                                                                                        }
                                                                                                    }}
                                                                                                    onClick={e => {
                                                                                                        e.preventDefault()
                                                                                                        e.stopPropagation()
                                                                                                    }}
                                                                                                    sx={{
                                                                                                        bgcolor: '#efebfdff',
                                                                                                        color: '#7A6FA1',
                                                                                                        cursor: 'default',
                                                                                                        '& .MuiChip-deleteIcon': {
                                                                                                            color: '#7A6FA1',
                                                                                                            cursor: 'pointer',
                                                                                                            '&:hover': {
                                                                                                                color: '#7A6FA1',
                                                                                                            },
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            )
                                                                                        })}
                                                                                    </Box>
                                                                                )
                                                                            }}
                                                                        >
                                                                            {specialties.map(specialty => (
                                                                                <MenuItem
                                                                                    key={specialty._id}
                                                                                    value={specialty._id}
                                                                                >
                                                                                    {specialty.name}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                        {degreesError && (
                                                                            <FormHelperText>{degreesError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>

                                                            </Fade>

                                                            {shouldShowSpecialtyLicense && (
                                                                <Fade
                                                                    in
                                                                    timeout={800}
                                                                    style={{ transitionDelay: '450ms' }}
                                                                >
                                                                    <Box>
                                                                        {/* License Information Card */}
                                                                        <Box
                                                                            sx={{
                                                                                bgcolor: '#f8f9fa',
                                                                                borderRadius: 2,
                                                                                p: 3,
                                                                                mb: 3,
                                                                                border: '1px solid #e9ecef',
                                                                            }}
                                                                        >
                                                                            <Grid container spacing={3}>
                                                                                <Grid
                                                                                    item
                                                                                    xs={12}
                                                                                    md={6}
                                                                                    id="license-type-field"
                                                                                >
                                                                                    <Typography
                                                                                        variant="body1"
                                                                                        sx={{
                                                                                            color: 'grey.700',
                                                                                            mb: 1.5,
                                                                                            fontWeight: 500,
                                                                                            fontSize: '0.875rem',
                                                                                        }}
                                                                                    >
                                                                                        Specialty License Type *
                                                                                    </Typography>
                                                                                    <FormControl
                                                                                        fullWidth
                                                                                        error={!!npiStepErrors.licenseType}
                                                                                        disabled={isLoadingLicenseTypes}
                                                                                    >
                                                                                        <Select
                                                                                            displayEmpty
                                                                                            value={Array.isArray(onboardingData.licenseType) ? (onboardingData.licenseType[0] || '') : (onboardingData.licenseType || '')}
                                                                                            onChange={(event) => {
                                                                                                const selectedValue = event.target.value as string;
                                                                                                setOnboardingData(prev => ({
                                                                                                    ...prev,
                                                                                                    licenseType: [selectedValue],
                                                                                                }));
                                                                                                if (npiStepErrors.licenseType) {
                                                                                                    setNpiStepErrors(prev => ({ ...prev, licenseType: '' }));
                                                                                                }
                                                                                            }}
                                                                                            renderValue={(selected) => {
                                                                                                const val = Array.isArray(selected) ? selected[0] : selected;
                                                                                                if (!val) {
                                                                                                    return (
                                                                                                        <Typography sx={{ color: 'grey.500', fontSize: '0.875rem' }}>
                                                                                                            {isLoadingLicenseTypes
                                                                                                                ? 'Loading Specialty License Types *...'
                                                                                                                : 'Select Specialty License type'}
                                                                                                        </Typography>
                                                                                                    );
                                                                                                }
                                                                                                const matchedLicense = licenseTypes.find(type => type._id === val);
                                                                                                return matchedLicense ? matchedLicense.task : val;
                                                                                            }}
                                                                                            sx={{
                                                                                                borderRadius: 2,
                                                                                                '& .MuiOutlinedInput-notchedOutline':
                                                                                                {
                                                                                                    borderColor: 'grey.300',
                                                                                                },
                                                                                                '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                                {
                                                                                                    borderColor: 'grey.400',
                                                                                                },
                                                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                                {
                                                                                                    borderColor: primaryColor,
                                                                                                    borderWidth: 2,
                                                                                                },
                                                                                            }}
                                                                                        >
                                                                                            {licenseTypes.map(licenseType => (
                                                                                                <MenuItem
                                                                                                    key={licenseType._id}
                                                                                                    value={licenseType._id}
                                                                                                >
                                                                                                    {licenseType.task}
                                                                                                </MenuItem>
                                                                                            ))}
                                                                                        </Select>
                                                                                        {npiStepErrors.licenseType && (
                                                                                            <FormHelperText>
                                                                                                {npiStepErrors.licenseType}
                                                                                            </FormHelperText>
                                                                                        )}
                                                                                    </FormControl>
                                                                                </Grid>
                                                                                {(licenseTypeArray.some(id => id !== '69aacb7782693bdedf50db43')) && (
                                                                                    <>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={12}
                                                                                            md={6}
                                                                                            id="license-name-field"
                                                                                        >
                                                                                            <Typography
                                                                                                variant="body1"
                                                                                                sx={{
                                                                                                    color: 'grey.700',
                                                                                                    mb: 1.5,
                                                                                                    fontWeight: 500,
                                                                                                    fontSize: '0.875rem',
                                                                                                }}
                                                                                            >
                                                                                                NPI Number
                                                                                                {licenseTypeArray.some(id => isNpiRequired(id))
                                                                                                    ? ' *'
                                                                                                    : ''}
                                                                                            </Typography>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                placeholder="Enter NPI number"
                                                                                                value={onboardingData.licenseNpi}
                                                                                                onChange={handleOnboardingInputChange(
                                                                                                    'licenseNpi'
                                                                                                )}
                                                                                                required={licenseTypeArray.some(id => isNpiRequired(id))}
                                                                                                error={!!npiStepErrors.licenseNpi}
                                                                                                helperText={npiStepErrors.licenseNpi}
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-root': {
                                                                                                        bgcolor: 'white',
                                                                                                        borderRadius: 2,
                                                                                                        '& fieldset': {
                                                                                                            borderColor: 'grey.300',
                                                                                                        },
                                                                                                        '&:hover fieldset': {
                                                                                                            borderColor: 'grey.400',
                                                                                                        },
                                                                                                        '&.Mui-focused fieldset': {
                                                                                                            borderColor: primaryColor,
                                                                                                            borderWidth: 2,
                                                                                                        },
                                                                                                    },
                                                                                                }}
                                                                                            />
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={12}
                                                                                            md={12}
                                                                                            id="license-state-field"
                                                                                        >
                                                                                            <Typography
                                                                                                variant="body1"
                                                                                                sx={{
                                                                                                    color: 'grey.700',
                                                                                                    mb: 1.5,
                                                                                                    fontWeight: 500,
                                                                                                    fontSize: '0.875rem',
                                                                                                }}
                                                                                            >
                                                                                                License State*
                                                                                            </Typography>
                                                                                            <FormControl
                                                                                                fullWidth
                                                                                                error={!!npiStepErrors.licenseState}
                                                                                            >
                                                                                                <Select
                                                                                                    multiple
                                                                                                    value={Array.isArray(onboardingData.licenseState) ? onboardingData.licenseState : (onboardingData.licenseState ? [onboardingData.licenseState] : [])}
                                                                                                    onChange={(e) => {
                                                                                                        const val = e.target.value;
                                                                                                        setOnboardingData(prev => ({
                                                                                                            ...prev,
                                                                                                            licenseState: typeof val === 'string' ? val.split(',') : val
                                                                                                        }));
                                                                                                        if (npiStepErrors.licenseState) {
                                                                                                            setNpiStepErrors(prev => ({ ...prev, licenseState: '' }));
                                                                                                        }
                                                                                                    }}
                                                                                                    displayEmpty
                                                                                                    renderValue={(selected) => {
                                                                                                        const selectedArray = selected as string[];
                                                                                                        if (selectedArray.length === 0) {
                                                                                                            return <em style={{ color: 'grey.400' }}>Select state</em>;
                                                                                                        }
                                                                                                        return (
                                                                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                                                                {selectedArray.map((value) => (
                                                                                                                    <Chip key={value} label={value} size="small" />
                                                                                                                ))}
                                                                                                            </Box>
                                                                                                        );
                                                                                                    }}
                                                                                                    sx={{
                                                                                                        borderRadius: 2,
                                                                                                        '& .MuiOutlinedInput-notchedOutline':
                                                                                                        {
                                                                                                            borderColor: 'grey.300',
                                                                                                        },
                                                                                                        '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                                        {
                                                                                                            borderColor: 'grey.400',
                                                                                                        },
                                                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                                        {
                                                                                                            borderColor: primaryColor,
                                                                                                            borderWidth: 2,
                                                                                                        },
                                                                                                    }}
                                                                                                >
                                                                                                    {states.map(state => (
                                                                                                        <MenuItem
                                                                                                            key={state._id}
                                                                                                            value={state.name}
                                                                                                        >
                                                                                                            {state.name}
                                                                                                        </MenuItem>
                                                                                                    ))}
                                                                                                </Select>
                                                                                                {npiStepErrors.licenseState && (
                                                                                                    <FormHelperText>
                                                                                                        {npiStepErrors.licenseState}
                                                                                                    </FormHelperText>
                                                                                                )}
                                                                                            </FormControl>
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={12}
                                                                                            md={12}
                                                                                            id="license-number-field"
                                                                                        >
                                                                                            <Typography
                                                                                                variant="body1"
                                                                                                sx={{
                                                                                                    color: 'grey.700',
                                                                                                    mb: 1.5,
                                                                                                    fontWeight: 500,
                                                                                                    fontSize: '0.875rem',
                                                                                                }}
                                                                                            >
                                                                                                Upload Valid Specialty License - PT/OT/SP Agency are NOT mandatory
                                                                                                *
                                                                                            </Typography>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                placeholder="Enter license number"
                                                                                                value={onboardingData.licenseNumber}
                                                                                                onChange={handleOnboardingInputChange(
                                                                                                    'licenseNumber'
                                                                                                )}
                                                                                                error={!!npiStepErrors.licenseNumber}
                                                                                                helperText={npiStepErrors.licenseNumber}
                                                                                                sx={{
                                                                                                    mb: 3,
                                                                                                    '& .MuiOutlinedInput-root': {
                                                                                                        bgcolor: 'white',
                                                                                                        borderRadius: 2,
                                                                                                        '& fieldset': {
                                                                                                            borderColor: 'grey.300',
                                                                                                        },
                                                                                                        '&:hover fieldset': {
                                                                                                            borderColor: 'grey.400',
                                                                                                        },
                                                                                                        '&.Mui-focused fieldset': {
                                                                                                            borderColor: primaryColor,
                                                                                                            borderWidth: 2,
                                                                                                        },
                                                                                                    },
                                                                                                }}
                                                                                            />

                                                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                                                {(Array.isArray(onboardingData.licenseState) ? onboardingData.licenseState : (onboardingData.licenseState ? [onboardingData.licenseState] : [])).map((stateName) => {
                                                                                                    const stateDoc = onboardingData.licenseStateDocs?.[stateName];
                                                                                                    const isUploading = !!stateLicenseUploadLoading[stateName];
                                                                                                    const isDocMissing = !!npiStepErrors.licenseFileUrl && !stateDoc?.fileUrl;
                                                                                                    return (
                                                                                                        <Box key={stateName} sx={{ border: isDocMissing ? '1px solid #d32f2f' : '1px solid #e2e8f0', borderRadius: 2, p: 2, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.800' }}>
                                                                                                                    Specialty License Document for {stateName} *
                                                                                                                </Typography>
                                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                                    {isUploading ? (
                                                                                                                        <CircularProgress size={20} />
                                                                                                                    ) : stateDoc?.fileUrl ? (
                                                                                                                        <Chip
                                                                                                                            label={stateDoc.fileName || `${stateName} License Document`}
                                                                                                                            size="small"
                                                                                                                            onClick={() => window.open(stateDoc.fileUrl, '_blank', 'noopener,noreferrer')}
                                                                                                                            onDelete={(e) => handleRemoveStateLicense(stateName)(e)}
                                                                                                                            sx={{
                                                                                                                                cursor: 'pointer',
                                                                                                                                maxWidth: 200,
                                                                                                                                textOverflow: 'ellipsis',
                                                                                                                                overflow: 'hidden',
                                                                                                                                whiteSpace: 'nowrap',
                                                                                                                                bgcolor: '#EAE7F2',
                                                                                                                                color: '#574F75',
                                                                                                                                fontWeight: 500,
                                                                                                                                border: '1px solid #D1C9E7',
                                                                                                                                '& .MuiChip-deleteIcon': {
                                                                                                                                    color: '#7A6FA1',
                                                                                                                                    '&:hover': { color: '#574F75' }
                                                                                                                                }
                                                                                                                            }}
                                                                                                                        />
                                                                                                                    ) : (
                                                                                                                        <>
                                                                                                                            <input
                                                                                                                                type="file"
                                                                                                                                id={`license-file-input-${stateName}`}
                                                                                                                                style={{ display: 'none' }}
                                                                                                                                accept=".pdf,.jpg,.jpeg,.png"
                                                                                                                                onChange={handleStateLicenseDocumentChange(stateName)}
                                                                                                                            />
                                                                                                                            <Button
                                                                                                                                variant="outlined"
                                                                                                                                size="small"
                                                                                                                                startIcon={<CloudUpload />}
                                                                                                                                onClick={() => document.getElementById(`license-file-input-${stateName}`)?.click()}
                                                                                                                                sx={{
                                                                                                                                    textTransform: 'none',
                                                                                                                                    color: isDocMissing ? '#d32f2f' : primaryColor,
                                                                                                                                    borderColor: isDocMissing ? '#d32f2f' : primaryColor,
                                                                                                                                    '&:hover': {
                                                                                                                                        borderColor: isDocMissing ? '#b71c1c' : primaryColor,
                                                                                                                                    }
                                                                                                                                }}
                                                                                                                            >
                                                                                                                                Upload Document
                                                                                                                            </Button>
                                                                                                                        </>
                                                                                                                    )}
                                                                                                                </Box>
                                                                                                            </Box>
                                                                                                            {isDocMissing && (
                                                                                                                <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                                                                                                                    Specialty License document is required for {stateName}
                                                                                                                </Typography>
                                                                                                            )}
                                                                                                        </Box>
                                                                                                    );
                                                                                                })}
                                                                                            </Box>
                                                                                            {npiStepErrors.licenseFileUrl && (
                                                                                                <FormHelperText error sx={{ mt: 1 }}>
                                                                                                    {npiStepErrors.licenseFileUrl}
                                                                                                </FormHelperText>
                                                                                            )}
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={12}
                                                                                            md={6}
                                                                                            id="license-issue-field"
                                                                                        >
                                                                                            <Typography
                                                                                                variant="body1"
                                                                                                sx={{
                                                                                                    color: 'grey.700',
                                                                                                    mb: 1.5,
                                                                                                    fontWeight: 500,
                                                                                                    fontSize: '0.875rem',
                                                                                                }}
                                                                                            >
                                                                                                License Issue Date*
                                                                                            </Typography>
                                                                                            <LocalizationProvider
                                                                                                dateAdapter={AdapterDateFns}
                                                                                            >
                                                                                                <DatePicker
                                                                                                    value={
                                                                                                        onboardingData.issueDate
                                                                                                            ? new Date(onboardingData.issueDate)
                                                                                                            : null
                                                                                                    }
                                                                                                    onChange={value => {
                                                                                                        const formatted = value
                                                                                                            ? formatDate(
                                                                                                                value as Date,
                                                                                                                'MM/dd/yyyy'
                                                                                                            )
                                                                                                            : ''
                                                                                                        setOnboardingData(prev => ({
                                                                                                            ...prev,
                                                                                                            issueDate: formatted,
                                                                                                        }))
                                                                                                        clearNpiFieldError('issueDate')
                                                                                                    }}
                                                                                                    format="MM/dd/yyyy"
                                                                                                    views={['year', 'month', 'day']}
                                                                                                    openTo="year"
                                                                                                    maxDate={new Date()}
                                                                                                    slotProps={{
                                                                                                        textField: {
                                                                                                            fullWidth: true,
                                                                                                            placeholder: 'MM/DD/YYYY',
                                                                                                            error: !!npiStepErrors.issueDate,
                                                                                                            helperText: npiStepErrors.issueDate,
                                                                                                            sx: {
                                                                                                                '& .MuiOutlinedInput-root': {
                                                                                                                    bgcolor: 'white',
                                                                                                                    borderRadius: 2,
                                                                                                                    '& fieldset': {
                                                                                                                        borderColor: 'grey.300',
                                                                                                                    },
                                                                                                                    '&:hover fieldset': {
                                                                                                                        borderColor: 'grey.400',
                                                                                                                    },
                                                                                                                    '&.Mui-focused fieldset': {
                                                                                                                        borderColor: primaryColor,
                                                                                                                        borderWidth: 2,
                                                                                                                    },
                                                                                                                },
                                                                                                            },
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            </LocalizationProvider>
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={12}
                                                                                            md={6}
                                                                                            id="license-expiration-field"
                                                                                        >
                                                                                            <Typography
                                                                                                variant="body1"
                                                                                                sx={{
                                                                                                    color: 'grey.700',
                                                                                                    mb: 1.5,
                                                                                                    fontWeight: 500,
                                                                                                    fontSize: '0.875rem',
                                                                                                }}
                                                                                            >
                                                                                                License Expiration Date*
                                                                                            </Typography>
                                                                                            <LocalizationProvider
                                                                                                dateAdapter={AdapterDateFns}
                                                                                            >
                                                                                                <DatePicker
                                                                                                    value={
                                                                                                        onboardingData.licenseExpirationDate
                                                                                                            ? new Date(
                                                                                                                onboardingData.licenseExpirationDate
                                                                                                            )
                                                                                                            : null
                                                                                                    }
                                                                                                    onChange={value => {
                                                                                                        const formatted = value
                                                                                                            ? formatDate(
                                                                                                                value as Date,
                                                                                                                'MM/dd/yyyy'
                                                                                                            )
                                                                                                            : ''
                                                                                                        setOnboardingData(prev => ({
                                                                                                            ...prev,
                                                                                                            licenseExpirationDate: formatted,
                                                                                                        }))
                                                                                                        clearNpiFieldError(
                                                                                                            'licenseExpirationDate'
                                                                                                        )
                                                                                                    }}
                                                                                                    format="MM/dd/yyyy"
                                                                                                    views={['year', 'month', 'day']}
                                                                                                    openTo="year"
                                                                                                    minDate={new Date()}
                                                                                                    slotProps={{
                                                                                                        textField: {
                                                                                                            fullWidth: true,
                                                                                                            placeholder: 'MM/DD/YYYY',
                                                                                                            error:
                                                                                                                !!npiStepErrors.licenseExpirationDate,
                                                                                                            helperText:
                                                                                                                npiStepErrors.licenseExpirationDate,
                                                                                                            sx: {
                                                                                                                '& .MuiOutlinedInput-root': {
                                                                                                                    bgcolor: 'white',
                                                                                                                    borderRadius: 2,
                                                                                                                    '& fieldset': {
                                                                                                                        borderColor: 'grey.300',
                                                                                                                    },
                                                                                                                    '&:hover fieldset': {
                                                                                                                        borderColor: 'grey.400',
                                                                                                                    },
                                                                                                                    '&.Mui-focused fieldset': {
                                                                                                                        borderColor: primaryColor,
                                                                                                                        borderWidth: 2,
                                                                                                                    },
                                                                                                                },
                                                                                                            },
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            </LocalizationProvider>
                                                                                        </Grid>
                                                                                        <Grid item xs={12} md={6}>
                                                                                            <Typography
                                                                                                variant="body1"
                                                                                                sx={{
                                                                                                    color: 'grey.700',
                                                                                                    mb: 1.5,
                                                                                                    fontWeight: 500,
                                                                                                    fontSize: '0.875rem',
                                                                                                }}
                                                                                            >
                                                                                                License Status*
                                                                                            </Typography>
                                                                                            <FormControl fullWidth>
                                                                                                <Select
                                                                                                    value={
                                                                                                        onboardingData.licenseStatus ||
                                                                                                        'Active'
                                                                                                    }
                                                                                                    onChange={handleOnboardingInputChange(
                                                                                                        'licenseStatus'
                                                                                                    )}
                                                                                                    sx={{
                                                                                                        borderRadius: 2,
                                                                                                        '& .MuiOutlinedInput-notchedOutline':
                                                                                                        {
                                                                                                            borderColor: 'grey.300',
                                                                                                        },
                                                                                                        '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                                        {
                                                                                                            borderColor: 'grey.400',
                                                                                                        },
                                                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                                        {
                                                                                                            borderColor: primaryColor,
                                                                                                            borderWidth: 2,
                                                                                                        },
                                                                                                    }}
                                                                                                >
                                                                                                    <MenuItem value="Active">
                                                                                                        Active
                                                                                                    </MenuItem>
                                                                                                    <MenuItem value="Inactive">
                                                                                                        Inactive
                                                                                                    </MenuItem>
                                                                                                    <MenuItem value="Suspended">
                                                                                                        Suspended
                                                                                                    </MenuItem>
                                                                                                    <MenuItem value="Expired">
                                                                                                        Expired
                                                                                                    </MenuItem>
                                                                                                </Select>
                                                                                            </FormControl>
                                                                                        </Grid>
                                                                                    </>
                                                                                )}
                                                                            </Grid>
                                                                        </Box>
                                                                        {/* Render additional license cards */}
                                                                        {additionalLicenses.map((lic, index) => (
                                                                            <Box
                                                                                key={index}
                                                                                sx={{
                                                                                    bgcolor: '#f8f9fa',
                                                                                    borderRadius: 2,
                                                                                    p: 3,
                                                                                    mb: 3,
                                                                                    border: '1px solid #e9ecef',
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        justifyContent: 'flex-end',
                                                                                        mb: 2,
                                                                                    }}
                                                                                >
                                                                                    <IconButton
                                                                                        aria-label="Remove license"
                                                                                        onClick={() =>
                                                                                            setAdditionalLicenses(prev =>
                                                                                                prev.filter((_, i) => i !== index)
                                                                                            )
                                                                                        }
                                                                                        size="small"
                                                                                        sx={{ color: 'grey.600' }}
                                                                                    >
                                                                                        <Delete />
                                                                                    </IconButton>
                                                                                </Box>

                                                                                <Grid container spacing={3}>
                                                                                    <Grid
                                                                                        item
                                                                                        xs={12}
                                                                                        md={6}
                                                                                        id={`additional-license-type-${index}`}
                                                                                    >
                                                                                        <Typography
                                                                                            variant="body1"
                                                                                            sx={{
                                                                                                color: 'grey.700',
                                                                                                mb: 1.5,
                                                                                                fontWeight: 500,
                                                                                                fontSize: '0.875rem',
                                                                                            }}
                                                                                        >
                                                                                            Specialty License Type *
                                                                                        </Typography>
                                                                                        <FormControl
                                                                                            fullWidth
                                                                                            error={!!npiStepErrors[`additionalLicenseType_${index}`]}
                                                                                            disabled={isLoadingLicenseTypes}
                                                                                        >
                                                                                            <Select
                                                                                                displayEmpty
                                                                                                value={lic.licenseType || ''}
                                                                                                onChange={(event) => {
                                                                                                    handleAdditionalLicenseChange(
                                                                                                        index,
                                                                                                        'licenseType',
                                                                                                        event.target.value as string
                                                                                                    );
                                                                                                }}
                                                                                                renderValue={(selected) => {
                                                                                                    const val = selected as string;
                                                                                                    if (!val) {
                                                                                                        return (
                                                                                                            <Typography sx={{ color: 'grey.500', fontSize: '0.875rem' }}>
                                                                                                                {isLoadingLicenseTypes
                                                                                                                    ? 'Loading Specialty License Types *...'
                                                                                                                    : 'Select Specialty License type'}
                                                                                                            </Typography>
                                                                                                        );
                                                                                                    }
                                                                                                    const matchedLicense = licenseTypes.find(type => type._id === val);
                                                                                                    return matchedLicense ? matchedLicense.task : val;
                                                                                                }}
                                                                                                sx={{
                                                                                                    borderRadius: 2,
                                                                                                    '& .MuiOutlinedInput-notchedOutline':
                                                                                                    {
                                                                                                        borderColor: 'grey.300',
                                                                                                    },
                                                                                                    '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                                    {
                                                                                                        borderColor: 'grey.400',
                                                                                                    },
                                                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                                    {
                                                                                                        borderColor: primaryColor,
                                                                                                        borderWidth: 2,
                                                                                                    },
                                                                                                }}
                                                                                            >
                                                                                                {licenseTypes.map(licenseType => (
                                                                                                    <MenuItem
                                                                                                        key={licenseType._id}
                                                                                                        value={licenseType._id}
                                                                                                    >
                                                                                                        {licenseType.task}
                                                                                                    </MenuItem>
                                                                                                ))}
                                                                                            </Select>
                                                                                            {npiStepErrors[`additionalLicenseType_${index}`] && (
                                                                                                <FormHelperText>
                                                                                                    {npiStepErrors[`additionalLicenseType_${index}`]}
                                                                                                </FormHelperText>
                                                                                            )}
                                                                                        </FormControl>
                                                                                    </Grid>
                                                                                    {lic.licenseType !== '69aacb7782693bdedf50db43' && (
                                                                                        <>
                                                                                            <Grid
                                                                                                item
                                                                                                xs={12}
                                                                                                md={6}
                                                                                                id={`additional-license-npi-${index}`}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="body1"
                                                                                                    sx={{
                                                                                                        color: 'grey.700',
                                                                                                        mb: 1.5,
                                                                                                        fontWeight: 500,
                                                                                                        fontSize: '0.875rem',
                                                                                                    }}
                                                                                                >
                                                                                                    NPI Number
                                                                                                </Typography>
                                                                                                <TextField
                                                                                                    fullWidth
                                                                                                    placeholder="Enter NPI number"
                                                                                                    value={lic.licenseNpi || ''}
                                                                                                    onChange={e =>
                                                                                                        handleAdditionalLicenseChange(
                                                                                                            index,
                                                                                                            'licenseNpi',
                                                                                                            e.target.value
                                                                                                        )
                                                                                                    }
                                                                                                    error={!!npiStepErrors[`additionalLicenseNpi_${index}`]}
                                                                                                    helperText={npiStepErrors[`additionalLicenseNpi_${index}`]}
                                                                                                    sx={{
                                                                                                        '& .MuiOutlinedInput-root': {
                                                                                                            bgcolor: 'white',
                                                                                                            borderRadius: 2,
                                                                                                            '& fieldset': {
                                                                                                                borderColor: 'grey.300',
                                                                                                            },
                                                                                                            '&:hover fieldset': {
                                                                                                                borderColor: 'grey.400',
                                                                                                            },
                                                                                                            '&.Mui-focused fieldset': {
                                                                                                                borderColor: primaryColor,
                                                                                                                borderWidth: 2,
                                                                                                            },
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            </Grid>
                                                                                            <Grid
                                                                                                item
                                                                                                xs={12}
                                                                                                md={12}
                                                                                                id={`additional-license-state-${index}`}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="body1"
                                                                                                    sx={{
                                                                                                        color: 'grey.700',
                                                                                                        mb: 1.5,
                                                                                                        fontWeight: 500,
                                                                                                        fontSize: '0.875rem',
                                                                                                    }}
                                                                                                >
                                                                                                    License State*
                                                                                                </Typography>
                                                                                                <FormControl
                                                                                                    fullWidth
                                                                                                    error={!!npiStepErrors[`additionalLicenseState_${index}`]}
                                                                                                >
                                                                                                    <Select
                                                                                                        multiple
                                                                                                        value={Array.isArray(lic.licenseState) ? lic.licenseState : (lic.licenseState ? [lic.licenseState] : [])}
                                                                                                        onChange={(e) => {
                                                                                                            const value = e.target.value;
                                                                                                            const stateArr = typeof value === 'string' ? value.split(',') : value;
                                                                                                            handleAdditionalLicenseChange(
                                                                                                                index,
                                                                                                                'licenseState',
                                                                                                                stateArr
                                                                                                            );
                                                                                                        }}
                                                                                                        displayEmpty
                                                                                                        renderValue={(selected) => {
                                                                                                            const selectedArray = selected as string[];
                                                                                                            if (selectedArray.length === 0) {
                                                                                                                return (
                                                                                                                    <em style={{ color: 'grey.400' }}>
                                                                                                                        Select state
                                                                                                                    </em>
                                                                                                                );
                                                                                                            }
                                                                                                            return (
                                                                                                                <Box
                                                                                                                    sx={{
                                                                                                                        display: 'flex',
                                                                                                                        flexWrap: 'wrap',
                                                                                                                        gap: 0.5,
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {selectedArray.map(value => (
                                                                                                                        <Chip
                                                                                                                            key={value}
                                                                                                                            label={value}
                                                                                                                            size="small"
                                                                                                                        />
                                                                                                                    ))}
                                                                                                                </Box>
                                                                                                            );
                                                                                                        }}
                                                                                                        sx={{
                                                                                                            borderRadius: 2,
                                                                                                            '& .MuiOutlinedInput-notchedOutline':
                                                                                                            {
                                                                                                                borderColor: 'grey.300',
                                                                                                            },
                                                                                                            '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                                            {
                                                                                                                borderColor: 'grey.400',
                                                                                                            },
                                                                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                                            {
                                                                                                                borderColor: primaryColor,
                                                                                                                borderWidth: 2,
                                                                                                            },
                                                                                                        }}
                                                                                                    >
                                                                                                        {states.map(state => (
                                                                                                            <MenuItem
                                                                                                                key={state._id}
                                                                                                                value={state.name}
                                                                                                            >
                                                                                                                {state.name}
                                                                                                            </MenuItem>
                                                                                                        ))}
                                                                                                    </Select>
                                                                                                    {npiStepErrors[`additionalLicenseState_${index}`] && (
                                                                                                        <FormHelperText>
                                                                                                            {npiStepErrors[`additionalLicenseState_${index}`]}
                                                                                                        </FormHelperText>
                                                                                                    )}
                                                                                                </FormControl>
                                                                                            </Grid>
                                                                                            <Grid
                                                                                                item
                                                                                                xs={12}
                                                                                                md={12}
                                                                                                id={`additional-license-number-${index}`}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="body1"
                                                                                                    sx={{
                                                                                                        color: 'grey.700',
                                                                                                        mb: 1.5,
                                                                                                        fontWeight: 500,
                                                                                                        fontSize: '0.875rem',
                                                                                                    }}
                                                                                                >
                                                                                                    Upload Valid Specialty License - PT/OT/SP Agency are NOT mandatory *
                                                                                                </Typography>
                                                                                                <TextField
                                                                                                    fullWidth
                                                                                                    placeholder="Enter license number"
                                                                                                    value={lic.licenseNumber || ''}
                                                                                                    onChange={e =>
                                                                                                        handleAdditionalLicenseChange(
                                                                                                            index,
                                                                                                            'licenseNumber',
                                                                                                            e.target.value
                                                                                                        )
                                                                                                    }
                                                                                                    error={!!npiStepErrors[`additionalLicenseNumber_${index}`]}
                                                                                                    helperText={npiStepErrors[`additionalLicenseNumber_${index}`]}
                                                                                                    sx={{
                                                                                                        mb: 3,
                                                                                                        '& .MuiOutlinedInput-root': {
                                                                                                            bgcolor: 'white',
                                                                                                            borderRadius: 2,
                                                                                                            '& fieldset': {
                                                                                                                borderColor: 'grey.300',
                                                                                                            },
                                                                                                            '&:hover fieldset': {
                                                                                                                borderColor: 'grey.400',
                                                                                                            },
                                                                                                            '&.Mui-focused fieldset': {
                                                                                                                borderColor: primaryColor,
                                                                                                                borderWidth: 2,
                                                                                                            },
                                                                                                        },
                                                                                                    }}
                                                                                                />

                                                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                                                    {(Array.isArray(lic.licenseState) ? lic.licenseState : (lic.licenseState ? [lic.licenseState] : [])).map((stateName) => {
                                                                                                        const stateDoc = lic.licenseStateDocs?.[stateName];
                                                                                                        const loadingKey = `add_${index}_${stateName}`;
                                                                                                        const isUploading = !!stateLicenseUploadLoading[loadingKey];
                                                                                                        const isDocMissing = !!npiStepErrors[`additionalLicenseFileUrl_${index}`] && !stateDoc?.fileUrl;
                                                                                                        return (
                                                                                                            <Box key={stateName} sx={{ border: isDocMissing ? '1px solid #d32f2f' : '1px solid #e2e8f0', borderRadius: 2, p: 2, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.800' }}>
                                                                                                                        Specialty License Document for {stateName} *
                                                                                                                    </Typography>
                                                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                                        {isUploading ? (
                                                                                                                            <CircularProgress size={20} />
                                                                                                                        ) : stateDoc?.fileUrl ? (
                                                                                                                            <Chip
                                                                                                                                label={stateDoc.fileName || `${stateName} License Document`}
                                                                                                                                size="small"
                                                                                                                                onClick={() => window.open(stateDoc.fileUrl, '_blank', 'noopener,noreferrer')}
                                                                                                                                onDelete={(e) => handleRemoveAdditionalStateLicense(index, stateName)(e)}
                                                                                                                                sx={{
                                                                                                                                    cursor: 'pointer',
                                                                                                                                    maxWidth: 200,
                                                                                                                                    textOverflow: 'ellipsis',
                                                                                                                                    overflow: 'hidden',
                                                                                                                                    whiteSpace: 'nowrap',
                                                                                                                                    bgcolor: '#EAE7F2',
                                                                                                                                    color: '#574F75',
                                                                                                                                    fontWeight: 500,
                                                                                                                                    border: '1px solid #D1C9E7',
                                                                                                                                    '& .MuiChip-deleteIcon': {
                                                                                                                                        color: '#7A6FA1',
                                                                                                                                        '&:hover': { color: '#574F75' }
                                                                                                                                    }
                                                                                                                                }}
                                                                                                                            />
                                                                                                                        ) : (
                                                                                                                            <>
                                                                                                                                <input
                                                                                                                                    type="file"
                                                                                                                                    id={`additional-license-file-input-${index}-${stateName}`}
                                                                                                                                    style={{ display: 'none' }}
                                                                                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                                                                                    onChange={handleAdditionalStateLicenseDocumentChange(index, stateName)}
                                                                                                                                />
                                                                                                                                <Button
                                                                                                                                    variant="outlined"
                                                                                                                                    size="small"
                                                                                                                                    startIcon={<CloudUpload />}
                                                                                                                                    onClick={() => document.getElementById(`additional-license-file-input-${index}-${stateName}`)?.click()}
                                                                                                                                    sx={{
                                                                                                                                        textTransform: 'none',
                                                                                                                                        color: isDocMissing ? '#d32f2f' : primaryColor,
                                                                                                                                        borderColor: isDocMissing ? '#d32f2f' : primaryColor,
                                                                                                                                        '&:hover': {
                                                                                                                                            borderColor: isDocMissing ? '#b71c1c' : primaryColor,
                                                                                                                                        }
                                                                                                                                    }}
                                                                                                                                >
                                                                                                                                    Upload Document
                                                                                                                                </Button>
                                                                                                                            </>
                                                                                                                        )}
                                                                                                                    </Box>
                                                                                                                </Box>
                                                                                                                {isDocMissing && (
                                                                                                                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                                                                                                                        Specialty License document is required for {stateName}
                                                                                                                    </Typography>
                                                                                                                )}
                                                                                                            </Box>
                                                                                                        );
                                                                                                    })}
                                                                                                </Box>
                                                                                                {npiStepErrors[`additionalLicenseFileUrl_${index}`] && (
                                                                                                    <FormHelperText error sx={{ mt: 1 }}>
                                                                                                        {npiStepErrors[`additionalLicenseFileUrl_${index}`]}
                                                                                                    </FormHelperText>
                                                                                                )}
                                                                                            </Grid>
                                                                                            <Grid
                                                                                                item
                                                                                                xs={12}
                                                                                                md={6}
                                                                                                id={`additional-license-issue-${index}`}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="body1"
                                                                                                    sx={{
                                                                                                        color: 'grey.700',
                                                                                                        mb: 1.5,
                                                                                                        fontWeight: 500,
                                                                                                        fontSize: '0.875rem',
                                                                                                    }}
                                                                                                >
                                                                                                    License Issue Date*
                                                                                                </Typography>
                                                                                                <LocalizationProvider
                                                                                                    dateAdapter={AdapterDateFns}
                                                                                                >
                                                                                                    <DatePicker
                                                                                                        value={
                                                                                                            lic.issueDate
                                                                                                                ? new Date(lic.issueDate)
                                                                                                                : null
                                                                                                        }
                                                                                                        onChange={value => {
                                                                                                            const formatted = value
                                                                                                                ? formatDate(
                                                                                                                    value as Date,
                                                                                                                    'MM/dd/yyyy'
                                                                                                                )
                                                                                                                : ''
                                                                                                            handleAdditionalLicenseChange(
                                                                                                                index,
                                                                                                                'issueDate',
                                                                                                                formatted
                                                                                                            )
                                                                                                        }}
                                                                                                        format="MM/dd/yyyy"
                                                                                                        views={['year', 'month', 'day']}
                                                                                                        openTo="year"
                                                                                                        maxDate={new Date()}
                                                                                                        slotProps={{
                                                                                                            textField: {
                                                                                                                fullWidth: true,
                                                                                                                placeholder: 'MM/DD/YYYY',
                                                                                                                error: !!npiStepErrors[`additionalIssueDate_${index}`],
                                                                                                                helperText: npiStepErrors[`additionalIssueDate_${index}`],
                                                                                                                sx: {
                                                                                                                    '& .MuiOutlinedInput-root': {
                                                                                                                        bgcolor: 'white',
                                                                                                                        borderRadius: 2,
                                                                                                                        '& fieldset': {
                                                                                                                            borderColor: 'grey.300',
                                                                                                                        },
                                                                                                                        '&:hover fieldset': {
                                                                                                                            borderColor: 'grey.400',
                                                                                                                        },
                                                                                                                        '&.Mui-focused fieldset': {
                                                                                                                            borderColor: primaryColor,
                                                                                                                            borderWidth: 2,
                                                                                                                        },
                                                                                                                    },
                                                                                                                },
                                                                                                            },
                                                                                                        }}
                                                                                                    />
                                                                                                </LocalizationProvider>
                                                                                            </Grid>
                                                                                            <Grid
                                                                                                item
                                                                                                xs={12}
                                                                                                md={6}
                                                                                                id={`additional-license-expiration-${index}`}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="body1"
                                                                                                    sx={{
                                                                                                        color: 'grey.700',
                                                                                                        mb: 1.5,
                                                                                                        fontWeight: 500,
                                                                                                        fontSize: '0.875rem',
                                                                                                    }}
                                                                                                >
                                                                                                    License Expiration Date*
                                                                                                </Typography>
                                                                                                <LocalizationProvider
                                                                                                    dateAdapter={AdapterDateFns}
                                                                                                >
                                                                                                    <DatePicker
                                                                                                        value={
                                                                                                            lic.licenseExpirationDate
                                                                                                                ? new Date(
                                                                                                                    lic.licenseExpirationDate
                                                                                                                )
                                                                                                                : null
                                                                                                        }
                                                                                                        onChange={value => {
                                                                                                            const formatted = value
                                                                                                                ? formatDate(
                                                                                                                    value as Date,
                                                                                                                    'MM/dd/yyyy'
                                                                                                                )
                                                                                                                : ''
                                                                                                            handleAdditionalLicenseChange(
                                                                                                                index,
                                                                                                                'licenseExpirationDate',
                                                                                                                formatted
                                                                                                            )
                                                                                                        }}
                                                                                                        format="MM/dd/yyyy"
                                                                                                        views={['year', 'month', 'day']}
                                                                                                        openTo="year"
                                                                                                        minDate={new Date()}
                                                                                                        slotProps={{
                                                                                                            textField: {
                                                                                                                fullWidth: true,
                                                                                                                placeholder: 'MM/DD/YYYY',
                                                                                                                error: !!npiStepErrors[`additionalLicenseExpirationDate_${index}`],
                                                                                                                helperText: npiStepErrors[`additionalLicenseExpirationDate_${index}`],
                                                                                                                sx: {
                                                                                                                    '& .MuiOutlinedInput-root': {
                                                                                                                        bgcolor: 'white',
                                                                                                                        borderRadius: 2,
                                                                                                                        '& fieldset': {
                                                                                                                            borderColor: 'grey.300',
                                                                                                                        },
                                                                                                                        '&:hover fieldset': {
                                                                                                                            borderColor: 'grey.400',
                                                                                                                        },
                                                                                                                        '&.Mui-focused fieldset': {
                                                                                                                            borderColor: primaryColor,
                                                                                                                            borderWidth: 2,
                                                                                                                        },
                                                                                                                    },
                                                                                                                },
                                                                                                            },
                                                                                                        }}
                                                                                                    />
                                                                                                </LocalizationProvider>
                                                                                            </Grid>
                                                                                            <Grid
                                                                                                item
                                                                                                xs={12}
                                                                                                md={6}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="body1"
                                                                                                    sx={{
                                                                                                        color: 'grey.700',
                                                                                                        mb: 1.5,
                                                                                                        fontWeight: 500,
                                                                                                        fontSize: '0.875rem',
                                                                                                    }}
                                                                                                >
                                                                                                    License Status*
                                                                                                </Typography>
                                                                                                <FormControl fullWidth>
                                                                                                    <Select
                                                                                                        value={lic.licenseStatus || 'Active'}
                                                                                                        onChange={e =>
                                                                                                            handleAdditionalLicenseChange(
                                                                                                                index,
                                                                                                                'licenseStatus',
                                                                                                                e.target.value as string
                                                                                                            )
                                                                                                        }
                                                                                                        sx={{
                                                                                                            borderRadius: 2,
                                                                                                            '& .MuiOutlinedInput-notchedOutline':
                                                                                                                { borderColor: 'grey.300' },
                                                                                                            '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                                                { borderColor: 'grey.400' },
                                                                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                                            {
                                                                                                                borderColor: primaryColor,
                                                                                                                borderWidth: 2,
                                                                                                            },
                                                                                                        }}
                                                                                                    >
                                                                                                        <MenuItem value="Active">
                                                                                                            Active
                                                                                                        </MenuItem>
                                                                                                        <MenuItem value="Inactive">
                                                                                                            Inactive
                                                                                                        </MenuItem>
                                                                                                        <MenuItem value="Suspended">
                                                                                                            Suspended
                                                                                                        </MenuItem>
                                                                                                        <MenuItem value="Expired">
                                                                                                            Expired
                                                                                                        </MenuItem>
                                                                                                    </Select>
                                                                                                </FormControl>
                                                                                            </Grid>
                                                                                        </>
                                                                                    )}
                                                                                </Grid>
                                                                            </Box>
                                                                        ))}

                                                                        {/* Add more License button */}
                                                                        <Button
                                                                            variant="outlined"
                                                                            startIcon={<Add />}
                                                                            onClick={() =>
                                                                                setAdditionalLicenses(prev => [
                                                                                    ...prev,
                                                                                    {
                                                                                        licenseType: '',
                                                                                        licenseNpi: '',
                                                                                        licenseState: [],
                                                                                        licenseNumber: '',
                                                                                        issueDate: '',
                                                                                        licenseExpirationDate: '',
                                                                                        licenseStatus: 'Active',
                                                                                        licenseFileUrl: '',
                                                                                        licenseStateDocs: {},
                                                                                    },
                                                                                ])
                                                                            }
                                                                            sx={{
                                                                                borderColor: '#d1d5db',
                                                                                color: 'grey.700',
                                                                                textTransform: 'none',
                                                                                fontWeight: 500,
                                                                                px: 3,
                                                                                py: 1.5,
                                                                                mb: 2,
                                                                                borderRadius: 2,
                                                                                '&:hover': {
                                                                                    borderColor: primaryColor,
                                                                                    color: primaryColor,
                                                                                    bgcolor: '#f8f9ff',
                                                                                },
                                                                            }}
                                                                        >
                                                                            Add more License
                                                                        </Button>

                                                                        {/* Main License Document Preview */}
                                                                        {Object.keys(onboardingData.licenseStateDocs || {}).length > 0 ? (
                                                                            <>
                                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'grey.800', mt: 2 }}>
                                                                                    Added Specialty License(s)
                                                                                </Typography>
                                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                                                                                    {Object.entries(onboardingData.licenseStateDocs || {}).map(([stateName, doc]) => {
                                                                                        if (!doc.fileUrl) return null;
                                                                                        return (
                                                                                            <Box key={stateName} sx={{ p: 2.5, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', gap: 2, maxWidth: '400px' }}>
                                                                                                {doc.filePreview ? (
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            width: 64,
                                                                                                            height: 64,
                                                                                                            borderRadius: 1,
                                                                                                            overflow: 'hidden',
                                                                                                            border: '1px solid #e5e7eb',
                                                                                                            bgcolor: 'white',
                                                                                                            display: 'flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center'
                                                                                                        }}
                                                                                                    >
                                                                                                        <img
                                                                                                            src={doc.filePreview}
                                                                                                            alt="License preview"
                                                                                                            style={{
                                                                                                                width: '100%',
                                                                                                                height: '100%',
                                                                                                                objectFit: 'contain'
                                                                                                            }}
                                                                                                        />
                                                                                                    </Box>
                                                                                                ) : (
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            width: 64,
                                                                                                            height: 64,
                                                                                                            borderRadius: 1,
                                                                                                            bgcolor: '#fee2e2',
                                                                                                            display: 'flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center'
                                                                                                        }}
                                                                                                    >
                                                                                                        <PictureAsPdf sx={{ fontSize: 32, color: '#dc2626' }} />
                                                                                                    </Box>
                                                                                                )}
                                                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.800', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                                                        {doc.fileName || 'License Document'}
                                                                                                    </Typography>
                                                                                                    <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mt: 0.5 }}>
                                                                                                        Specialty License Document ({stateName})
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                                <Box>
                                                                                                    <Button
                                                                                                        variant="text"
                                                                                                        size="small"
                                                                                                        onClick={() => {
                                                                                                            if (doc.fileUrl) {
                                                                                                                window.open(doc.fileUrl, '_blank', 'noopener,noreferrer');
                                                                                                            }
                                                                                                        }}
                                                                                                        sx={{ textTransform: 'none', color: primaryColor, fontWeight: 600, minWidth: 'auto', p: 0.5, mr: 1 }}
                                                                                                    >
                                                                                                        View
                                                                                                    </Button>
                                                                                                    <Button
                                                                                                        variant="text"
                                                                                                        size="small"
                                                                                                        color="error"
                                                                                                        onClick={(e) => handleRemoveStateLicense(stateName)(e)}
                                                                                                        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: 0.5 }}
                                                                                                    >
                                                                                                        Remove
                                                                                                    </Button>
                                                                                                </Box>
                                                                                            </Box>
                                                                                        );
                                                                                    })}
                                                                                </Box>
                                                                            </>
                                                                        ) : (
                                                                            !!onboardingData.licenseFileUrl && (
                                                                                <>
                                                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'grey.800', mt: 2 }}>
                                                                                        Added Specialty License(s)
                                                                                    </Typography>
                                                                                    <Box sx={{ mt: 1, p: 2.5, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', gap: 2, maxWidth: '400px' }}>
                                                                                        {onboardingData.licenseFilePreview ? (
                                                                                            <Box
                                                                                                sx={{
                                                                                                    width: 64,
                                                                                                    height: 64,
                                                                                                    borderRadius: 1,
                                                                                                    overflow: 'hidden',
                                                                                                    border: '1px solid #e5e7eb',
                                                                                                    bgcolor: 'white',
                                                                                                    display: 'flex',
                                                                                                    alignItems: 'center',
                                                                                                    justifyContent: 'center'
                                                                                                }}
                                                                                            >
                                                                                                <img
                                                                                                    src={onboardingData.licenseFilePreview}
                                                                                                    alt="License preview"
                                                                                                    style={{
                                                                                                        width: '100%',
                                                                                                        height: '100%',
                                                                                                        objectFit: 'contain'
                                                                                                    }}
                                                                                                />
                                                                                            </Box>
                                                                                        ) : (
                                                                                            <Box
                                                                                                sx={{
                                                                                                    width: 64,
                                                                                                    height: 64,
                                                                                                    borderRadius: 1,
                                                                                                    bgcolor: '#fee2e2',
                                                                                                    display: 'flex',
                                                                                                    alignItems: 'center',
                                                                                                    justifyContent: 'center'
                                                                                                }}
                                                                                            >
                                                                                                <PictureAsPdf sx={{ fontSize: 32, color: '#dc2626' }} />
                                                                                            </Box>
                                                                                        )}
                                                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.800', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                                                {onboardingData.licenseFileName || 'License Document'}
                                                                                            </Typography>
                                                                                            <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mt: 0.5 }}>
                                                                                                Specialty License Document
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Box>
                                                                                            <Button
                                                                                                variant="text"
                                                                                                size="small"
                                                                                                onClick={() => {
                                                                                                    if (onboardingData.licenseFileUrl) {
                                                                                                        window.open(onboardingData.licenseFileUrl, '_blank', 'noopener,noreferrer');
                                                                                                    }
                                                                                                }}
                                                                                                sx={{ textTransform: 'none', color: primaryColor, fontWeight: 600, minWidth: 'auto', p: 0.5, mr: 1 }}
                                                                                            >
                                                                                                View
                                                                                            </Button>
                                                                                            <Button
                                                                                                variant="text"
                                                                                                size="small"
                                                                                                color="error"
                                                                                                onClick={(e) => handleRemoveMainLicense(e)}
                                                                                                sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: 0.5 }}
                                                                                            >
                                                                                                Remove
                                                                                            </Button>
                                                                                        </Box>
                                                                                    </Box>
                                                                                </>
                                                                            )
                                                                        )}

                                                                        {/* Additional License Document Previews */}
                                                                        {additionalLicenses.map((lic, index) => {
                                                                            const stateDocsList = Object.entries(lic.licenseStateDocs || {}).filter(([_, d]) => !!d.fileUrl);
                                                                            if (stateDocsList.length > 0) {
                                                                                return (
                                                                                    <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                                        {stateDocsList.map(([stateName, doc]) => (
                                                                                            <Box key={stateName} sx={{ mt: 1, p: 2.5, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', gap: 2, maxWidth: '400px' }}>
                                                                                                {doc.filePreview ? (
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            width: 64,
                                                                                                            height: 64,
                                                                                                            borderRadius: 1,
                                                                                                            overflow: 'hidden',
                                                                                                            border: '1px solid #e5e7eb',
                                                                                                            bgcolor: 'white',
                                                                                                            display: 'flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center'
                                                                                                        }}
                                                                                                    >
                                                                                                        <img
                                                                                                            src={doc.filePreview}
                                                                                                            alt="License preview"
                                                                                                            style={{
                                                                                                                width: '100%',
                                                                                                                height: '100%',
                                                                                                                objectFit: 'contain'
                                                                                                            }}
                                                                                                        />
                                                                                                    </Box>
                                                                                                ) : (
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            width: 64,
                                                                                                            height: 64,
                                                                                                            borderRadius: 1,
                                                                                                            bgcolor: '#fee2e2',
                                                                                                            display: 'flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center'
                                                                                                        }}
                                                                                                    >
                                                                                                        <PictureAsPdf sx={{ fontSize: 32, color: '#dc2626' }} />
                                                                                                    </Box>
                                                                                                )}
                                                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.800', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                                                        {doc.fileName || `License Document (${stateName})`}
                                                                                                    </Typography>
                                                                                                    <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mt: 0.5 }}>
                                                                                                        Specialty License Document ({stateName})
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                                <Box>
                                                                                                    <Button
                                                                                                        variant="text"
                                                                                                        size="small"
                                                                                                        onClick={() => {
                                                                                                            if (doc.fileUrl) {
                                                                                                                window.open(doc.fileUrl, '_blank', 'noopener,noreferrer');
                                                                                                            }
                                                                                                        }}
                                                                                                        sx={{ textTransform: 'none', color: primaryColor, fontWeight: 600, minWidth: 'auto', p: 0.5, mr: 1 }}
                                                                                                    >
                                                                                                        View
                                                                                                    </Button>
                                                                                                    <Button
                                                                                                        variant="text"
                                                                                                        size="small"
                                                                                                        color="error"
                                                                                                        onClick={(e) => handleRemoveAdditionalStateLicense(index, stateName)(e)}
                                                                                                        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: 0.5 }}
                                                                                                    >
                                                                                                        Remove
                                                                                                    </Button>
                                                                                                </Box>
                                                                                            </Box>
                                                                                        ))}
                                                                                    </Box>
                                                                                );
                                                                            }

                                                                            if (!lic.licenseFileUrl) return null;
                                                                            return (
                                                                                <Box key={index} sx={{ mt: 2, p: 2.5, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', gap: 2, maxWidth: '400px' }}>
                                                                                    {lic.licenseFilePreview ? (
                                                                                        <Box
                                                                                            sx={{
                                                                                                width: 64,
                                                                                                height: 64,
                                                                                                borderRadius: 1,
                                                                                                overflow: 'hidden',
                                                                                                border: '1px solid #e5e7eb',
                                                                                                bgcolor: 'white',
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center'
                                                                                            }}
                                                                                        >
                                                                                            <img
                                                                                                src={lic.licenseFilePreview}
                                                                                                alt="License preview"
                                                                                                style={{
                                                                                                    width: '100%',
                                                                                                    height: '100%',
                                                                                                    objectFit: 'contain'
                                                                                                }}
                                                                                            />
                                                                                        </Box>
                                                                                    ) : (
                                                                                        <Box
                                                                                            sx={{
                                                                                                width: 64,
                                                                                                height: 64,
                                                                                                borderRadius: 1,
                                                                                                bgcolor: '#fee2e2',
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center'
                                                                                            }}
                                                                                        >
                                                                                            <PictureAsPdf sx={{ fontSize: 32, color: '#dc2626' }} />
                                                                                        </Box>
                                                                                    )}
                                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.800', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                                            {lic.licenseFileName || `License Document ${index + 1}`}
                                                                                        </Typography>
                                                                                        <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mt: 0.5 }}>
                                                                                            Additional License Document
                                                                                        </Typography>
                                                                                    </Box>
                                                                                    <Box>
                                                                                        <Button
                                                                                            variant="text"
                                                                                            size="small"
                                                                                            onClick={() => {
                                                                                                if (lic.licenseFileUrl) {
                                                                                                    window.open(lic.licenseFileUrl, '_blank', 'noopener,noreferrer');
                                                                                                }
                                                                                            }}
                                                                                            sx={{ textTransform: 'none', color: primaryColor, fontWeight: 600, minWidth: 'auto', p: 0.5, mr: 1 }}
                                                                                        >
                                                                                            View
                                                                                        </Button>
                                                                                        <Button
                                                                                            variant="text"
                                                                                            size="small"
                                                                                            color="error"
                                                                                            onClick={(e) => handleRemoveAdditionalLicenseFile(index, e)}
                                                                                            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: 0.5 }}
                                                                                        >
                                                                                            Remove
                                                                                        </Button>
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                </Fade>
                                                            )}


                                                            {/* How Do You Provide Therapy Services? */}
                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '500ms' }}
                                                            >
                                                                <Box id="services-provided-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        How Do You Provide Healthcare Service? <span style={{ color: '#d32f2f' }}>*</span>
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!servicesError}>
                                                                        <Select
                                                                            multiple
                                                                            value={Array.isArray(onboardingData.services) ? onboardingData.services : []}
                                                                            onChange={handleOnboardingMultiSelectChange(
                                                                                'services'
                                                                            )}
                                                                            open={providedServicesSelectOpen}
                                                                            onOpen={() => {
                                                                                if (!isDeletingChip) {
                                                                                    setProvidedServicesSelectOpen(true)
                                                                                }
                                                                            }}
                                                                            onClose={() =>
                                                                                setProvidedServicesSelectOpen(false)
                                                                            }
                                                                            displayEmpty
                                                                            disabled={isLoadingProvidedServices}
                                                                            sx={{
                                                                                borderRadius: 2,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                    borderWidth: 2,
                                                                                },
                                                                            }}
                                                                            renderValue={selected => {
                                                                                const selectedValues = Array.isArray(selected) ? (selected as string[]) : (selected ? [selected as string] : [])
                                                                                if (
                                                                                    selectedValues.length === 0
                                                                                ) {
                                                                                    return (
                                                                                        <em style={{ color: 'grey.400' }}>
                                                                                            Select service type
                                                                                        </em>
                                                                                    )
                                                                                }
                                                                                return (
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: 'flex',
                                                                                            flexWrap: 'wrap',
                                                                                            gap: 0.5,
                                                                                        }}
                                                                                    >
                                                                                        {selectedValues.map(
                                                                                            value => {
                                                                                                return (
                                                                                                    <Chip
                                                                                                        key={value}
                                                                                                        label={value}
                                                                                                        size="small"
                                                                                                        onDelete={e => {
                                                                                                            e.preventDefault()
                                                                                                            e.stopPropagation()
                                                                                                            handleRemoveChip(
                                                                                                                'services',
                                                                                                                value
                                                                                                            )
                                                                                                        }}
                                                                                                        onMouseDown={e => {
                                                                                                            // Only prevent if clicking on the chip itself, not the delete icon
                                                                                                            if (
                                                                                                                e.target ===
                                                                                                                e.currentTarget ||
                                                                                                                (
                                                                                                                    e.target as HTMLElement
                                                                                                                ).closest(
                                                                                                                    '.MuiChip-deleteIcon'
                                                                                                                )
                                                                                                            ) {
                                                                                                                e.preventDefault()
                                                                                                                e.stopPropagation()
                                                                                                            }
                                                                                                        }}
                                                                                                        onClick={e => {
                                                                                                            e.preventDefault()
                                                                                                            e.stopPropagation()
                                                                                                        }}
                                                                                                        sx={{
                                                                                                            bgcolor: '#efebfdff',
                                                                                                            color: '#7A6FA1',
                                                                                                            cursor: 'default',
                                                                                                            '& .MuiChip-deleteIcon': {
                                                                                                                color: '#7A6FA1',
                                                                                                                cursor: 'pointer',
                                                                                                                '&:hover': {
                                                                                                                    color: '#7A6FA1',
                                                                                                                },
                                                                                                            },
                                                                                                        }}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        )}
                                                                                    </Box>
                                                                                )
                                                                            }}
                                                                        >
                                                                            {isLoadingProvidedServices ? (
                                                                                <MenuItem disabled>
                                                                                    <CircularProgress
                                                                                        size={20}
                                                                                        sx={{ mr: 1 }}
                                                                                    />
                                                                                    Loading services...
                                                                                </MenuItem>
                                                                            ) : providedServices.length === 0 ? (
                                                                                <MenuItem disabled>
                                                                                    No services available.
                                                                                </MenuItem>
                                                                            ) : (
                                                                                providedServices.map(service => (
                                                                                    <MenuItem
                                                                                        key={service.id}
                                                                                        value={service.name}
                                                                                    >
                                                                                        {service.name}
                                                                                    </MenuItem>
                                                                                ))
                                                                            )}
                                                                        </Select>
                                                                        {servicesError && (
                                                                            <FormHelperText>{servicesError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>
                                                        </Grid>

                                                        {/* Right Column */}
                                                        <Grid item xs={12} md={6}>
                                                            {/* Insurance Accepted */}
                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '800ms' }}
                                                            >
                                                                <Box id="insurance-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Insurance Accepted <span style={{ color: '#d32f2f' }}>*</span>
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!insuranceError}>
                                                                        <Autocomplete
                                                                            multiple
                                                                            options={payerSearchOptions}
                                                                            value={selectedPayers}
                                                                            inputValue={payerSearchInput}
                                                                            onChange={handleInsuranceAcceptedChange}
                                                                            onInputChange={(
                                                                                event,
                                                                                newInputValue
                                                                            ) => {
                                                                                console.log('onInputChange newInputValue:', newInputValue);
                                                                                setPayerSearchInput(newInputValue)
                                                                            }}
                                                                            getOptionLabel={option => option.payer_name}
                                                                            isOptionEqualToValue={(option, value) =>
                                                                                option._id === value._id
                                                                            }
                                                                            loading={isSearchingPayers}
                                                                            filterOptions={(options, state) => {
                                                                                const query = state.inputValue.trim().toLowerCase();
                                                                                console.log('Autocomplete filterOptions query:', query, 'options count:', options.length);
                                                                                if (!query) return options;
                                                                                const filtered = options.filter(option =>
                                                                                    option.payer_name.toLowerCase().startsWith(query)
                                                                                );
                                                                                console.log('Filtered options:', filtered);
                                                                                return filtered;
                                                                            }}
                                                                            noOptionsText={
                                                                                payerSearchInput.trim().length === 0
                                                                                    ? 'Start typing to search for insurance providers'
                                                                                    : 'No insurance providers found'
                                                                            }
                                                                            onClose={(event, reason) => {
                                                                                // Clear input when an option is selected
                                                                                if (reason === 'selectOption') {
                                                                                    setPayerSearchInput('')
                                                                                }
                                                                            }}
                                                                            renderInput={params => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Search and select insurance providers"
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 2,
                                                                                            '& fieldset': {
                                                                                                borderColor: '#7A6FA1',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: '#7A6FA1',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: '#7A6FA1',
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {isSearchingPayers ? (
                                                                                                    <CircularProgress
                                                                                                        color="inherit"
                                                                                                        size={20}
                                                                                                    />
                                                                                                ) : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        ),
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            renderTags={(value, getTagProps) =>
                                                                                value.map((option, index) => (
                                                                                    <Chip
                                                                                        {...getTagProps({ index })}
                                                                                        key={option._id}
                                                                                        label={option.payer_name}
                                                                                        size="small"
                                                                                        sx={{
                                                                                            bgcolor: '#efebfdff',
                                                                                            color: '#7A6FA1',
                                                                                            cursor: 'default',
                                                                                            '& .MuiChip-deleteIcon': {
                                                                                                color: '#7A6FA1',
                                                                                                cursor: 'pointer',
                                                                                                '&:hover': {
                                                                                                    color: '#7A6FA1',
                                                                                                },
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                ))
                                                                            }
                                                                        />
                                                                        {insuranceError && (
                                                                            <FormHelperText>{insuranceError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>
                                                        </Grid>

                                                        {/* Years of Experience */}
                                                        <Grid item xs={12} md={6}>
                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '900ms' }}
                                                            >
                                                                <Box id="years-experience-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Year of Establishment (optional)
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!yearsOfExperienceError}>
                                                                        <TextField
                                                                            value={onboardingData.yearsOfExperience}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    yearsOfExperience: val,
                                                                                }))
                                                                                setYearsOfExperienceError('')
                                                                            }}
                                                                            placeholder="Enter year (e.g. 2018)"
                                                                            error={!!yearsOfExperienceError}
                                                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                                            onKeyDown={(e) => {
                                                                                if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                                                                                    e.preventDefault()
                                                                                }
                                                                            }}
                                                                            sx={{
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    borderRadius: 2,
                                                                                    bgcolor: 'white',
                                                                                    height: '48px',
                                                                                    '& fieldset': {
                                                                                        borderColor: '#D5D1E1',
                                                                                    },
                                                                                    '&:hover fieldset': {
                                                                                        borderColor: '#B8B3C8',
                                                                                    },
                                                                                    '&.Mui-focused fieldset': {
                                                                                        borderColor: '#7A6FA1',
                                                                                        borderWidth: 2,
                                                                                    },
                                                                                },
                                                                                '& .MuiOutlinedInput-input': {
                                                                                    fontSize: '0.875rem',
                                                                                    color: '#4E4861',
                                                                                    fontWeight: 500,
                                                                                    pl: 1,
                                                                                }
                                                                            }}
                                                                        />
                                                                        {yearsOfExperienceError && (
                                                                            <FormHelperText>{yearsOfExperienceError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container spacing={3}>

                                                        <Grid item xs={12}>

                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '900ms' }}
                                                            >
                                                                <Box id="no-of-providers-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Number of Providers <span style={{ color: '#d32f2f' }}>*</span>
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!noOfProviderError}>
                                                                        <TextField
                                                                            value={onboardingData.noOfProvider}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value.replace(/\D/g, '').slice(0, 5)
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    noOfProvider: val,
                                                                                }))
                                                                                setNoOfProviderError('')
                                                                            }}
                                                                            placeholder="Enter number of providers"
                                                                            error={!!noOfProviderError}
                                                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                                            onKeyDown={(e) => {
                                                                                if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                                                                                    e.preventDefault()
                                                                                }
                                                                            }}
                                                                            sx={{
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    borderRadius: 2,
                                                                                    bgcolor: 'white',
                                                                                    height: '48px',
                                                                                    '& fieldset': {
                                                                                        borderColor: '#D5D1E1',
                                                                                    },
                                                                                    '&:hover fieldset': {
                                                                                        borderColor: '#B8B3C8',
                                                                                    },
                                                                                    '&.Mui-focused fieldset': {
                                                                                        borderColor: '#7A6FA1',
                                                                                        borderWidth: 2,
                                                                                    },
                                                                                },
                                                                                '& .MuiOutlinedInput-input': {
                                                                                    fontSize: '0.875rem',
                                                                                    color: '#4E4861',
                                                                                    fontWeight: 500,
                                                                                    pl: 1,
                                                                                }
                                                                            }}
                                                                        />
                                                                        {noOfProviderError && (
                                                                            <FormHelperText>{noOfProviderError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>

                                                        </Grid>
                                                    </Grid>

                                                    {/* Board Certification Section */}
                                                    {/* <Box id="board-certification-section" sx={{ mt: 4 }}>
                                                        <Divider sx={{ mb: 3 }} />
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                color: 'grey.800',
                                                                mb: 2.5,
                                                                fontWeight: 600,
                                                                fontSize: '1rem',
                                                            }}
                                                        >
                                                            Board Certification (Optional)
                                                        </Typography>
                                                        <Grid container spacing={3}>
                                                            <Grid item xs={12} md={6}>
                                                                <Fade
                                                                    in
                                                                    timeout={800}
                                                                    style={{ transitionDelay: '200ms' }}
                                                                >
                                                                    <Box sx={{ mb: 3 }} id="board-number-field">
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Board Certification No
                                                                        </Typography>
                                                                        <TextField
                                                                            fullWidth
                                                                            value={
                                                                                onboardingData.boardCertificationNo
                                                                            }
                                                                            onChange={e => {
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    boardCertificationNo: e.target.value,
                                                                                }))
                                                                                if (boardCertificationNoError) {
                                                                                    setBoardCertificationNoError('')
                                                                                }
                                                                            }}
                                                                            placeholder="e.g. 3342434234"
                                                                            error={!!boardCertificationNoError}
                                                                            helperText={boardCertificationNoError}
                                                                            sx={{
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    bgcolor: 'white',
                                                                                    borderRadius: 2,
                                                                                    '& fieldset': {
                                                                                        borderColor: 'grey.300',
                                                                                    },
                                                                                    '&:hover fieldset': {
                                                                                        borderColor: 'grey.400',
                                                                                    },
                                                                                    '&.Mui-focused fieldset': {
                                                                                        borderColor: primaryColor,
                                                                                        borderWidth: 2,
                                                                                    },
                                                                                },
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </Fade>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Fade
                                                                    in
                                                                    timeout={800}
                                                                    style={{ transitionDelay: '300ms' }}
                                                                >
                                                                    <Box sx={{ mb: 3 }} id="board-name-field">
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Board Name
                                                                        </Typography>
                                                                        <Autocomplete
                                                                            options={boardNameOptions}
                                                                            value={
                                                                                boardNameOptions.find(
                                                                                    o =>
                                                                                        o._id === onboardingData.boardName
                                                                                ) || null
                                                                            }
                                                                            inputValue={boardNameSearch}
                                                                            onInputChange={(_, newValue) => {
                                                                                setBoardNameSearch(newValue)
                                                                            }}
                                                                            onChange={(_, newValue) => {
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    boardName: newValue?._id || '',
                                                                                    boardSpeciality: '',
                                                                                    boardSubSpeciality: '',
                                                                                }))
                                                                                setBoardSpecialityOptions([])
                                                                                setBoardSubSpecialityOptions([])
                                                                                if (boardNameError) {
                                                                                    setBoardNameError('')
                                                                                }
                                                                            }}
                                                                            getOptionLabel={option => option.name}
                                                                            isOptionEqualToValue={(opt, val) =>
                                                                                opt._id === val._id
                                                                            }
                                                                            loading={isSearchingBoardName}
                                                                            filterOptions={x => x}
                                                                            noOptionsText={
                                                                                boardNameSearch.trim().length === 0
                                                                                    ? 'Start typing to search'
                                                                                    : 'No options found'
                                                                            }
                                                                            renderInput={params => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Search and select board"
                                                                                    error={!!boardNameError}
                                                                                    helperText={boardNameError}
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 2,
                                                                                            bgcolor: 'white',
                                                                                            '& fieldset': {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {isSearchingBoardName ? (
                                                                                                    <CircularProgress
                                                                                                        color="inherit"
                                                                                                        size={20}
                                                                                                    />
                                                                                                ) : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        ),
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                </Fade>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Fade
                                                                    in
                                                                    timeout={800}
                                                                    style={{ transitionDelay: '400ms' }}
                                                                >
                                                                    <Box sx={{ mb: 3 }} id="board-specialty-field">
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Board Specialty
                                                                        </Typography>
                                                                        <Autocomplete
                                                                            disabled={!onboardingData.boardName}
                                                                            options={boardSpecialityOptions}
                                                                            value={
                                                                                boardSpecialityOptions.find(
                                                                                    o =>
                                                                                        o._id ===
                                                                                        onboardingData.boardSpeciality
                                                                                ) || null
                                                                            }
                                                                            inputValue={boardSpecialitySearch}
                                                                            onInputChange={(_, newValue) => {
                                                                                setBoardSpecialitySearch(newValue)
                                                                            }}
                                                                            onChange={(_, newValue) => {
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    boardSpeciality: newValue?._id || '',
                                                                                    boardSubSpeciality: '',
                                                                                }))
                                                                                setBoardSubSpecialityOptions([])
                                                                                if (boardSpecialityError) {
                                                                                    setBoardSpecialityError('')
                                                                                }
                                                                            }}
                                                                            getOptionLabel={option => option.name}
                                                                            isOptionEqualToValue={(opt, val) =>
                                                                                opt._id === val._id
                                                                            }
                                                                            loading={isSearchingBoardSpeciality}
                                                                            filterOptions={x => x}
                                                                            noOptionsText={
                                                                                boardSpecialitySearch.trim().length ===
                                                                                    0
                                                                                    ? 'Start typing to search'
                                                                                    : 'No options found'
                                                                            }
                                                                            renderInput={params => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Search and select speciality"
                                                                                    error={!!boardSpecialityError}
                                                                                    helperText={boardSpecialityError}
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 2,
                                                                                            bgcolor: 'white',
                                                                                            '& fieldset': {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {isSearchingBoardSpeciality ? (
                                                                                                    <CircularProgress
                                                                                                        color="inherit"
                                                                                                        size={20}
                                                                                                    />
                                                                                                ) : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        ),
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                </Fade>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Fade
                                                                    in
                                                                    timeout={800}
                                                                    style={{ transitionDelay: '500ms' }}
                                                                >
                                                                    <Box sx={{ mb: 3 }} id="board-subspecialty-field">
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Board Sub-Specialty
                                                                        </Typography>
                                                                        <Autocomplete
                                                                            disabled={!onboardingData.boardSpeciality}
                                                                            options={boardSubSpecialityOptions}
                                                                            value={
                                                                                boardSubSpecialityOptions.find(
                                                                                    o =>
                                                                                        o._id ===
                                                                                        onboardingData.boardSubSpeciality
                                                                                ) || null
                                                                            }
                                                                            inputValue={boardSubSpecialitySearch}
                                                                            onInputChange={(_, newValue) => {
                                                                                setBoardSubSpecialitySearch(newValue)
                                                                            }}
                                                                            onChange={(_, newValue) => {
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    boardSubSpeciality:
                                                                                        newValue?._id || '',
                                                                                }))
                                                                            }}
                                                                            getOptionLabel={option => option.name}
                                                                            isOptionEqualToValue={(opt, val) =>
                                                                                opt._id === val._id
                                                                            }
                                                                            loading={isSearchingBoardSubSpeciality}
                                                                            filterOptions={x => x}
                                                                            noOptionsText={
                                                                                boardSubSpecialitySearch.trim()
                                                                                    .length === 0
                                                                                    ? 'Start typing to search'
                                                                                    : 'No options found'
                                                                            }
                                                                            renderInput={params => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Search and select sub-speciality"
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 2,
                                                                                            bgcolor: 'white',
                                                                                            '& fieldset': {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {isSearchingBoardSubSpeciality ? (
                                                                                                    <CircularProgress
                                                                                                        color="inherit"
                                                                                                        size={20}
                                                                                                    />
                                                                                                ) : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        ),
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                </Fade>
                                                            </Grid>
                                                        </Grid>
                                                    </Box> */}

                                                    {/* Billing Phone & Address */}
                                                    <Divider sx={{ my: 4 }} />
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            color: 'grey.800',
                                                            mb: 3,
                                                            fontWeight: 600,
                                                            fontSize: '1.125rem',
                                                        }}
                                                    >
                                                        Billing Phone & Address
                                                    </Typography>
                                                    <Grid container spacing={3} sx={{ mb: 4 }}>
                                                        {/* Billing NPI */}
                                                        <Grid item xs={12} md={6}>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1.5,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Billing NPI *
                                                            </Typography>
                                                            <TextField
                                                                id="billing-npi-field"
                                                                fullWidth
                                                                placeholder="Enter 10 digit NPI number"
                                                                value={formData.billingNpi || ''}
                                                                onChange={e => {
                                                                    const val = e.target.value
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        billingNpi: val,
                                                                        npiOrganization: val,
                                                                    }))
                                                                    // Reset verification when billing NPI changes
                                                                    setIsBillingNpiVerified(false)
                                                                    setBillingNpiVerificationError('')
                                                                    setIsNpiVerified(false)
                                                                    setNpiVerificationError('')
                                                                }}
                                                                error={!!billingNpiVerificationError}
                                                                helperText={billingNpiVerificationError}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            {isBillingNpiVerified ? (
                                                                                <Box
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 0.5,
                                                                                        mr: 1,
                                                                                    }}
                                                                                >
                                                                                    <CheckCircle
                                                                                        sx={{
                                                                                            color: 'success.main',
                                                                                            fontSize: 20,
                                                                                        }}
                                                                                    />
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: 'success.main',
                                                                                            fontWeight: 500,
                                                                                        }}
                                                                                    >
                                                                                        Verified
                                                                                    </Typography>
                                                                                </Box>
                                                                            ) : (
                                                                                <Button
                                                                                    variant="contained"
                                                                                    size="small"
                                                                                    onClick={handleVerifyBillingNpi}
                                                                                    disabled={
                                                                                        isBillingNpiVerifying ||
                                                                                        !formData.billingNpi.trim()
                                                                                    }
                                                                                    sx={{
                                                                                        textTransform: 'none',
                                                                                        borderColor: primaryColor,
                                                                                        bgcolor: primaryColor,
                                                                                        color: 'white',
                                                                                        minWidth: 'auto',
                                                                                        px: 1.5,
                                                                                        py: 0.5,
                                                                                        fontSize: '0.75rem',
                                                                                        '&:hover': {
                                                                                            borderColor: darken(
                                                                                                primaryColor,
                                                                                                0.2
                                                                                            ),
                                                                                            bgcolor: darken(
                                                                                                primaryColor,
                                                                                                0.2
                                                                                            ),
                                                                                            color: 'white',
                                                                                        },
                                                                                        '&.Mui-disabled': {
                                                                                            borderColor: 'grey.300',
                                                                                            color: 'grey.400',
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    {isBillingNpiVerifying ? (
                                                                                        <>
                                                                                            <CircularProgress
                                                                                                size={14}
                                                                                                sx={{ mr: 0.5 }}
                                                                                            />
                                                                                            Verifying
                                                                                        </>
                                                                                    ) : (
                                                                                        'Verify'
                                                                                    )}
                                                                                </Button>
                                                                            )}
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        '& fieldset': {
                                                                            borderColor: 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey.400',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: primaryColor,
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>

                                                        {/* Billing Phone Number */}
                                                        <Grid item xs={12} md={6}>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1.5,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Billing Phone Number *
                                                            </Typography>
                                                            <PhoneInput
                                                                id="billing-phone-field"
                                                                fullWidth
                                                                value={formData.billingPhone}
                                                                onChange={value => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        billingPhone: value,
                                                                    }))
                                                                    if (isBillingPhoneTouched) {
                                                                        setBillingPhoneError(
                                                                            getBillingPhoneValidationMessage(
                                                                                value
                                                                            )
                                                                        )
                                                                    }
                                                                }}
                                                                onBlur={(
                                                                    event: React.FocusEvent<HTMLInputElement>
                                                                ) => {
                                                                    setIsBillingPhoneTouched(true)
                                                                    setBillingPhoneError(
                                                                        getBillingPhoneValidationMessage(
                                                                            event.target.value
                                                                        )
                                                                    )
                                                                }}
                                                                onValidationChange={isValid => {
                                                                    if (!isBillingPhoneTouched) {
                                                                        return
                                                                    }
                                                                    setBillingPhoneError(
                                                                        isValid
                                                                            ? ''
                                                                            : getBillingPhoneValidationMessage(
                                                                                formData.billingPhone || ''
                                                                            )
                                                                    )
                                                                }}
                                                                required
                                                                error={!!billingPhoneError}
                                                                helperText={billingPhoneError}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        '& fieldset': {
                                                                            borderColor: billingPhoneError
                                                                                ? 'error.main'
                                                                                : 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: billingPhoneError
                                                                                ? 'error.main'
                                                                                : 'grey.400',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: billingPhoneError
                                                                                ? 'error.main'
                                                                                : primaryColor,
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>

                                                    {/* Billing Address */}
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            color: 'grey.800',
                                                            mb: 3,
                                                            fontWeight: 600,
                                                            fontSize: '1.125rem',
                                                        }}
                                                    >
                                                        Billing Address
                                                    </Typography>

                                                    <Grid container spacing={3} sx={{ mb: 4 }}>
                                                        <Grid item xs={12} id="billing-address-field">
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1.5,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Billing Address *
                                                            </Typography>
                                                            <AddressAutocomplete
                                                                sx={{
                                                                    margin: 'normal',
                                                                    fullWidth: true,
                                                                    fontSize: '1rem',
                                                                    '& .MuiInputBase-root': {
                                                                        height: 56,
                                                                    },
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: '16px !important' as any,
                                                                        bgcolor: 'white',
                                                                        '& fieldset': {
                                                                            borderColor: 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey.400',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: primaryColor,
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: '1.5rem',
                                                                        fontSize: '1rem',
                                                                    },
                                                                }}
                                                                value={formData.billingAddressLine}
                                                                onChange={(address, components) => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        billingAddressLine: address,
                                                                        // Only update city, state, zip, country if components are provided and have values
                                                                        // Don't clear them when address is cleared (components will have empty strings)
                                                                        ...(components && {
                                                                            ...(components.city && {
                                                                                billingCity: components.city,
                                                                            }),
                                                                            ...(components.state && {
                                                                                billingState: components.state,
                                                                            }),
                                                                            ...((components.zipCode ||
                                                                                components.postalCode) && {
                                                                                billingZipCode:
                                                                                    components.zipCode ||
                                                                                    components.postalCode,
                                                                            }),
                                                                            ...(components.country && {
                                                                                billingCountry: components.country,
                                                                            }),
                                                                        }),
                                                                    }))
                                                                    clearNpiFieldError('billingAddressLine')
                                                                    if (components?.city) {
                                                                        clearNpiFieldError('billingCity')
                                                                    }
                                                                    if (components?.state) {
                                                                        clearNpiFieldError('billingState')
                                                                    }
                                                                    if (
                                                                        components?.zipCode ||
                                                                        components?.postalCode
                                                                    ) {
                                                                        clearNpiFieldError('billingZipCode')
                                                                    }
                                                                    if (components?.country) {
                                                                        clearNpiFieldError('billingCountry')
                                                                    }
                                                                }}
                                                                placeholder="Start typing billing address..."
                                                                addressType="billingAddress"
                                                                required
                                                                error={!!npiStepErrors.billingAddressLine}
                                                                helperText={
                                                                    npiStepErrors.billingAddressLine
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={6}
                                                            id="billing-city-field"
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 0.5,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                City *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                placeholder="Enter billing city"
                                                                value={formData.billingCity}
                                                                onChange={e => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        billingCity: e.target.value,
                                                                    }))
                                                                    clearNpiFieldError('billingCity')
                                                                }}
                                                                error={!!npiStepErrors.billingCity}
                                                                helperText={npiStepErrors.billingCity}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        '& fieldset': {
                                                                            borderColor: 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey.400',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={6}
                                                            id="billing-country-container"
                                                        >
                                                            <LocationSelector
                                                                renderField="country"
                                                                country={formData.billingCountry}
                                                                state={formData.billingState}
                                                                onCountryChange={name => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        billingCountry: name,
                                                                        billingState: '',
                                                                    }))
                                                                    clearNpiFieldError('billingCountry')
                                                                }}
                                                                onStateChange={name => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        billingState: name,
                                                                    }))
                                                                    clearNpiFieldError('billingState')
                                                                }}
                                                                countryError={npiStepErrors.billingCountry}
                                                                accentColor={primaryColor}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={6}
                                                            id="billing-state-container"
                                                        >
                                                            <LocationSelector
                                                                renderField="state"
                                                                country={formData.billingCountry}
                                                                state={formData.billingState}
                                                                onCountryChange={name => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        billingCountry: name,
                                                                        billingState: '',
                                                                    }))
                                                                    clearNpiFieldError('billingCountry')
                                                                }}
                                                                onStateChange={name => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        billingState: name,
                                                                    }))
                                                                    clearNpiFieldError('billingState')
                                                                }}
                                                                stateError={npiStepErrors.billingState}
                                                                accentColor={primaryColor}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={6}
                                                            id="billing-zip-field"
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 0.5,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Zip code *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                placeholder="Enter billing zip code"
                                                                value={formData.billingZipCode}
                                                                onChange={e => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        billingZipCode: e.target.value,
                                                                    }))
                                                                    clearNpiFieldError('billingZipCode')
                                                                }}
                                                                error={!!npiStepErrors.billingZipCode}
                                                                helperText={npiStepErrors.billingZipCode}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        '& fieldset': {
                                                                            borderColor: 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey.400',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: primaryColor,
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </>
                                            )}



                                            {/* Step 3: NPI & License verification */}
                                            {currentStep === 2 && (
                                                <Box >
                                                    {paymentError && (
                                                        <Alert
                                                            severity="error"
                                                            sx={{ mb: 3, borderRadius: 2 }}
                                                            onClose={() => setPaymentError(null)}
                                                        >
                                                            {paymentError}
                                                        </Alert>
                                                    )}
                                                    <Fade
                                                        in
                                                        timeout={800}
                                                        style={{ transitionDelay: '200ms' }}
                                                    >
                                                        <Box sx={{ mb: 4 }}>
                                                            {/* How are you enrolled to bill payers? */}
                                                            {/* <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: 'grey.800',
                                                                    mb: 3,
                                                                    fontWeight: 600,
                                                                    fontSize: '1.125rem',
                                                                }}
                                                            >
                                                                How are you enrolled to bill payers?
                                                            </Typography> */}

                                                            {/* <FormControl
                                                                component="fieldset"
                                                                sx={{
                                                                    mb: 4,
                                                                }}
                                                            >
                                                                <RadioGroup
                                                                    value={formData.billingType || 'individual'}
                                                                    onChange={e => {
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            billingType: e.target.value,
                                                                            taxId: '',
                                                                            companyName: '',
                                                                            ssn: '',
                                                                            billingNpi: '',
                                                                        }))
                                                                        // Clear errors when billing type changes
                                                                        setCompanyNameError('')
                                                                        setTaxIdError('')
                                                                        setSsnError('')
                                                                        // Clear errors when billing type changes
                                                                        setTaxIdError('')
                                                                        setSsnError('')
                                                                        setBillingNpiVerificationError('')
                                                                        setIsBillingNpiVerified(false)
                                                                    }}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'row',
                                                                        gap: 2,
                                                                    }}
                                                                >
                                                                    <FormControlLabel
                                                                        value="individual"
                                                                        control={
                                                                            <Radio
                                                                                sx={{
                                                                                    color: primaryColor,
                                                                                    '&.Mui-checked': {
                                                                                        color: primaryColor,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        }
                                                                        label="I bill as an individual provider"
                                                                        sx={{
                                                                            '& .MuiFormControlLabel-label': {
                                                                                fontSize: '0.875rem',
                                                                                fontWeight: 500,
                                                                                color: 'grey.700',
                                                                            },
                                                                        }}
                                                                    />
                                                                    <FormControlLabel
                                                                        value="business"
                                                                        control={
                                                                            <Radio
                                                                                sx={{
                                                                                    color: primaryColor,
                                                                                    '&.Mui-checked': {
                                                                                        color: primaryColor,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        }
                                                                        label="I bill through a company (Business Entity)"
                                                                        sx={{
                                                                            '& .MuiFormControlLabel-label': {
                                                                                fontSize: '0.875rem',
                                                                                fontWeight: 500,
                                                                                color: 'grey.700',
                                                                            },
                                                                        }}
                                                                    />
                                                                </RadioGroup>
                                                            </FormControl> */}

                                                            <Grid item xs={6} mb={3}>
                                                                <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                    NPI (Organization Level 2) *
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    placeholder="10-digit NPI number"
                                                                    value={formData.npiOrganization}
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                        const val = e.target.value
                                                                        setFormData(prev => ({ ...prev, npiOrganization: val }))
                                                                        if (npiOrganizationError) setNpiOrganizationError('')
                                                                        if (npiOrganizationDocError) setNpiOrganizationDocError('')
                                                                        setIsNpiVerified(false)
                                                                        setNpiVerificationError('')
                                                                        setNpiStepErrors(prev => ({ ...prev, npiOrganization: '', npiVerification: '' }))
                                                                    }}
                                                                    required
                                                                    error={!!npiOrganizationError || !!npiOrganizationDocError || !!npiVerificationError || !!npiStepErrors.npiOrganization || !!npiStepErrors.npiVerification}
                                                                    helperText={npiOrganizationError || npiOrganizationDocError || npiVerificationError || npiStepErrors.npiOrganization || npiStepErrors.npiVerification}
                                                                    sx={accountFieldSx}
                                                                    InputProps={{
                                                                        endAdornment: (
                                                                            <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                                <Button
                                                                                    size="small"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        e.preventDefault();
                                                                                        handleVerifyNpi();
                                                                                    }}
                                                                                    disabled={isNpiVerifying || !formData.npiOrganization || isNpiVerified}
                                                                                    sx={{
                                                                                        textTransform: 'none',
                                                                                        bgcolor: isNpiVerified ? '#10B981' : '#7A6FA1',
                                                                                        color: 'white',
                                                                                        fontWeight: 600,
                                                                                        mr: 0.5,
                                                                                        height: 32,
                                                                                        borderRadius: 1.5,
                                                                                        '&:hover': {
                                                                                            bgcolor: isNpiVerified ? '#0D9488' : '#574F75',
                                                                                        },
                                                                                        '&:disabled': {
                                                                                            bgcolor: isNpiVerified ? '#10B981' : '#e2e8f0',
                                                                                            color: isNpiVerified ? 'white' : '#94a3b8',
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {isNpiVerifying ? 'Verifying...' : isNpiVerified ? '✓ Verified' : 'Verify'}
                                                                                </Button>
                                                                                {uploadingDocumentField === 'npiOrganizationDoc' ? (
                                                                                    <CircularProgress size={20} />
                                                                                ) : formData.npiOrganizationDocUrl ? (
                                                                                    <Chip
                                                                                        label={formData.npiOrganizationDocName || 'NPI Certificate'}
                                                                                        size="small"
                                                                                        onClick={() => {
                                                                                            if (formData.npiOrganizationDocUrl) {
                                                                                                window.open(formData.npiOrganizationDocUrl, '_blank', 'noopener,noreferrer');
                                                                                            }
                                                                                        }}
                                                                                        onDelete={() => {
                                                                                            setFormData(prev => ({
                                                                                                ...prev,
                                                                                                npiOrganizationDocUrl: '',
                                                                                                npiOrganizationDocName: '',
                                                                                            }))
                                                                                        }}
                                                                                        sx={{
                                                                                            cursor: 'pointer', // Show hand cursor on hover to indicate it's clickable
                                                                                            maxWidth: 120,
                                                                                            textOverflow: 'ellipsis',
                                                                                            overflow: 'hidden',
                                                                                            whiteSpace: 'nowrap',
                                                                                            bgcolor: '#EAE7F2',
                                                                                            color: '#574F75',
                                                                                            fontWeight: 500,
                                                                                            border: '1px solid #D1C9E7',
                                                                                            '& .MuiChip-deleteIcon': {
                                                                                                color: '#7A6FA1',
                                                                                                '&:hover': {
                                                                                                    color: '#574F75'
                                                                                                }
                                                                                            }
                                                                                        }}
                                                                                    />

                                                                                ) : (
                                                                                    <>
                                                                                        <input
                                                                                            type="file"
                                                                                            id="npiOrganizationDoc-file-input"
                                                                                            style={{ display: 'none' }}
                                                                                            accept=".pdf,image/*"
                                                                                            onChange={(e) => {
                                                                                                const file = e.target.files?.[0];
                                                                                                if (file) {
                                                                                                    handleDocumentUpload('npiOrganizationDoc')(file);
                                                                                                }
                                                                                                e.target.value = '';
                                                                                            }}
                                                                                        />
                                                                                        <Tooltip title="Click the upload icon on the right to upload the NPI document" placement="top">
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    e.preventDefault();
                                                                                                    document.getElementById('npiOrganizationDoc-file-input')?.click();
                                                                                                }}

                                                                                                sx={uploadBtnSx}
                                                                                            >
                                                                                                <CloudUpload />

                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    </>
                                                                                )}
                                                                            </InputAdornment>
                                                                        )
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid item xs={12} mb={3}>
                                                                <Grid container spacing={3}>
                                                                    <Grid item xs={12} md={4}>
                                                                        <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                            Nursing NPI
                                                                        </Typography>
                                                                        <TextField
                                                                            fullWidth
                                                                            placeholder="Nursing NPI"
                                                                            value={formData.nursingNpi || ''}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                setFormData(prev => ({ ...prev, nursingNpi: val }));
                                                                            }}
                                                                            sx={accountFieldSx}
                                                                            InputProps={{
                                                                                endAdornment: (
                                                                                    <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                                        {uploadingDocumentField === 'nursingNpiDoc' ? (
                                                                                            <CircularProgress size={20} />
                                                                                        ) : formData.nursingNpiDocUrl ? (
                                                                                            <Chip
                                                                                                label={formData.nursingNpiDocName || 'Nursing NPI Certificate'}
                                                                                                size="small"
                                                                                                onClick={() => {
                                                                                                    if (formData.nursingNpiDocUrl) {
                                                                                                        window.open(formData.nursingNpiDocUrl, '_blank', 'noopener,noreferrer');
                                                                                                    }
                                                                                                }}
                                                                                                onDelete={() => {
                                                                                                    setFormData(prev => ({
                                                                                                        ...prev,
                                                                                                        nursingNpiDocUrl: '',
                                                                                                        nursingNpiDocName: '',
                                                                                                    }))
                                                                                                }}
                                                                                                sx={{
                                                                                                    cursor: 'pointer', // Show hand cursor on hover to indicate it's clickable
                                                                                                    maxWidth: 120,
                                                                                                    textOverflow: 'ellipsis',
                                                                                                    overflow: 'hidden',
                                                                                                    whiteSpace: 'nowrap',
                                                                                                    bgcolor: '#EAE7F2',
                                                                                                    color: '#574F75',
                                                                                                    fontWeight: 500,
                                                                                                    border: '1px solid #D1C9E7',
                                                                                                    '& .MuiChip-deleteIcon': {
                                                                                                        color: '#7A6FA1',
                                                                                                        '&:hover': {
                                                                                                            color: '#574F75'
                                                                                                        }
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        ) : (
                                                                                            <>
                                                                                                <input
                                                                                                    type="file"
                                                                                                    id="nursingNpiDoc-file-input"
                                                                                                    style={{ display: 'none' }}
                                                                                                    accept=".pdf,image/*"
                                                                                                    onChange={(e) => {
                                                                                                        const file = e.target.files?.[0];
                                                                                                        if (file) {
                                                                                                            handleDocumentUpload('nursingNpiDoc')(file);
                                                                                                        }
                                                                                                        e.target.value = '';
                                                                                                    }}
                                                                                                />
                                                                                                <Tooltip title="Upload Nursing NPI Certificate" placement="top">
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            e.preventDefault();
                                                                                                            document.getElementById('nursingNpiDoc-file-input')?.click();
                                                                                                        }}
                                                                                                        sx={uploadBtnSx}
                                                                                                    >
                                                                                                        <CloudUpload />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                                                                            </>
                                                                                        )}
                                                                                    </InputAdornment>
                                                                                )
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={4}>
                                                                        <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                            Therapy NPI
                                                                        </Typography>
                                                                        <TextField
                                                                            fullWidth
                                                                            placeholder="Therapy NPI"
                                                                            value={formData.therapyNpi || ''}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                setFormData(prev => ({ ...prev, therapyNpi: val }));
                                                                            }}
                                                                            sx={accountFieldSx}
                                                                            InputProps={{
                                                                                endAdornment: (
                                                                                    <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                                        {uploadingDocumentField === 'therapyNpiDoc' ? (
                                                                                            <CircularProgress size={20} />
                                                                                        ) : formData.therapyNpiDocUrl ? (
                                                                                            <Chip
                                                                                                label={formData.therapyNpiDocName || 'Therapy NPI Certificate'}
                                                                                                size="small"
                                                                                                onClick={() => {
                                                                                                    if (formData.therapyNpiDocUrl) {
                                                                                                        window.open(formData.therapyNpiDocUrl, '_blank', 'noopener,noreferrer');
                                                                                                    }
                                                                                                }}
                                                                                                onDelete={() => {
                                                                                                    setFormData(prev => ({
                                                                                                        ...prev,
                                                                                                        therapyNpiDocUrl: '',
                                                                                                        therapyNpiDocName: '',
                                                                                                    }))
                                                                                                }}
                                                                                                sx={{
                                                                                                    cursor: 'pointer', // Show hand cursor on hover to indicate it's clickable
                                                                                                    maxWidth: 120,
                                                                                                    textOverflow: 'ellipsis',
                                                                                                    overflow: 'hidden',
                                                                                                    whiteSpace: 'nowrap',
                                                                                                    bgcolor: '#EAE7F2',
                                                                                                    color: '#574F75',
                                                                                                    fontWeight: 500,
                                                                                                    border: '1px solid #D1C9E7',
                                                                                                    '& .MuiChip-deleteIcon': {
                                                                                                        color: '#7A6FA1',
                                                                                                        '&:hover': {
                                                                                                            color: '#574F75'
                                                                                                        }
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        ) : (
                                                                                            <>
                                                                                                <input
                                                                                                    type="file"
                                                                                                    id="therapyNpiDoc-file-input"
                                                                                                    style={{ display: 'none' }}
                                                                                                    accept=".pdf,image/*"
                                                                                                    onChange={(e) => {
                                                                                                        const file = e.target.files?.[0];
                                                                                                        if (file) {
                                                                                                            handleDocumentUpload('therapyNpiDoc')(file);
                                                                                                        }
                                                                                                        e.target.value = '';
                                                                                                    }}
                                                                                                />
                                                                                                <Tooltip title="Upload Therapy NPI Certificate" placement="top">
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            e.preventDefault();
                                                                                                            document.getElementById('therapyNpiDoc-file-input')?.click();
                                                                                                        }}
                                                                                                        sx={uploadBtnSx}
                                                                                                    >
                                                                                                        <CloudUpload />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                                                                            </>
                                                                                        )}
                                                                                    </InputAdornment>
                                                                                )
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={4}>
                                                                        <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                            Caregiver NPI
                                                                        </Typography>
                                                                        <TextField
                                                                            fullWidth
                                                                            placeholder="Caregiver NPI"
                                                                            value={formData.caregiverNpi || ''}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                setFormData(prev => ({ ...prev, caregiverNpi: val }));
                                                                            }}
                                                                            sx={accountFieldSx}
                                                                            InputProps={{
                                                                                endAdornment: (
                                                                                    <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                                        {uploadingDocumentField === 'caregiverNpiDoc' ? (
                                                                                            <CircularProgress size={20} />
                                                                                        ) : formData.caregiverNpiDocUrl ? (
                                                                                            <Chip
                                                                                                label={formData.caregiverNpiDocName || 'Caregiver NPI Certificate'}
                                                                                                size="small"
                                                                                                onClick={() => {
                                                                                                    if (formData.caregiverNpiDocUrl) {
                                                                                                        window.open(formData.caregiverNpiDocUrl, '_blank', 'noopener,noreferrer');
                                                                                                    }
                                                                                                }}
                                                                                                onDelete={() => {
                                                                                                    setFormData(prev => ({
                                                                                                        ...prev,
                                                                                                        caregiverNpiDocUrl: '',
                                                                                                        caregiverNpiDocName: '',
                                                                                                    }))
                                                                                                }}
                                                                                                sx={{
                                                                                                    cursor: 'pointer', // Show hand cursor on hover to indicate it's clickable
                                                                                                    maxWidth: 120,
                                                                                                    textOverflow: 'ellipsis',
                                                                                                    overflow: 'hidden',
                                                                                                    whiteSpace: 'nowrap',
                                                                                                    bgcolor: '#EAE7F2',
                                                                                                    color: '#574F75',
                                                                                                    fontWeight: 500,
                                                                                                    border: '1px solid #D1C9E7',
                                                                                                    '& .MuiChip-deleteIcon': {
                                                                                                        color: '#7A6FA1',
                                                                                                        '&:hover': { color: '#574F75' }
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        ) : (
                                                                                            <>
                                                                                                <input
                                                                                                    type="file"
                                                                                                    id="caregiverNpiDoc-file-input"
                                                                                                    style={{ display: 'none' }}
                                                                                                    accept=".pdf,image/*"
                                                                                                    onChange={(e) => {
                                                                                                        const file = e.target.files?.[0];
                                                                                                        if (file) {
                                                                                                            handleDocumentUpload('caregiverNpiDoc')(file);
                                                                                                        }
                                                                                                        e.target.value = '';
                                                                                                    }}
                                                                                                />
                                                                                                <Tooltip title="Upload Caregiver NPI Certificate" placement="top">
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            e.preventDefault();
                                                                                                            document.getElementById('caregiverNpiDoc-file-input')?.click();
                                                                                                        }}
                                                                                                        sx={uploadBtnSx}
                                                                                                    >
                                                                                                        <CloudUpload />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                                                                            </>
                                                                                        )}
                                                                                    </InputAdornment>
                                                                                )
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>

                                                        </Box>
                                                    </Fade>

                                                    {/* Corporate Documents & Clinical Accreditation */}
                                                    <AgencyDocumentsSection
                                                        formData={formData}
                                                        uploadingField={uploadingDocumentField}
                                                        onUpload={(fieldPrefix, file) =>
                                                            handleDocumentUpload(fieldPrefix)(file)
                                                        }
                                                        errors={npiStepErrors}
                                                        onRemove={(fieldPrefix, index) => {
                                                            if (fieldPrefix === 'clinicalAccreditation') {
                                                                setFormData(prev => {
                                                                    const newUrls = [...(prev.clinicalAccreditationUrls || [])];
                                                                    const newNames = [...(prev.clinicalAccreditationNames || [])];
                                                                    newUrls.splice(index, 1);
                                                                    newNames.splice(index, 1);
                                                                    return {
                                                                        ...prev,
                                                                        clinicalAccreditationUrls: newUrls,
                                                                        clinicalAccreditationNames: newNames,
                                                                    };
                                                                });
                                                            }
                                                        }}
                                                        accentColor={primaryColor}
                                                    />

                                                    {/* Insurance Coverage */}
                                                    <Fade
                                                        in
                                                        timeout={800}
                                                        style={{ transitionDelay: '400ms' }}
                                                    >
                                                        <Box sx={{ mb: 4 }}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: 'grey.800',
                                                                    fontWeight: 600,
                                                                    fontSize: '1.125rem',
                                                                    mb: 3,
                                                                }}
                                                            >
                                                                Insurance Coverage
                                                            </Typography>
                                                            <Grid container spacing={3}>
                                                                <Grid item xs={12} md={6}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Coverage of Insurance (COI) (Upload Multiple)
                                                                    </Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        placeholder="Enter policy number"
                                                                        value={formData.coverageOfInsurance || ''}
                                                                        onChange={handleInputChange(
                                                                            'coverageOfInsurance'
                                                                        )}
                                                                        sx={accountFieldSx}
                                                                        InputProps={{
                                                                            endAdornment: (
                                                                                <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                                    {uploadingDocumentField === 'coverageOfInsuranceDoc' && (
                                                                                        <CircularProgress size={20} />
                                                                                    )}
                                                                                    <input
                                                                                        type="file"
                                                                                        multiple
                                                                                        id="coverageOfInsuranceDoc-file-input"
                                                                                        style={{ display: 'none' }}
                                                                                        accept=".pdf,image/*"
                                                                                        onChange={(e) => {
                                                                                            const files = e.target.files;
                                                                                            if (files) {
                                                                                                Array.from(files).forEach(file => {
                                                                                                    handleDocumentUpload('coverageOfInsuranceDoc')(file);
                                                                                                });
                                                                                            }
                                                                                            e.target.value = '';
                                                                                        }}
                                                                                    />
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            e.preventDefault();
                                                                                            document.getElementById('coverageOfInsuranceDoc-file-input')?.click();
                                                                                        }}
                                                                                        title="Upload Coverage of Insurance"
                                                                                        sx={uploadBtnSx}
                                                                                    >
                                                                                        <CloudUpload />
                                                                                    </IconButton>
                                                                                </InputAdornment>
                                                                            )
                                                                        }}
                                                                    />
                                                                    {formData.coverageOfInsuranceDocUrls && formData.coverageOfInsuranceDocUrls.length > 0 && (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                                                                            {formData.coverageOfInsuranceDocUrls.map((url, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    label={formData.coverageOfInsuranceDocNames[index] || 'COI Document'}
                                                                                    size="small"
                                                                                    onClick={() => {
                                                                                        if (url) {
                                                                                            window.open(url, '_blank');
                                                                                        }
                                                                                    }}
                                                                                    onDelete={() => {
                                                                                        setFormData(prev => {
                                                                                            const newUrls = [...(prev.coverageOfInsuranceDocUrls || [])]
                                                                                            const newNames = [...(prev.coverageOfInsuranceDocNames || [])]
                                                                                            newUrls.splice(index, 1)
                                                                                            newNames.splice(index, 1)
                                                                                            return {
                                                                                                ...prev,
                                                                                                coverageOfInsuranceDocUrls: newUrls,
                                                                                                coverageOfInsuranceDocNames: newNames,
                                                                                            }
                                                                                        })
                                                                                    }}
                                                                                    sx={{
                                                                                        cursor: 'pointer',
                                                                                        '&:hover': {
                                                                                            backgroundColor: '#e0e0e0',
                                                                                        },
                                                                                        maxWidth: 180,
                                                                                        textOverflow: 'ellipsis',
                                                                                        overflow: 'hidden',
                                                                                        whiteSpace: 'nowrap',
                                                                                        bgcolor: '#EAE7F2',
                                                                                        color: '#574F75',
                                                                                        fontWeight: 500,
                                                                                        border: '1px solid #D1C9E7',
                                                                                        '& .MuiChip-deleteIcon': {
                                                                                            color: '#7A6FA1',
                                                                                            '&:hover': {
                                                                                                color: '#574F75'
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Professional Liability (Errors & Omissions, E&O) Insurance Certificate
                                                                    </Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        placeholder="Enter policy number"
                                                                        value={formData.professionalLiabilityInsurance}
                                                                        onChange={handleInputChange(
                                                                            'professionalLiabilityInsurance'
                                                                        )}
                                                                        sx={accountFieldSx}
                                                                        InputProps={{
                                                                            endAdornment: (
                                                                                <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                                    {uploadingDocumentField === 'professionalLiabilityInsuranceDoc' && (
                                                                                        <CircularProgress size={20} />
                                                                                    )}
                                                                                    <input
                                                                                        type="file"
                                                                                        id="professionalLiabilityInsuranceDoc-file-input"
                                                                                        style={{ display: 'none' }}
                                                                                        accept=".pdf,image/*"
                                                                                        onChange={(e) => {
                                                                                            const files = e.target.files;
                                                                                            if (files && files[0]) {
                                                                                                handleDocumentUpload('professionalLiabilityInsuranceDoc')(files[0]);
                                                                                            }
                                                                                            e.target.value = '';
                                                                                        }}
                                                                                    />
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            e.preventDefault();
                                                                                            document.getElementById('professionalLiabilityInsuranceDoc-file-input')?.click();
                                                                                        }}
                                                                                        title="Upload Professional Liability Insurance"
                                                                                        sx={uploadBtnSx}
                                                                                    >
                                                                                        <CloudUpload />
                                                                                    </IconButton>
                                                                                </InputAdornment>
                                                                            )
                                                                        }}
                                                                    />
                                                                    {formData.professionalLiabilityInsuranceDocUrls && formData.professionalLiabilityInsuranceDocUrls.length > 0 && (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                                                                            {formData.professionalLiabilityInsuranceDocUrls.map((url, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    label={formData.professionalLiabilityInsuranceDocNames[index] || 'PLI Document'}
                                                                                    size="small"

                                                                                    onClick={() => {
                                                                                        if (url) {
                                                                                            window.open(url, '_blank');
                                                                                        }
                                                                                    }}
                                                                                    onDelete={() => {
                                                                                        setFormData(prev => {
                                                                                            const newUrls = [...(prev.professionalLiabilityInsuranceDocUrls || [])]
                                                                                            const newNames = [...(prev.professionalLiabilityInsuranceDocNames || [])]
                                                                                            newUrls.splice(index, 1)
                                                                                            newNames.splice(index, 1)
                                                                                            return {
                                                                                                ...prev,
                                                                                                professionalLiabilityInsuranceDocUrls: newUrls,
                                                                                                professionalLiabilityInsuranceDocNames: newNames,
                                                                                            }
                                                                                        })
                                                                                    }}
                                                                                    sx={{
                                                                                        cursor: 'pointer',
                                                                                        '&:hover': {
                                                                                            backgroundColor: '#e0e0e0',
                                                                                        },
                                                                                        maxWidth: 180,
                                                                                        textOverflow: 'ellipsis',
                                                                                        overflow: 'hidden',
                                                                                        whiteSpace: 'nowrap',
                                                                                        bgcolor: '#EAE7F2',
                                                                                        color: '#574F75',
                                                                                        fontWeight: 500,
                                                                                        border: '1px solid #D1C9E7',
                                                                                        '& .MuiChip-deleteIcon': {
                                                                                            color: '#7A6FA1',
                                                                                            '&:hover': {
                                                                                                color: '#574F75'
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        General Liability Insurance Certificate
                                                                    </Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        placeholder="Enter policy number"
                                                                        value={formData.generalLiabilityInsurance}
                                                                        onChange={handleInputChange(
                                                                            'generalLiabilityInsurance'
                                                                        )}
                                                                        sx={accountFieldSx}
                                                                        InputProps={{
                                                                            endAdornment: (
                                                                                <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                                    {uploadingDocumentField === 'generalLiabilityInsuranceDoc' && (
                                                                                        <CircularProgress size={20} />
                                                                                    )}
                                                                                    <input
                                                                                        type="file"
                                                                                        id="generalLiabilityInsuranceDoc-file-input"
                                                                                        style={{ display: 'none' }}
                                                                                        accept=".pdf,image/*"
                                                                                        onChange={(e) => {
                                                                                            const files = e.target.files;
                                                                                            if (files && files[0]) {
                                                                                                handleDocumentUpload('generalLiabilityInsuranceDoc')(files[0]);
                                                                                            }
                                                                                            e.target.value = '';
                                                                                        }}
                                                                                    />
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            e.preventDefault();
                                                                                            document.getElementById('generalLiabilityInsuranceDoc-file-input')?.click();
                                                                                        }}
                                                                                        title="Upload General Liability Insurance"
                                                                                        sx={uploadBtnSx}
                                                                                    >
                                                                                        <CloudUpload />
                                                                                    </IconButton>
                                                                                </InputAdornment>
                                                                            )
                                                                        }}
                                                                    />
                                                                    {formData.generalLiabilityInsuranceDocUrls && formData.generalLiabilityInsuranceDocUrls.length > 0 && (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                                                                            {formData.generalLiabilityInsuranceDocUrls.map((url, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    label={formData.generalLiabilityInsuranceDocNames[index] || 'GLI Document'}
                                                                                    size="small"

                                                                                    onClick={() => {
                                                                                        if (url) {
                                                                                            window.open(url, '_blank');
                                                                                        }
                                                                                    }}
                                                                                    onDelete={() => {
                                                                                        setFormData(prev => {
                                                                                            const newUrls = [...(prev.generalLiabilityInsuranceDocUrls || [])]
                                                                                            const newNames = [...(prev.generalLiabilityInsuranceDocNames || [])]
                                                                                            newUrls.splice(index, 1)
                                                                                            newNames.splice(index, 1)
                                                                                            return {
                                                                                                ...prev,
                                                                                                generalLiabilityInsuranceDocUrls: newUrls,
                                                                                                generalLiabilityInsuranceDocNames: newNames,
                                                                                            }
                                                                                        })
                                                                                    }}
                                                                                    sx={{
                                                                                        cursor: 'pointer',
                                                                                        '&:hover': {
                                                                                            backgroundColor: '#e0e0e0',
                                                                                        },
                                                                                        maxWidth: 180,
                                                                                        textOverflow: 'ellipsis',
                                                                                        overflow: 'hidden',
                                                                                        whiteSpace: 'nowrap',
                                                                                        bgcolor: '#EAE7F2',
                                                                                        color: '#574F75',
                                                                                        fontWeight: 500,
                                                                                        border: '1px solid #D1C9E7',
                                                                                        '& .MuiChip-deleteIcon': {
                                                                                            color: '#7A6FA1',
                                                                                            '&:hover': {
                                                                                                color: '#574F75'
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Grid>



                                                                <Grid item xs={12} md={6}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Workers' Compensation Insurance Certificate
                                                                    </Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        placeholder="Enter policy number"
                                                                        value={formData.workersCompensation || ''}
                                                                        onChange={handleInputChange(
                                                                            'workersCompensation'
                                                                        )}
                                                                        sx={accountFieldSx}
                                                                        InputProps={{
                                                                            endAdornment: (
                                                                                <InputAdornment position="end" sx={{ gap: 1 }}>
                                                                                    {uploadingDocumentField === 'workersCompensationDoc' && (
                                                                                        <CircularProgress size={20} />
                                                                                    )}
                                                                                    <input
                                                                                        type="file"
                                                                                        id="workersCompensationDoc-file-input"
                                                                                        style={{ display: 'none' }}
                                                                                        accept=".pdf,image/*"
                                                                                        onChange={(e) => {
                                                                                            const files = e.target.files;
                                                                                            if (files && files[0]) {
                                                                                                handleDocumentUpload('workersCompensationDoc')(files[0]);
                                                                                            }
                                                                                            e.target.value = '';
                                                                                        }}
                                                                                    />
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            e.preventDefault();
                                                                                            document.getElementById('workersCompensationDoc-file-input')?.click();
                                                                                        }}
                                                                                        title="Upload Workers Compensation"
                                                                                        sx={uploadBtnSx}
                                                                                    >
                                                                                        <CloudUpload />
                                                                                    </IconButton>
                                                                                </InputAdornment>
                                                                            )
                                                                        }}
                                                                    />
                                                                    {formData.workersCompensationDocUrls && formData.workersCompensationDocUrls.length > 0 && (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                                                                            {formData.workersCompensationDocUrls.map((url, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    label={formData.workersCompensationDocNames[index] || 'Workers Compensation Document'}
                                                                                    size="small"
                                                                                    onClick={() => {
                                                                                        if (url) {
                                                                                            window.open(url, '_blank');
                                                                                        }
                                                                                    }}
                                                                                    onDelete={() => {
                                                                                        setFormData(prev => {
                                                                                            const newUrls = [...(prev.workersCompensationDocUrls || [])]
                                                                                            const newNames = [...(prev.workersCompensationDocNames || [])]
                                                                                            newUrls.splice(index, 1)
                                                                                            newNames.splice(index, 1)
                                                                                            return {
                                                                                                ...prev,
                                                                                                workersCompensationDocUrls: newUrls,
                                                                                                workersCompensationDocNames: newNames,
                                                                                            }
                                                                                        })
                                                                                    }}
                                                                                    sx={{
                                                                                        cursor: 'pointer',
                                                                                        '&:hover': {
                                                                                            backgroundColor: '#e0e0e0',
                                                                                        },
                                                                                        maxWidth: 180,
                                                                                        textOverflow: 'ellipsis',
                                                                                        overflow: 'hidden',
                                                                                        whiteSpace: 'nowrap',
                                                                                        bgcolor: '#EAE7F2',
                                                                                        color: '#574F75',
                                                                                        fontWeight: 500,
                                                                                        border: '1px solid #D1C9E7',
                                                                                        '& .MuiChip-deleteIcon': {
                                                                                            color: '#7A6FA1',
                                                                                            '&:hover': {
                                                                                                color: '#574F75'
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    </Fade>
                                                </Box>
                                            )}

                                            {/* Step 4: Choose Subscription */}
                                            {currentStep === 3 && (
                                                <ChooseSubscriptionStep
                                                    showSubscriptionDialog={showSubscriptionDialog}
                                                    onCloseDialog={() => setShowSubscriptionDialog(false)}
                                                    onOpenDialog={() => setShowSubscriptionDialog(true)}
                                                    onPlanSelect={handlePlanSelect}
                                                    isProcessingPayment={isProcessingPayment}
                                                    paymentError={paymentError}
                                                    onClearPaymentError={() => setPaymentError(null)}
                                                    subscriptionPaymentSuccess={subscriptionPaymentSuccess}
                                                    providersCount={parseInt(onboardingData.noOfProvider) || 1}
                                                    selectedPlan={selectedPlan}
                                                    billingCycle={billingCycle}
                                                    discountedPrice={discountedPrice}
                                                    selectedProviderCount={
                                                        pendingPlanSelection?.customNoOfUser ??
                                                        (parseInt(onboardingData.noOfProvider) || 1)
                                                    }
                                                    agencyName={formData.agencyName}
                                                    agencyId={therapistId}
                                                    administratorFirstName={formData.firstName}
                                                    administratorLastName={formData.lastName}
                                                    planSelected={planSelected}
                                                    onActivate={handleActivateSubscription}
                                                />
                                            )}

                                            {/* Step 5: Connect Bank */}
                                            {currentStep === 4 && (
                                                <ConnectBankStep
                                                    isConnectingBank={isConnectingBank}
                                                    paymentError={paymentError}
                                                    onClearPaymentError={() => setPaymentError(null)}
                                                    onStripeConnect={handleStripeConnect}
                                                />
                                            )}

                                            {/* Placeholder for other steps */}
                                            {currentStep > 4 && (
                                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                                    <Typography variant="h4" sx={{ mb: 2 }}>
                                                        {steps[currentStep]}
                                                    </Typography>
                                                    <Typography variant="body1" color="text.secondary">
                                                        This step will be implemented soon...
                                                    </Typography>
                                                </Box>
                                            )}

                                            <OnboardingNavigation
                                                currentStep={currentStep}
                                                hasAboutYouData={hasAboutYouData}
                                                isProcessingPayment={isProcessingPayment}
                                                subscriptionPaymentSuccess={subscriptionPaymentSuccess}
                                                planSelected={planSelected}
                                                onBack={handleBack}
                                                onNext={handleNext}
                                            />
                                        </>
                                    )}
                                </Paper>
                            </Grow>
                        </Box>
                    </Slide>
                </Box>
            </Box>

            {/* Payment Modal */}
            {subscriptionDetails && (
                <AgencyPaymentModal
                    open={showPaymentModal}
                    onClose={() => {
                        setShowPaymentModal(false)
                        setPaymentError(null)
                    }}
                    onPaymentSuccess={handlePaymentSuccess}
                    plan={selectedPlan}
                    billingCycle={billingCycle}
                    discountedPrice={discountedPrice}
                    amount={
                        // Price per provider, matching the discounted total shown on the
                        // Subscription Agreement step (discountedPrice is that step's
                        // coupon-adjusted GRAND TOTAL, divided back down by provider count).
                        // Previously this parsed a chunk of the Stripe client_secret as a
                        // number, which is never numeric and silently fell back to the
                        // undiscounted plan price, ignoring any coupon.
                        (() => {
                            const providerCountForPayment =
                                subscriptionDetails?.noOfUser ||
                                parseInt(onboardingData.noOfProvider) ||
                                1
                            return discountedPrice != null
                                ? discountedPrice / providerCountForPayment
                                : selectedPlan
                                    ? billingCycle === 'monthly'
                                        ? selectedPlan.monthly_fee
                                        : selectedPlan.anual_fee
                                    : 0
                        })()
                    }
                    therapistFirstName={formData.firstName}
                    therapistLastName={formData.lastName}
                    therapistEmail={formData.email}
                    clientSecret={subscriptionDetails.client_secret}
                    billingAddress={formData.billingAddressLine}
                    billingCity={formData.billingCity}
                    billingState={formData.billingState}
                    billingZip={formData.billingZipCode}
                    agencyId={therapistId}
                    providersCount={parseInt(onboardingData.noOfProvider) || 1}
                    noOfUser={subscriptionDetails?.noOfUser}
                />
            )}

            {/* Success Alert */}
            <Snackbar
                open={showSuccessAlert}
                autoHideDuration={2000}
                onClose={() => setShowSuccessAlert(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setShowSuccessAlert(false)}
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    {successMessage || 'Account created successfully!'}
                </Alert>
            </Snackbar>

            {/* Error Alert */}
            <Snackbar
                open={showErrorAlert}
                autoHideDuration={4000}
                onClose={() => setShowErrorAlert(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setShowErrorAlert(false)}
                    severity="error"
                    sx={{ width: '100%' }}
                >
                    {errorMessage || 'Failed to upload document.'}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default AgencySignUp
